/* global globalThis */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import { ReactReduxContext } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Alert,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  compileAssessmentReportData,
  generateClinicalAssessmentPdf,
} from "../../utils/clinicalAssessmentPdf";

// Sub-components
import GeneralAssessment from "./GeneralAssessment";
import ProgramSpecific from "./ProgramSpecific";
import OutcomeAndSign from "./OutcomeAndSign";
import MemberDetails from "./MemberDetails";
import { LoadingWaveDots } from "../../components/common/Loader/LoadingWave";
import {
  initializeAssessmentApi,
  autosaveAssessmentApi,
  submitAssessmentApi,
  fetchAssessmentSchemasApi,
  parseSchemasResponse,
  parseSchemaPayload,
  getTemplateSchemaFromPayload,
  fetchDraftPayloadApi,
  fetchAssessmentGroups,
  fetchAssessmentGroupById,
  fetchAssessmentTemplateByType,
  fetchAssessmentReferenceData,
  getCachedAssessmentReferenceData,
  checkIsFormless,
  isEnrolledOutcome,
  isProgramCompletionOutcome,
  isDeclinedOutcome,
  isUnableToReachOutcome,
  isDeclinedOrUnableToReach,
  getOutcomeNameFromReferenceData,
  hasTerminalOutcome,
} from "../../api/assessment";

const mapLocalProgramId = (prog) => {
  if (!prog) return "";
  return String(prog).trim();
};

const cleanProgramId = (id) => {
  if (!id) return "";
  const cleaned = String(id)
    .toUpperCase()
    .replaceAll(/_PROGRAM_SPECIFIC/gi, "")
    .trim();
  if (cleaned === "WOMENS_HEALTH" || cleaned === "WOMENS_HEALTH_PROGRAM") {
    return "WOMENS_HEALTH_PROGRAM";
  }
  return cleaned;
};

const normalizeProgramId = (id) => {
  if (!id) return "";
  const upper = String(id).toUpperCase().trim();
  if (upper.includes("WOMEN")) {
    return "WOMENS_HEALTH_PROGRAM";
  }
  return upper
    .replaceAll(/_PROGRAM_SPECIFIC|_PROGRAM/gi, "")
    .replaceAll(/[\s_-]+/g, "")
    .trim();
};

const findProgramMatch = (list, progId, progName) => {
  if (!Array.isArray(list) || !list.length) return null;
  const targetId = String(progId || "")
    .toUpperCase()
    .trim();
  const targetClean = cleanProgramId(targetId);
  const targetNorm = normalizeProgramId(progId);
  const targetName = String(progName || "")
    .toUpperCase()
    .trim();
  const targetNameNorm = normalizeProgramId(progName);

  return list.find((p) => {
    if (!p) return false;
    const pid = String(p.programId || p.id || p.cleanId || "")
      .toUpperCase()
      .trim();
    const pclean = cleanProgramId(pid);
    const pnorm = normalizeProgramId(pid);
    const pname = String(
      p.name || p.displayName || p.groupName || p.programDisplayName || "",
    )
      .toUpperCase()
      .trim();
    const pnamenorm = normalizeProgramId(pname);

    return (
      (targetId && pid === targetId) ||
      (targetClean && pclean === targetClean) ||
      (targetClean && pid === targetClean) ||
      (pclean && pclean === targetId) ||
      (targetNorm && pnorm === targetNorm) ||
      (targetName && pname === targetName) ||
      (targetNameNorm && pnamenorm === targetNameNorm) ||
      (targetNameNorm && pnorm === targetNameNorm) ||
      (targetNorm && pnamenorm === targetNorm)
    );
  });
};

const getCanonicalProgramId = (rawKey, name, availableGroups = []) => {
  if (!rawKey && !name) return "";
  const cleanRaw = cleanProgramId(rawKey);
  const normRaw = normalizeProgramId(rawKey);
  const normName = normalizeProgramId(name);

  // Check against available assessment groups (API 1) to find exact programId
  if (Array.isArray(availableGroups) && availableGroups.length > 0) {
    const matched = availableGroups.find((g) => {
      const gType = String(g.groupType || "").toUpperCase();
      if (gType !== "PROGRAM_SPECIFIC" && gType !== "BIOSIMILARS") return false;

      const gProgId = String(g.programId || "")
        .toUpperCase()
        .trim();
      const gCleanProgId = cleanProgramId(gProgId);
      const gNormProgId = normalizeProgramId(gProgId);
      const gId = String(g.id || "")
        .toUpperCase()
        .trim();
      const gName = String(g.groupName || g.name || g.displayName || "")
        .toUpperCase()
        .trim();
      const gNormName = normalizeProgramId(gName);
      return (
        (gProgId &&
          (gProgId === cleanRaw ||
            gCleanProgId === cleanRaw ||
            gNormProgId === normRaw ||
            gNormProgId === normName)) ||
        (gId && (gId === String(rawKey).trim() || gId === cleanRaw)) ||
        (gName && (gNormName === normRaw || gNormName === normName))
      );
    });

    if (matched?.programId) {
      return String(matched.programId).toUpperCase().trim();
    }
  }

  return (
    cleanRaw ||
    normRaw ||
    String(rawKey || name)
      .toUpperCase()
      .trim()
  );
};

const isNonClinicalProgram = (id, name) => {
  const upperId = String(id || "")
    .toUpperCase()
    .trim();
  const upperName = String(name || "")
    .toUpperCase()
    .trim();
  return (
    upperId.includes("BIOSIMILAR") ||
    upperName.includes("BIOSIMILAR") ||
    upperId.includes("COPAY") ||
    upperName.includes("COPAY") ||
    upperId.includes("SCRIPTCO") ||
    upperName.includes("SCRIPTCO") ||
    upperId.includes("OTHER") ||
    upperName.includes("OTHER") ||
    upperId.includes("GENERAL") ||
    upperName.includes("GENERAL") ||
    upperId.includes("LIFESTYLE") ||
    upperName.includes("LIFESTYLE") ||
    upperId.includes("CARE_PLAN") ||
    upperName.includes("CARE_PLAN") ||
    upperId.includes("CAREPLAN") ||
    upperName.includes("CAREPLAN") ||
    upperId.includes("METADATA") ||
    upperName.includes("METADATA")
  );
};

const PROGRAM_DISPLAY_NAMES = {
  WOMENS_HEALTH_PROGRAM: "Women's Health Program",
  WOMENS_HEALTH: "Women's Health Program",
  DIABETES: "Diabetes",
  WEIGHT_MANAGEMENT: "Weight Management",
  BIOSIMILARS: "Biosimilar",
};

const CustomStepIcon = (props) => {
  const { active, completed } = props;

  if (completed) {
    return (
      <CheckCircleIcon
        sx={{
          color: "#403C6F",
          fontSize: 18,
          zIndex: 1,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: "50%",
        backgroundColor: active ? "#403C6F" : "#fff",
        border: `2px solid ${active ? "#403C6F" : "#C4C2D3"}`,
        zIndex: 1,
      }}
    />
  );
};

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
};

const getActiveProgramStrings = (selectedProgram) => {
  if (!selectedProgram) return [];
  const activeProgramStrings = [];
  const addProgramDetails = (p) => {
    if (p.groupName) activeProgramStrings.push(String(p.groupName));
    if (p.programId) activeProgramStrings.push(String(p.programId));
    if (p.programName) activeProgramStrings.push(String(p.programName));
    if (p.program) activeProgramStrings.push(String(p.program));
    if (p.id) activeProgramStrings.push(String(p.id));
  };
  if (Array.isArray(selectedProgram)) {
    selectedProgram.forEach(addProgramDetails);
  } else {
    addProgramDetails(selectedProgram);
  }
  return activeProgramStrings;
};

const checkProgramMatch = (progNames, normP) => {
  return progNames.some((act) => act.includes(normP) || normP.includes(act));
};

export const isFieldVisible = (field) => {
  return field.visible !== false;
};

const checkValueEmpty = (val) => {
  return (
    val === undefined ||
    val === null ||
    val === "" ||
    (Array.isArray(val) && val.length === 0)
  );
};

const getActualValue = (valObj) => {
  return valObj && typeof valObj === "object" && "value" in valObj
    ? valObj.value
    : valObj;
};

const getFieldVal = (formPayload, sectionKey, fieldKey) => {
  if (formPayload?.[sectionKey]?.[fieldKey] !== undefined) {
    return formPayload[sectionKey][fieldKey];
  }
  if (formPayload?.[fieldKey] !== undefined) {
    return formPayload[fieldKey];
  }
  return undefined;
};

const getGroupSubVal = (formPayload, sectionKey, fieldKey, subKey) => {
  if (formPayload?.[sectionKey]?.[fieldKey]?.[subKey] !== undefined) {
    return formPayload[sectionKey][fieldKey][subKey];
  }
  if (formPayload?.[fieldKey]?.[subKey] !== undefined) {
    return formPayload[fieldKey][subKey];
  }
  if (formPayload?.[`${fieldKey}.${subKey}`] !== undefined) {
    return formPayload[`${fieldKey}.${subKey}`];
  }
  if (formPayload?.[sectionKey]?.[`${fieldKey}.${subKey}`] !== undefined) {
    return formPayload[sectionKey][`${fieldKey}.${subKey}`];
  }
  return undefined;
};

const isSubFieldEmpty = (subField, val) => {
  const actualVal = getActualValue(val);
  if (subField?.type === "checkbox") {
    return !actualVal;
  }
  if (subField?.type === "boolean") {
    return actualVal === undefined || actualVal === null || actualVal === "";
  }
  return checkValueEmpty(actualVal);
};

const isDependencySatisfied = (dependency, formPayload) => {
  if (!dependency) return true;
  const { key, value } = dependency;
  let actualValue;
  Object.keys(formPayload || {}).forEach((sKey) => {
    if (formPayload[sKey]?.[key] !== undefined) {
      actualValue = getActualValue(formPayload[sKey][key]);
    }
  });
  if (actualValue === undefined && formPayload?.[key] !== undefined) {
    actualValue = getActualValue(formPayload[key]);
  }
  return Array.isArray(value)
    ? value.includes(actualValue)
    : actualValue === value;
};

const validateGroupField = (
  field,
  sectionKey,
  fieldKey,
  formPayload,
  visFn,
) => {
  const isGroupRequired = Boolean(
    field.required === true || String(field.required).toLowerCase() === "true",
  );
  const hasSubFields =
    field.fields &&
    typeof field.fields === "object" &&
    Object.keys(field.fields).length > 0;

  if (hasSubFields) {
    const subKeys = Object.keys(field.fields);

    const hasMissingRequiredSubField = subKeys.some((subKey) => {
      const subField = field.fields[subKey];
      const isSubRequired = Boolean(
        subField?.required === true ||
        String(subField?.required).toLowerCase() === "true",
      );
      if (!isSubRequired) return false;
      if (visFn && !visFn(subField)) return false;
      const val = getGroupSubVal(formPayload, sectionKey, fieldKey, subKey);
      return isSubFieldEmpty(subField, val);
    });

    const hasAnyEmptySubField = subKeys.some((subKey) => {
      const subField = field.fields[subKey];
      if (visFn && !visFn(subField)) return false;
      const val = getGroupSubVal(formPayload, sectionKey, fieldKey, subKey);
      return isSubFieldEmpty(subField, val);
    });

    const isGroupMissing = isGroupRequired
      ? hasMissingRequiredSubField || hasAnyEmptySubField
      : hasMissingRequiredSubField;

    return !isGroupMissing;
  }

  if (isGroupRequired) {
    const val = getActualValue(getFieldVal(formPayload, sectionKey, fieldKey));
    return !checkValueEmpty(val);
  }
  return true;
};

const validateStandardField = (field, sectionKey, fieldKey, formPayload) => {
  const isFieldRequired = Boolean(
    field.required === true || String(field.required).toLowerCase() === "true",
  );
  if (!isFieldRequired) return true;

  const val = getActualValue(getFieldVal(formPayload, sectionKey, fieldKey));
  if (field.type === "checkbox") {
    return Boolean(val);
  }
  if (field.type === "boolean") {
    return val !== undefined && val !== null && val !== "";
  }
  return !checkValueEmpty(val);
};

const resolveActiveBranches = (field, value) => {
  const activeBranches = [];
  if (field.type === "boolean") {
    if (value === true && field.branches?.true) {
      activeBranches.push({ key: "true", config: field.branches.true });
    } else if (value === false && field.branches?.false) {
      activeBranches.push({ key: "false", config: field.branches.false });
    }
  } else if (field.type === "single_select") {
    if (value && field.branches?.[value]) {
      activeBranches.push({ key: value, config: field.branches[value] });
    }
  } else if (field.type === "multi_select") {
    const selected = Array.isArray(value) ? value : [];
    selected.forEach((opt) => {
      if (field.branches?.[opt]) {
        activeBranches.push({ key: opt, config: field.branches[opt] });
      }
    });
  }
  return activeBranches;
};

const validateFieldBranches = (
  field,
  sectionKey,
  fieldKey,
  formPayload,
  isFieldVisFn,
) => {
  if (!field.branches) return true;
  const value = getActualValue(getFieldVal(formPayload, sectionKey, fieldKey));
  const activeBranches = resolveActiveBranches(field, value);

  return activeBranches.every(({ key: branchKey, config }) => {
    const subQ = config.subQuestion;
    if (subQ && isFieldVisFn(subQ) && subQ.required) {
      const rawBranchVal =
        formPayload?.[sectionKey]?.[fieldKey]?.branches?.[branchKey]
          ?.subQuestion?.value ??
        formPayload?.[`${fieldKey}_branch_${branchKey}`];
      const val = getActualValue(rawBranchVal);
      return !checkValueEmpty(val);
    }
    return true;
  });
};

const validateSingleSchemaField = (
  field,
  sectionKey,
  fieldKey,
  formPayload,
  visFn,
) => {
  if (!visFn(field)) return true;
  if (!isDependencySatisfied(field.dependency, formPayload)) return true;

  if (field.type === "group") {
    return validateGroupField(field, sectionKey, fieldKey, formPayload, visFn);
  }

  const isStandardValid = validateStandardField(
    field,
    sectionKey,
    fieldKey,
    formPayload,
  );
  if (!isStandardValid) return false;

  return validateFieldBranches(field, sectionKey, fieldKey, formPayload, visFn);
};

export const validateSchemaForm = (
  schema,
  formPayload,
  visFn = isFieldVisible,
) => {
  if (!schema?.sections) return true;

  return Object.keys(schema.sections).every((sectionKey) => {
    const section = schema.sections[sectionKey];
    if (!section.fields) return true;

    return Object.keys(section.fields).every((fieldKey) => {
      const field = section.fields[fieldKey];
      return validateSingleSchemaField(
        field,
        sectionKey,
        fieldKey,
        formPayload,
        visFn,
      );
    });
  });
};

const extractBranchesValue = (branches) => {
  if (!branches) return undefined;
  const updatedBranches = {};
  Object.keys(branches).forEach((branchKey) => {
    const subQ = branches[branchKey]?.subQuestion;
    if (subQ) {
      updatedBranches[branchKey] = {
        subQuestion: {
          ...subQ,
          value: subQ.value ?? null,
        },
      };
    }
  });
  return updatedBranches;
};

const extractMultiSelectValue = (field) => {
  if (Array.isArray(field.value)) {
    return field.value;
  }
  if (field.options) {
    return field.options
      .filter((opt) => opt.is_selected)
      .map((opt) => opt.label);
  }
  return [];
};

const extractSingleSelectValue = (field) => {
  if (typeof field.value === "string") {
    return field.value;
  }
  if (field.options) {
    return field.options.find((opt) => opt.is_selected)?.label || "";
  }
  return "";
};

const extractBooleanValue = (field) => {
  return field.is_checked ?? field.value ?? false;
};

const extractAnswersFromField = (field) => {
  if (field.type === "group") {
    const groupVal = {};
    if (field.fields) {
      Object.keys(field.fields).forEach((groupFieldKey) => {
        groupVal[groupFieldKey] = field.fields[groupFieldKey].value ?? "";
      });
    }
    return groupVal;
  }

  let baseVal = field.value ?? "";
  if (field.type === "multi_select") {
    baseVal = extractMultiSelectValue(field);
  } else if (field.type === "boolean" || field.type === "checkbox") {
    baseVal = extractBooleanValue(field);
  } else if (field.type === "single_select") {
    baseVal = extractSingleSelectValue(field);
  }

  const branches = extractBranchesValue(field.branches);
  if (branches) {
    return { value: baseVal, branches };
  }
  return baseVal;
};

const extractAnswersFromSchema = (schema) => {
  const payload = {};
  if (schema?.sections) {
    Object.keys(schema.sections).forEach((sectionKey) => {
      payload[sectionKey] = {};
      const fields = schema.sections[sectionKey].fields;
      if (fields) {
        Object.keys(fields).forEach((fieldKey) => {
          payload[sectionKey][fieldKey] = extractAnswersFromField(
            fields[fieldKey],
          );
        });
      }
    });
  }
  return payload;
};

const buildInitialPrograms = (selectedProgram, assessmentType, refData) => {
  const initialPrograms = [];
  const seen = new Set();
  const addProgram = (prog) => {
    if (!prog) return;
    const rawProgramId =
      prog.programId ||
      prog.id ||
      prog.cleanId ||
      prog.groupName ||
      prog.name ||
      "";
    const clean = cleanProgramId(rawProgramId);
    if (clean || rawProgramId) {
      const upper = String(clean || rawProgramId)
        .toUpperCase()
        .trim();
      if (
        upper !== "OTHER" &&
        upper !== "GENERAL" &&
        upper !== "LIFESTYLE" &&
        upper !== "CARE_PLAN" &&
        !seen.has(upper)
      ) {
        seen.add(upper);
        const isFormless = checkIsFormless(prog, refData);
        const rawType = prog.formType || prog.assessmentType;
        const resolvedType =
          isFormless || !rawType || String(rawType).trim() === ""
            ? "N/A"
            : String(rawType).toUpperCase();
        const item = {
          programId: clean || rawProgramId,
          formType: resolvedType,
        };
        if (prog.manuallyAdded && !isFormless) {
          item.manuallyAdded = true;
        }
        initialPrograms.push(item);
      }
    }
  };
  if (Array.isArray(selectedProgram)) {
    selectedProgram.forEach(addProgram);
  } else if (selectedProgram) {
    addProgram(selectedProgram);
  }
  return initialPrograms;
};

const buildInitialProgramsForType = (
  combinedPrograms,
  formattedType,
  referenceData,
) => {
  const progSet = new Set();
  const initialPrograms = [];
  (combinedPrograms || []).forEach((ap) => {
    const rawId = ap.programId || ap.id || ap.cleanId || "";
    const pid = cleanProgramId(rawId) || String(rawId);
    const uPid = pid.toUpperCase();
    if (
      !progSet.has(uPid) &&
      !isNonClinicalProgram(pid, ap.name) &&
      !uPid.includes("BIOSIMILAR")
    ) {
      progSet.add(uPid);
      const isFormless = checkIsFormless(ap, referenceData);
      const pType = isFormless
        ? "N/A"
        : (ap.formType || ap.assessmentType || formattedType).toUpperCase();
      const isManual = Boolean(ap.manuallyAdded);
      initialPrograms.push({
        programId: pid,
        formType: pType,
        ...(isManual && !isFormless ? { manuallyAdded: true } : {}),
      });
    }
  });
  return initialPrograms;
};

const setNonFormlessLoading = (prev, nonFormless, isLoading) => {
  const next = { ...prev };
  nonFormless.forEach((p) => {
    const cid = cleanProgramId(p.programId || p.id);
    if (isLoading) {
      next[p.id] = true;
      next[cid] = true;
      next[`${cid}_PROGRAM_SPECIFIC`] = true;
    } else {
      delete next[p.id];
      delete next[cid];
      delete next[`${cid}_PROGRAM_SPECIFIC`];
    }
  });
  return next;
};

const mergeAddedPrograms = (prev, initialManual) => {
  const combined = [...prev];
  initialManual.forEach((mp) => {
    const mpClean = cleanProgramId(mp.programId || mp.id);
    if (
      !combined.some((cp) => cleanProgramId(cp.programId || cp.id) === mpClean)
    ) {
      combined.push(mp);
    }
  });
  return combined;
};

const upsertEnrolledPrograms = (prev, enrolledActivePrograms) => {
  const next = [...prev];
  enrolledActivePrograms.forEach((eap) => {
    const eapClean = cleanProgramId(eap.programId || eap.id).toUpperCase();
    const idx = next.findIndex(
      (p) => cleanProgramId(p.programId || p.id).toUpperCase() === eapClean,
    );
    if (idx === -1) {
      next.push(eap);
    } else {
      next[idx] = { ...next[idx], ...eap };
    }
  });
  return next;
};

const resolveChosenFormType = (isProgCompletion, rawType) => {
  if (isProgCompletion) return "DISCHARGE";
  if (rawType && String(rawType).trim() !== "") {
    return String(rawType).toUpperCase();
  }
  return "INITIAL";
};

const resolveIsFormless = (p, existing, rawId, cleanKey, referenceData) => {
  if (p.isFormless !== undefined) return Boolean(p.isFormless);
  if (existing.isFormless !== undefined) return Boolean(existing.isFormless);
  return checkIsFormless(
    { id: rawId, cleanId: cleanKey, name: p.name },
    referenceData,
  );
};

const resolveProgramMergedFormType = (
  isProgCompletion,
  isFormless,
  rawFormType,
) => {
  if (isProgCompletion) return "DISCHARGE";
  if (isFormless) return "N/A";
  if (rawFormType && String(rawFormType).trim() !== "") {
    return String(rawFormType).toUpperCase();
  }
  return "";
};

const resolveProgramMergedManual = (p, matchedActive, existing) => {
  if (p.manuallyAdded !== undefined) return Boolean(p.manuallyAdded);
  if (matchedActive?.manuallyAdded !== undefined)
    return Boolean(matchedActive.manuallyAdded);
  if (existing.manuallyAdded !== undefined)
    return Boolean(existing.manuallyAdded);
  return Boolean(p.isManual || matchedActive?.isManual || existing.isManual);
};

const resolveEnrolledFormType = (
  isCompletion,
  selectedType,
  defaultType = "INITIAL",
) => {
  if (isCompletion) return "DISCHARGE";
  if (
    selectedType &&
    String(selectedType).trim() !== "" &&
    String(selectedType).toUpperCase() !== "N/A"
  ) {
    return String(selectedType).toUpperCase();
  }
  return (defaultType || "INITIAL").toUpperCase();
};

const parseElapsedTime = (res) => {
  if (typeof res?.elapsedTime === "number") return res.elapsedTime;
  if (typeof res?.elapsed_time === "number") return res.elapsed_time;
  if (typeof res?.elapsedTime === "string")
    return Number.parseInt(res.elapsedTime, 10) || 0;
  return 0;
};

const extractClinicalNotesFromResponse = (res, initialClinicalNotes) => {
  return (
    res.clinicalNote ||
    res.clinicalNotes ||
    res.note ||
    res.notes ||
    res.narrative ||
    res.summary ||
    res.comments ||
    res.payload?._outcomes?.clinicalNotes ||
    res.payload?._outcomes?.clinicalNote ||
    res.payload?._outcome?.clinicalNotes ||
    res.payload?._outcome?.clinicalNote ||
    res.payload?.clinicalNote ||
    res.payload?.clinicalNotes ||
    res.payload?.note ||
    res.payload?.notes ||
    res.payload?.narrative ||
    res.payload?.summary ||
    initialClinicalNotes ||
    ""
  );
};

const resolveCertificationStatus = (res, readOnly) => {
  if (
    readOnly ||
    String(res?.status || "").toUpperCase() === "SUBMITTED" ||
    String(res?.status || "").toUpperCase() === "COMPLETED"
  ) {
    return true;
  }
  const certification =
    res?.certification ??
    res?.payload?.certification ??
    res?.payload?._outcomes?.certification;
  if (certification !== undefined && certification !== null) {
    return Boolean(certification);
  }
  const signatureConfirmed =
    res?.signatureConfirmed ??
    res?.payload?.signatureConfirmed ??
    res?.payload?._outcomes?.signatureConfirmed;
  if (signatureConfirmed !== undefined && signatureConfirmed !== null) {
    return Boolean(signatureConfirmed);
  }
  return false;
};

const STAGES_WITH_PROGRAM = [
  "OUTCOMES",
  "CORE",
  "PROGRAM",
  "LIFESTYLE",
  "CARE_PLAN",
];
const STAGES_WITHOUT_PROGRAM = ["OUTCOMES", "CORE", "LIFESTYLE", "CARE_PLAN"];

const getStageKeyForStep = (activeStep, hasProgramSpecific) => {
  const stages = hasProgramSpecific
    ? STAGES_WITH_PROGRAM
    : STAGES_WITHOUT_PROGRAM;
  return stages[activeStep] || "OUTCOMES";
};

const resolveNextButtonLabel = (
  step,
  isDirectSubmit,
  submitting,
  totalSteps,
) => {
  if (step === 0) {
    if (submitting) return "Submitting...";
    return isDirectSubmit ? "Submit" : "Proceed ";
  }
  if (step === totalSteps - 1) {
    return submitting ? "Saving..." : "Submit";
  }
  return "Next";
};

const resolveProgramDraftFormType = (
  isCompletion,
  isFormlessProg,
  selectedType,
) => {
  if (isCompletion) return "DISCHARGE";
  if (isFormlessProg) return "N/A";
  if (
    selectedType &&
    String(selectedType).trim() !== "" &&
    String(selectedType).toUpperCase() !== "SELECT ASSESSMENT TYPE..."
  ) {
    return String(selectedType).toUpperCase();
  }
  return "N/A";
};

const classifyPayloadKey = (key) => {
  const upperKey = String(key || "").toUpperCase();
  if (
    key === "_uiState" ||
    key === "uiState" ||
    upperKey.includes("UI_STATE") ||
    upperKey.includes("UISTATE")
  ) {
    return "uiState";
  }
  if (
    key === "_outcomes" ||
    key === "_outcome" ||
    key === "outcomes" ||
    key === "outcome" ||
    upperKey.includes("_OUTCOMES") ||
    upperKey.includes("_OUTCOME")
  ) {
    return "outcomes";
  }
  if (upperKey.includes("METADATA")) {
    return "metadata";
  }
  if (
    (upperKey.includes("GENERAL") || upperKey.includes("OTHER")) &&
    !upperKey.includes("LIFESTYLE") &&
    !upperKey.includes("CARE_PLAN")
  ) {
    return "general";
  }
  if (upperKey.includes("LIFESTYLE")) {
    return "lifestyle";
  }
  if (upperKey.includes("CARE_PLAN")) {
    return "carePlan";
  }
  return "program";
};

const processLifestyleOrCarePlanSchemaEntry = (
  kind,
  key,
  payload,
  possibleSchema,
  state,
) => {
  const isLifestyle = kind === "lifestyle";
  const hasSections = Boolean(possibleSchema?.sections);
  const answers = hasSections
    ? extractAnswersFromSchema(possibleSchema)
    : payload[key];

  if (isLifestyle) {
    if (hasSections) state.extractedLifestyleSchema = possibleSchema;
    state.draftLifestyleAnswers = answers;
  } else {
    if (hasSections) state.extractedCarePlanSchema = possibleSchema;
    state.draftCarePlanAnswers = answers;
  }
};

const processProgramSchemaEntry = (key, payload, possibleSchema, state) => {
  const cleanedProgId = cleanProgramId(key);
  if (possibleSchema?.sections) {
    state.extractedProgramSchemasMap[`${cleanedProgId}_PROGRAM_SPECIFIC`] =
      possibleSchema;
    state.extractedProgramSchemasMap[key] = possibleSchema;
    state.extractedProgramSchemasMap[cleanedProgId] = possibleSchema;
    state.draftProgramAnswers[cleanedProgId] =
      extractAnswersFromSchema(possibleSchema);
  } else {
    state.draftProgramAnswers[cleanedProgId] = payload[key];
  }
};

const processPayloadSchemaEntry = (key, payload, state) => {
  const kind = classifyPayloadKey(key);
  if (kind === "uiState") {
    state.draftUiState = payload[key];
    return;
  }
  if (kind === "outcomes" || kind === "metadata") {
    return;
  }

  const possibleSchema =
    getTemplateSchemaFromPayload({ [key]: payload[key] }) ||
    getTemplateSchemaFromPayload(payload[key]);

  if (kind === "general") {
    if (possibleSchema?.sections) {
      state.extractedGenSchema = possibleSchema;
    }
  } else if (kind === "lifestyle" || kind === "carePlan") {
    processLifestyleOrCarePlanSchemaEntry(
      kind,
      key,
      payload,
      possibleSchema,
      state,
    );
  } else if (kind === "program") {
    processProgramSchemaEntry(key, payload, possibleSchema, state);
  }
};

const extractSessionSchemasFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      genKey: "OTHER_GENERAL",
      lifestyleKey: "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT",
      carePlanKey: "CARE_PLAN_COORDINATION_COMPLETION",
      progKey: null,
      extractedGenSchema: null,
      extractedLifestyleSchema: null,
      extractedCarePlanSchema: null,
      extractedProgramSchemasMap: {},
      draftProgramAnswers: {},
      draftGeneralAnswers: null,
      draftLifestyleAnswers: null,
      draftCarePlanAnswers: null,
      draftUiState: null,
    };
  }

  const keys = Object.keys(payload);
  const genKey =
    keys.find((k) => classifyPayloadKey(k) === "general") || "OTHER_GENERAL";
  const lifestyleKey =
    keys.find((k) => classifyPayloadKey(k) === "lifestyle") ||
    "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT";
  const carePlanKey =
    keys.find((k) => classifyPayloadKey(k) === "carePlan") ||
    "CARE_PLAN_COORDINATION_COMPLETION";
  const foundProgKey = keys.find(
    (k) =>
      classifyPayloadKey(k) === "program" &&
      k !== genKey &&
      k !== lifestyleKey &&
      k !== carePlanKey,
  );

  const state = {
    extractedGenSchema: null,
    extractedLifestyleSchema: null,
    extractedCarePlanSchema: null,
    extractedProgramSchemasMap: {},
    draftProgramAnswers: {},
    draftGeneralAnswers: null,
    draftLifestyleAnswers: null,
    draftCarePlanAnswers: null,
    draftUiState: null,
  };

  if (payload[genKey]) {
    const possibleGen = getTemplateSchemaFromPayload({
      [genKey]: payload[genKey],
    });
    if (possibleGen?.sections) {
      state.extractedGenSchema = possibleGen;
      state.draftGeneralAnswers = extractAnswersFromSchema(possibleGen);
    } else {
      state.draftGeneralAnswers = payload[genKey];
    }
  }

  keys.forEach((key) => {
    processPayloadSchemaEntry(key, payload, state);
  });

  return {
    genKey,
    lifestyleKey,
    carePlanKey,
    progKey: foundProgKey || null,
    ...state,
  };
};

const loadProgramSchemaFallback = async (
  targetId,
  desiredType,
  mapKey,
  cleanId,
) => {
  try {
    const batchData = await fetchAssessmentSchemasApi([
      `${targetId}:${desiredType}`,
    ]);
    const parsedBatch = parseSchemasResponse(batchData);
    if (
      parsedBatch?.programSchemasMap?.[mapKey] ||
      parsedBatch?.programSchemasMap?.[cleanId]
    ) {
      return (
        parsedBatch.programSchemasMap[mapKey] ||
        parsedBatch.programSchemasMap[cleanId]
      );
    }
  } catch (error_) {
    console.debug("fetchAssessmentSchemasApi fallback failed:", error_);
  }
  return fetchAssessmentTemplateByType(targetId, desiredType);
};

const findTargetGroupSummary = (allGroups, targetId, rawProgId, cleanId) => {
  return (allGroups || []).find((g) => {
    const cleanName = cleanProgramId(g.groupName);
    const cleanProgId = cleanProgramId(g.programId);
    const canonicalGId = getCanonicalProgramId(
      g.programId,
      g.groupName,
      allGroups,
    );
    return (
      g.programId === targetId ||
      g.programId === rawProgId ||
      canonicalGId === targetId ||
      cleanProgId === cleanId ||
      cleanName === cleanId ||
      cleanProgramId(g.displayName) === cleanId
    );
  });
};

const isProgramRemovedHelper = (key, name, removedProgramIds) => {
  if (!key && !name) return false;
  const upperKey = String(key || "")
    .toUpperCase()
    .trim();
  const cleanKey = cleanProgramId(key);
  const upperClean = String(cleanKey || "")
    .toUpperCase()
    .trim();
  const upperName = String(name || "")
    .toUpperCase()
    .trim();
  const normKey = normalizeProgramId(upperKey);
  const normName = normalizeProgramId(upperName);
  return (
    removedProgramIds.has(upperKey) ||
    removedProgramIds.has(upperClean) ||
    (normKey && removedProgramIds.has(normKey)) ||
    (upperName && removedProgramIds.has(upperName)) ||
    (normName && removedProgramIds.has(normName))
  );
};

const findMatchedGroupDisplayName = (
  availableProgramGroups,
  targetKey,
  cleanKey,
  normKey,
  normName,
) => {
  const matchedGroup = (availableProgramGroups || []).find((g) => {
    const gProgId = String(g.programId || g.id || "")
      .toUpperCase()
      .trim();
    const gCleanId = cleanProgramId(gProgId);
    const gCanonicalId = getCanonicalProgramId(
      g.programId,
      g.groupName,
      availableProgramGroups,
    );
    return (
      gProgId === targetKey ||
      gCleanId === cleanKey ||
      gCanonicalId === targetKey ||
      normalizeProgramId(g.groupName) === normKey ||
      normalizeProgramId(g.programDisplayName) === normKey ||
      (normName &&
        (normalizeProgramId(g.groupName) === normName ||
          normalizeProgramId(g.programDisplayName) === normName))
    );
  });
  return matchedGroup?.programDisplayName || matchedGroup?.groupName;
};

const addProgramToFilteredMapHelper = (
  map,
  seenMappedIds,
  rawKey,
  name,
  { removedProgramIds, availableProgramGroups, referenceProgramsMap },
) => {
  if (!rawKey && !name) return;
  if (isProgramRemovedHelper(rawKey, name, removedProgramIds)) return;
  if (isNonClinicalProgram(rawKey, name)) return;

  const canonicalId = getCanonicalProgramId(
    rawKey,
    name,
    availableProgramGroups,
  );
  const targetKey = String(
    canonicalId && canonicalId !== "OTHER" ? canonicalId : rawKey || name,
  )
    .trim()
    .toUpperCase();
  if (
    targetKey.includes("BIOSIMILAR") ||
    String(rawKey || "")
      .toUpperCase()
      .includes("BIOSIMILAR") ||
    String(name || "")
      .toUpperCase()
      .includes("BIOSIMILAR")
  ) {
    return;
  }
  if (isProgramRemovedHelper(targetKey, name, removedProgramIds)) return;
  if (isNonClinicalProgram(targetKey, name)) return;

  if (
    targetKey.includes("LIFESTYLE") ||
    targetKey.includes("CARE_PLAN") ||
    targetKey.includes("CAREPLAN") ||
    targetKey === "GENERAL" ||
    targetKey === "OTHER"
  ) {
    return;
  }

  const cleanKey = cleanProgramId(targetKey);
  const normKey = normalizeProgramId(targetKey);
  const normName = normalizeProgramId(name);

  if (
    !seenMappedIds.has(targetKey) &&
    !seenMappedIds.has(cleanKey) &&
    !seenMappedIds.has(normKey) &&
    (!normName || !seenMappedIds.has(normName))
  ) {
    seenMappedIds.add(targetKey);
    seenMappedIds.add(cleanKey);
    seenMappedIds.add(normKey);
    if (normName) seenMappedIds.add(normName);

    const groupDisplayName = findMatchedGroupDisplayName(
      availableProgramGroups,
      targetKey,
      cleanKey,
      normKey,
      normName,
    );

    const displayName =
      groupDisplayName ||
      name ||
      referenceProgramsMap[targetKey] ||
      PROGRAM_DISPLAY_NAMES[targetKey] ||
      targetKey;
    map[targetKey] = displayName;
  }
};

const shouldSkipOutreachKey = (normKey, name, normName) => {
  return (
    isNonClinicalProgram(normKey, name) ||
    normKey.includes("LIFESTYLE") ||
    normKey.includes("CARE_PLAN") ||
    normKey.includes("CAREPLAN") ||
    normKey === "GENERAL" ||
    normKey === "OTHER" ||
    normName.includes("LIFESTYLE") ||
    normName.includes("CAREPLAN") ||
    normName.includes("CARE_PLAN") ||
    normName.includes("GENERAL")
  );
};

const buildOutreachProgramEntry = (
  normKey,
  cleanKey,
  normKeyClean,
  name,
  {
    activePrograms,
    addedPrograms,
    selectedProgram,
    memberClinicalPrograms,
    referenceData,
    getOutcomeForProgram,
  },
) => {
  const matchedSource =
    findProgramMatch(activePrograms, normKey, name) ||
    findProgramMatch(addedPrograms, normKey, name) ||
    findProgramMatch(selectedProgram, normKey, name) ||
    findProgramMatch(memberClinicalPrograms, normKey, name);

  const isFormless = checkIsFormless(
    { id: normKey, cleanId: cleanKey, name },
    referenceData,
  );

  const isManual = Boolean(
    matchedSource?.manuallyAdded ||
    matchedSource?.isManual ||
    (addedPrograms || []).some((ad) => {
      const adClean = cleanProgramId(ad.programId || ad.id);
      return adClean === cleanKey || adClean === normKeyClean;
    }),
  );

  const rawType = matchedSource?.formType || matchedSource?.assessmentType;
  const progOutcome = getOutcomeForProgram({
    id: normKey,
    cleanId: cleanKey,
    name,
  });
  const isProgCompletion = isProgramCompletionOutcome(
    progOutcome,
    referenceData,
  );
  const chosenType = resolveChosenFormType(isProgCompletion, rawType);

  return {
    id: normKey,
    cleanId: cleanKey,
    name,
    formType: isFormless ? "N/A" : chosenType,
    assessmentType: isFormless ? "N/A" : chosenType,
    manuallyAdded: isManual,
    isFormless,
  };
};

const isProgramItemRemoved = (p, availableProgramGroups, removedSet) => {
  if (!p) return false;
  const rawId = String(p.programId || p.id || p.cleanId || p.name || "").trim();
  const canonicalId = getCanonicalProgramId(
    rawId,
    p.name || p.displayName || p.groupName,
    availableProgramGroups,
  );
  const cleanKey = cleanProgramId(canonicalId || rawId);
  const pid = rawId.toUpperCase();
  const pclean = (cleanKey || "").toUpperCase();
  const pnorm = normalizeProgramId(rawId);
  const pname = String(p.name || p.groupName || p.displayName || "")
    .toUpperCase()
    .trim();
  const pnamenorm = normalizeProgramId(pname);
  return (
    removedSet.has(pid) ||
    removedSet.has(pclean) ||
    (pnorm && removedSet.has(pnorm)) ||
    (pname && removedSet.has(pname)) ||
    (pnamenorm && removedSet.has(pnamenorm))
  );
};

const mergeSingleProgramItem = (
  p,
  combinedMap,
  {
    availableProgramGroups,
    removedSet,
    referenceData,
    activeList,
    addedList,
    selectedProgram,
    memberClinicalPrograms,
    getOutcomeForProgram,
  },
) => {
  if (!p || isProgramItemRemoved(p, availableProgramGroups, removedSet)) return;
  const rawId = p.programId || p.id || p.cleanId || p.name;
  if (!rawId) return;
  const canonicalId = getCanonicalProgramId(
    rawId,
    p.name || p.displayName || p.groupName,
    availableProgramGroups,
  );
  const cleanKey = cleanProgramId(canonicalId || rawId);
  const key = cleanKey ? cleanKey.toUpperCase() : String(rawId).toUpperCase();
  const existing = combinedMap.get(key) || {};

  const isFormless = resolveIsFormless(
    p,
    existing,
    rawId,
    cleanKey,
    referenceData,
  );

  const matchedActive =
    findProgramMatch(activeList, cleanKey, p.name) ||
    findProgramMatch(addedList, cleanKey, p.name) ||
    findProgramMatch(selectedProgram, cleanKey, p.name) ||
    findProgramMatch(memberClinicalPrograms, cleanKey, p.name);

  const rawFormType =
    p.formType ||
    p.assessmentType ||
    matchedActive?.formType ||
    matchedActive?.assessmentType ||
    existing.formType ||
    existing.assessmentType;

  const isProgCompletion = isProgramCompletionOutcome(
    getOutcomeForProgram(p),
    referenceData,
  );

  const formType = resolveProgramMergedFormType(
    isProgCompletion,
    isFormless,
    rawFormType,
  );

  const isManual = resolveProgramMergedManual(p, matchedActive, existing);
  const name = p.name || p.displayName || p.groupName || existing.name || rawId;

  combinedMap.set(key, {
    ...existing,
    ...p,
    id: rawId,
    programId: cleanKey || rawId,
    cleanId: cleanKey,
    name,
    formType,
    assessmentType: formType,
    manuallyAdded: isManual,
    isFormless,
  });
};

const shouldSkipEnrolledCandidate = (normKey, cleanNormKey, pName) => {
  return (
    normKey === "LIFESTYLE" ||
    normKey === "CARE_PLAN" ||
    normKey === "GENERAL" ||
    normKey === "OTHER" ||
    normKey.includes("BIOSIMILAR") ||
    cleanNormKey.includes("BIOSIMILAR") ||
    String(pName || "")
      .toUpperCase()
      .includes("BIOSIMILAR")
  );
};

const processEnrolledCandidate = (
  pName,
  rawPId,
  {
    availableProgramGroups,
    getOutcomeForProgram,
    referenceData,
    activePrograms,
    addedPrograms,
    selectedProgram,
    memberClinicalPrograms,
    seenIds,
    enrolledProgramList,
    enrolledActivePrograms,
    currentAssessmentType,
  },
) => {
  const canonicalId = getCanonicalProgramId(
    rawPId,
    pName,
    availableProgramGroups,
  );
  const finalProgId = canonicalId || rawPId;
  const normKey = String(finalProgId).toUpperCase().trim();
  const cleanNormKey = cleanProgramId(normKey);
  const normName = normalizeProgramId(pName);

  if (shouldSkipEnrolledCandidate(normKey, cleanNormKey, pName)) return;

  if (
    seenIds.has(normKey) ||
    seenIds.has(cleanNormKey) ||
    (normName && seenIds.has(normName))
  ) {
    return;
  }

  const outcomeVal = getOutcomeForProgram({
    id: finalProgId,
    cleanId: cleanNormKey,
    name: pName,
  });
  const isEnrolled = isEnrolledOutcome(outcomeVal, referenceData);
  const isCompletion = isProgramCompletionOutcome(outcomeVal, referenceData);

  if ((isEnrolled || isCompletion) && finalProgId) {
    seenIds.add(normKey);
    seenIds.add(cleanNormKey);
    if (normName) seenIds.add(normName);

    const isFormless = checkIsFormless(
      { id: finalProgId, cleanId: cleanNormKey, name: pName },
      referenceData,
    );

    const matchedSource =
      findProgramMatch(activePrograms, finalProgId, pName) ||
      findProgramMatch(addedPrograms, finalProgId, pName) ||
      findProgramMatch(selectedProgram, finalProgId, pName) ||
      findProgramMatch(memberClinicalPrograms, finalProgId, pName);

    const isManual = Boolean(
      matchedSource?.manuallyAdded ||
      matchedSource?.isManual ||
      (addedPrograms || []).some((ad) => {
        const adClean = cleanProgramId(ad.programId || ad.id);
        return adClean === cleanNormKey || adClean === normKey;
      }),
    );

    const selectedType =
      matchedSource?.formType ||
      matchedSource?.assessmentType ||
      matchedSource?.initialFormType ||
      matchedSource?.defaultAssessmentType;
    const chosenType = isFormless
      ? "N/A"
      : resolveEnrolledFormType(
          isCompletion,
          selectedType,
          currentAssessmentType || "INITIAL",
        );
    const progFormType = chosenType;

    enrolledProgramList.push({
      programId: cleanNormKey || finalProgId,
      formType: progFormType,
      ...(isManual && !isFormless ? { manuallyAdded: true } : {}),
    });
    if (!isFormless) {
      enrolledActivePrograms.push({
        id: cleanNormKey || finalProgId,
        name: pName,
        programId: cleanNormKey || finalProgId,
        formType: progFormType,
        assessmentType: progFormType,
        manuallyAdded: isManual,
      });
    }
  }
};

const buildSingleExportPdfProgramItem = (
  cleanKey,
  progName,
  outcomeVal,
  {
    referenceData,
    declinedNotes,
    declinedReason,
    selectedSubReason,
    activePrograms,
    addedPrograms,
    selectedProgram,
    memberClinicalPrograms,
    initialModalAssessmentTypes,
    currentAssessmentType,
    assessmentType,
  },
) => {
  const outcomeName = getOutcomeNameFromReferenceData(
    outcomeVal,
    referenceData,
  );
  const candidateKeys = [
    cleanKey,
    String(cleanKey).toUpperCase(),
    progName,
    String(progName || "").toUpperCase(),
  ].filter(Boolean);

  const noteVal =
    candidateKeys.map((k) => declinedNotes?.[k]).find(Boolean) || null;
  const reasonVal =
    candidateKeys.map((k) => declinedReason?.[k]).find(Boolean) || null;
  const subReasonVal =
    candidateKeys.map((k) => selectedSubReason?.[k]).find(Boolean) || null;

  const matchedActive =
    findProgramMatch(activePrograms, cleanKey, progName) ||
    findProgramMatch(addedPrograms, cleanKey, progName) ||
    findProgramMatch(selectedProgram, cleanKey, progName) ||
    findProgramMatch(memberClinicalPrograms, cleanKey, progName);

  const isManual = Boolean(
    matchedActive?.manuallyAdded ||
    matchedActive?.isManual ||
    addedPrograms?.some(
      (ad) => cleanProgramId(ad.id || ad.programId) === cleanKey,
    ),
  );

  const isCompletion = isProgramCompletionOutcome(outcomeVal, referenceData);

  const selectedType =
    matchedActive?.formType ||
    matchedActive?.assessmentType ||
    initialModalAssessmentTypes?.[cleanKey] ||
    initialModalAssessmentTypes?.[String(cleanKey).toUpperCase()];

  const resolvedFormType = isCompletion
    ? "DISCHARGE"
    : selectedType || currentAssessmentType || assessmentType || "INITIAL";

  return {
    programId: cleanKey,
    name: progName,
    outcome: outcomeName || outcomeVal,
    outcomeReason: reasonVal,
    subReason: subReasonVal,
    notes: noteVal,
    declinedNotes: noteVal,
    declinedNote: noteVal,
    manuallyAdded: isManual,
    isManual,
    assessmentType: resolvedFormType,
    formType: resolvedFormType,
  };
};

const buildExportPdfProgramsPayload = (
  filteredMap,
  selectedOutcome,
  context,
) => {
  const programsPayload = [];
  Object.keys(filteredMap || {}).forEach((progKey) => {
    const outcomeVal = selectedOutcome?.[progKey];
    if (outcomeVal) {
      const cleanKey = cleanProgramId(progKey);
      const progName = filteredMap[progKey];
      programsPayload.push(
        buildSingleExportPdfProgramItem(
          cleanKey,
          progName,
          outcomeVal,
          context,
        ),
      );
    }
  });
  return programsPayload;
};

export {
  isEnrolledOutcome,
  isProgramCompletionOutcome,
  isDeclinedOutcome,
  isUnableToReachOutcome,
  isDeclinedOrUnableToReach,
  getOutcomeNameFromReferenceData,
} from "../../api/assessment";
export { buildInitialPrograms };

const parseSingleRawProgram = (
  item,
  {
    canonicalProgramMap,
    selectedProgram,
    memberClinicalPrograms,
    isDraftSession,
  },
) => {
  if (!item) return null;
  const progId =
    typeof item === "string" ? item : item.programId || item.id || item.program;
  if (!progId) return null;
  const norm = mapLocalProgramId(progId);
  if (
    norm === "OTHER" ||
    norm === "GENERAL" ||
    norm === "LIFESTYLE" ||
    norm === "CARE_PLAN" ||
    norm === "METADATA" ||
    norm === "BIOSIMILARS"
  ) {
    return null;
  }
  const cleanedId = cleanProgramId(progId);
  const displayName =
    (typeof item === "object"
      ? item.programName || item.displayName || item.name
      : null) ||
    canonicalProgramMap[cleanedId] ||
    canonicalProgramMap[String(progId).toUpperCase()] ||
    PROGRAM_DISPLAY_NAMES[cleanedId] ||
    PROGRAM_DISPLAY_NAMES[String(progId).toUpperCase()] ||
    progId;

  const matchedFromSelected =
    findProgramMatch(selectedProgram, cleanedId, displayName) ||
    findProgramMatch(memberClinicalPrograms, cleanedId, displayName);

  if (!isDraftSession) {
    if (hasTerminalOutcome(item) || hasTerminalOutcome(matchedFromSelected)) {
      return null;
    }
    const isTerminalInMember =
      Array.isArray(memberClinicalPrograms) &&
      memberClinicalPrograms.some((mp) => {
        const mpId = mp.programId || mp.id;
        const mpName =
          mp.programDisplayName ||
          mp.programName ||
          mp.displayName ||
          mp.name ||
          mp.groupName;
        const matches =
          (mpId &&
            progId &&
            (cleanProgramId(mpId) === cleanedId ||
              String(mpId).toUpperCase() === String(progId).toUpperCase())) ||
          (mpName &&
            displayName &&
            String(mpName).toUpperCase() === String(displayName).toUpperCase());
        return matches && hasTerminalOutcome(mp);
      });
    if (isTerminalInMember) {
      return null;
    }
  }

  const manuallyAdded = Boolean(
    (typeof item === "object" ? item.manuallyAdded : false) ||
    matchedFromSelected?.manuallyAdded ||
    matchedFromSelected?.isManual ||
    (matchedFromSelected?.assessmentOnly &&
      matchedFromSelected?.nextAssessmentType),
  );
  const formType =
    (typeof item === "object"
      ? item.formType || item.assessmentType
      : undefined) ||
    matchedFromSelected?.formType ||
    matchedFromSelected?.assessmentType;
  const assessmentType =
    (typeof item === "object"
      ? item.assessmentType || item.formType
      : undefined) ||
    matchedFromSelected?.assessmentType ||
    matchedFromSelected?.formType;

  return {
    id: cleanedId,
    name: displayName,
    programId: cleanedId,
    manuallyAdded,
    formType,
    assessmentType,
    initialFormType: (formType || assessmentType || "INITIAL").toUpperCase(),
  };
};

const parseRawProgramsFromRes = (rawProgramsList, options, targetList) => {
  rawProgramsList.forEach((item) => {
    const prog = parseSingleRawProgram(item, options);
    if (prog) {
      targetList.push(prog);
    }
  });
};

const extractProgramKeysFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  return Object.keys(payload).filter(
    (k) =>
      !k.toUpperCase().includes("GENERAL") &&
      !k.toUpperCase().includes("OTHER") &&
      !k.toUpperCase().includes("LIFESTYLE") &&
      !k.toUpperCase().includes("CARE_PLAN") &&
      !k.toUpperCase().includes("METADATA") &&
      !k.toUpperCase().includes("BIOSIMILAR") &&
      !k.toUpperCase().includes("UI_STATE") &&
      !k.toUpperCase().includes("UISTATE") &&
      !k.toUpperCase().includes("OUTCOME") &&
      !k.startsWith("_") &&
      k !== "_uiState" &&
      k !== "_outcomes" &&
      k !== "_outcome",
  );
};

const parsePayloadFallbackKeys = (
  payloadKeys,
  canonicalProgramMap,
  targetList,
) => {
  payloadKeys.forEach((key) => {
    const cleanedId = cleanProgramId(key);
    if (!targetList.some((p) => cleanProgramId(p.id) === cleanedId)) {
      const displayName =
        canonicalProgramMap[cleanedId] ||
        canonicalProgramMap[String(key).toUpperCase()] ||
        PROGRAM_DISPLAY_NAMES[cleanedId] ||
        PROGRAM_DISPLAY_NAMES[String(key).toUpperCase()] ||
        key;
      targetList.push({
        id: cleanedId,
        name: displayName,
        programId: cleanedId,
      });
    }
  });
};

const mergeSelectedProgramsList = (
  selectedProgram,
  targetList,
  hasSavedPrograms,
) => {
  if (!selectedProgram) return;
  const progs = Array.isArray(selectedProgram)
    ? selectedProgram
    : [selectedProgram];
  progs.forEach((p) => {
    if (hasTerminalOutcome(p)) return;
    const progId = p.programId || p.id;
    if (!progId) return;
    const mapped = mapLocalProgramId(progId);
    if (
      mapped === "OTHER" ||
      mapped === "GENERAL" ||
      mapped === "LIFESTYLE" ||
      mapped === "CARE_PLAN" ||
      mapped === "METADATA" ||
      mapped === "BIOSIMILARS"
    ) {
      return;
    }
    const cleanedId = cleanProgramId(mapped || progId);
    const existingProg = targetList.find(
      (ip) => cleanProgramId(ip.id || ip.programId) === cleanedId,
    );
    if (existingProg) {
      if (!existingProg.formType && (p.formType || p.assessmentType)) {
        existingProg.formType = p.formType || p.assessmentType;
        existingProg.assessmentType = p.assessmentType || p.formType;
      }
      if (!existingProg.initialFormType) {
        existingProg.initialFormType = (
          p.formType ||
          p.assessmentType ||
          existingProg.formType ||
          "INITIAL"
        ).toUpperCase();
      }
      if (p.manuallyAdded || p.isManual) {
        existingProg.manuallyAdded = true;
      }
    } else if (!hasSavedPrograms) {
      const displayName =
        p.groupName ||
        p.programName ||
        p.programDisplayName ||
        p.name ||
        progId;
      const isManual = Boolean(p.manuallyAdded || p.isManual);
      const pType = p.formType || p.assessmentType;
      targetList.push({
        id: cleanedId,
        name: displayName,
        programId: cleanedId,
        manuallyAdded: isManual,
        formType: pType,
        assessmentType: pType,
        initialFormType: (pType || "INITIAL").toUpperCase(),
      });
    }
  });
};

const mergeMemberClinicalPrograms = (
  memberClinicalPrograms,
  targetList,
  canonicalProgramMap,
  hasSavedPrograms,
) => {
  if (
    !Array.isArray(memberClinicalPrograms) ||
    memberClinicalPrograms.length === 0
  ) {
    return;
  }
  memberClinicalPrograms.forEach((mp) => {
    if (mp.assessmentOnly && !mp.nextAssessmentType) return;
    if (hasTerminalOutcome(mp)) return;
    const progId = mp.programId || mp.id;
    if (!progId) return;
    const norm = mapLocalProgramId(progId);
    if (
      norm === "OTHER" ||
      norm === "GENERAL" ||
      norm === "LIFESTYLE" ||
      norm === "CARE_PLAN" ||
      norm === "METADATA" ||
      norm === "BIOSIMILARS"
    ) {
      return;
    }
    const cleanedId = cleanProgramId(progId);
    const existingProg = targetList.find(
      (ip) => cleanProgramId(ip.id || ip.programId) === cleanedId,
    );
    const isManual = Boolean(mp.assessmentOnly || mp.manuallyAdded);
    const pType = mp.nextAssessmentType || mp.assessmentType;
    if (existingProg) {
      if (!existingProg.formType && pType) {
        existingProg.formType = pType;
        existingProg.assessmentType = pType;
      }
      if (isManual) {
        existingProg.manuallyAdded = true;
      }
    } else if (!hasSavedPrograms) {
      const displayName =
        mp.programName ||
        mp.name ||
        mp.groupName ||
        mp.displayName ||
        canonicalProgramMap[cleanedId] ||
        cleanedId;
      targetList.push({
        id: cleanedId,
        name: displayName,
        programId: cleanedId,
        manuallyAdded: isManual,
        formType: pType,
        assessmentType: pType,
      });
    }
  });
};

const mergeWarningsPrograms = (warnings, canonicalProgramMap, targetList) => {
  if (!warnings || typeof warnings !== "object") return;
  Object.keys(warnings).forEach((wKey) => {
    const norm = mapLocalProgramId(wKey);
    const upperW = String(wKey).toUpperCase();
    if (
      norm !== "OTHER" &&
      norm !== "GENERAL" &&
      norm !== "LIFESTYLE" &&
      norm !== "CARE_PLAN" &&
      norm !== "METADATA" &&
      norm !== "BIOSIMILARS" &&
      !upperW.includes("LIFESTYLE") &&
      !upperW.includes("CARE_PLAN") &&
      !upperW.includes("CAREPLAN") &&
      !upperW.includes("GENERAL") &&
      !upperW.includes("OTHER") &&
      !upperW.includes("UI_STATE") &&
      !upperW.includes("UISTATE") &&
      !upperW.includes("OUTCOME") &&
      !wKey.startsWith("_")
    ) {
      const cleanedId = cleanProgramId(wKey);
      if (!targetList.some((p) => cleanProgramId(p.id) === cleanedId)) {
        const displayName =
          canonicalProgramMap[cleanedId] ||
          canonicalProgramMap[String(wKey).toUpperCase()] ||
          PROGRAM_DISPLAY_NAMES[cleanedId] ||
          PROGRAM_DISPLAY_NAMES[String(wKey).toUpperCase()] ||
          wKey;
        targetList.push({ id: cleanedId, name: displayName });
      }
    }
  });
};

const dedupeActiveProgramsList = (initialProgs) => {
  const seen = new Set();
  const dedupedProgs = [];
  initialProgs.forEach((p) => {
    const norm = mapLocalProgramId(p.id);
    const upperId = String(p.id || "").toUpperCase();
    const upperName = String(p.name || "").toUpperCase();
    if (
      norm !== "OTHER" &&
      norm !== "GENERAL" &&
      norm !== "LIFESTYLE" &&
      norm !== "CARE_PLAN" &&
      norm !== "METADATA" &&
      norm !== "BIOSIMILARS" &&
      !upperId.includes("LIFESTYLE") &&
      !upperId.includes("CARE_PLAN") &&
      !upperId.includes("CAREPLAN") &&
      !upperId.includes("GENERAL") &&
      !upperId.includes("OTHER") &&
      !upperName.includes("LIFESTYLE") &&
      !upperName.includes("CARE_PLAN") &&
      !upperName.includes("CAREPLAN") &&
      !upperId.includes("UI_STATE") &&
      !upperId.includes("UISTATE") &&
      !upperId.includes("OUTCOME") &&
      !String(p.id).startsWith("_") &&
      !seen.has(norm)
    ) {
      seen.add(norm);
      dedupedProgs.push(p);
    }
  });
  return dedupedProgs;
};

const resolveActivePrograms = (
  res,
  programMap,
  selectedProgram,
  isDraftSession,
  memberClinicalPrograms,
) => {
  const initialProgs = [];
  const canonicalProgramMap = {};
  if (programMap) {
    Object.entries(programMap).forEach(([id, name]) => {
      canonicalProgramMap[String(id).toUpperCase()] = name;
    });
  }

  const rawProgramsList =
    (Array.isArray(res?.programs) ? res.programs : null) ||
    (Array.isArray(res?.programOutcomes) ? res.programOutcomes : null) ||
    (Array.isArray(res?.programIds) ? res.programIds : null);

  if (
    rawProgramsList &&
    Array.isArray(rawProgramsList) &&
    rawProgramsList.length > 0
  ) {
    parseRawProgramsFromRes(
      rawProgramsList,
      {
        canonicalProgramMap,
        selectedProgram,
        memberClinicalPrograms,
        isDraftSession,
      },
      initialProgs,
    );
  }

  const payloadKeys = extractProgramKeysFromPayload(res?.payload);
  parsePayloadFallbackKeys(payloadKeys, canonicalProgramMap, initialProgs);

  const hasSavedPrograms = Boolean(
    (rawProgramsList && rawProgramsList.length > 0) ||
    (res?.payload?.programs && res.payload.programs.length > 0) ||
    isDraftSession ||
    (res?.id && res.id !== "preview"),
  );

  mergeSelectedProgramsList(selectedProgram, initialProgs, hasSavedPrograms);
  mergeMemberClinicalPrograms(
    memberClinicalPrograms,
    initialProgs,
    canonicalProgramMap,
    hasSavedPrograms,
  );
  mergeWarningsPrograms(res?.warnings, canonicalProgramMap, initialProgs);

  return dedupeActiveProgramsList(initialProgs);
};

const resolveActiveProgsFromRes = (res, programsToFetch) => {
  if (Array.isArray(res?.programOutcomes) && res.programOutcomes.length > 0) {
    return res.programOutcomes;
  }
  if (Array.isArray(res?.programs) && res.programs.length > 0) {
    return res.programs;
  }
  if (Array.isArray(res?.programIds) && res.programIds.length > 0) {
    return res.programIds;
  }
  return programsToFetch;
};

const isLifestyleGroup = (groupData) => {
  const name = String(groupData?.groupName || "").toLowerCase();
  const pid = String(groupData?.programId || "").toUpperCase();
  const type = String(groupData?.groupType || "").toUpperCase();
  return (
    type === "LIFESTYLE" ||
    pid.includes("LIFESTYLE") ||
    name.includes("lifestyle")
  );
};

const isCarePlanGroup = (groupData) => {
  const name = String(groupData?.groupName || "").toLowerCase();
  const pid = String(groupData?.programId || "").toUpperCase();
  const type = String(groupData?.groupType || "").toUpperCase();
  return (
    type === "CARE_PLAN" ||
    pid.includes("CARE_PLAN") ||
    name.includes("care plan") ||
    name.includes("careplan")
  );
};

const isGeneralGroup = (groupData) => {
  if (isLifestyleGroup(groupData) || isCarePlanGroup(groupData)) return false;
  const name = String(groupData?.groupName || "").toLowerCase();
  const pid = String(groupData?.programId || "").toUpperCase();
  const type = String(groupData?.groupType || "").toUpperCase();
  return (
    pid === "GLOBAL" ||
    pid === "OTHER" ||
    type === "GENERAL" ||
    name.includes("general")
  );
};

const checkAllProgramSchemasLoaded = (
  activeProgs,
  hasProgramSpecific,
  programSchemasMap,
) => {
  if (!hasProgramSpecific) return true;
  return (activeProgs || []).every((ap) => {
    const rawId =
      typeof ap === "string" ? ap : ap?.id || ap?.programId || ap?.name;
    const cleanId = cleanProgramId(rawId);
    if (!cleanId || cleanId === "OTHER" || cleanId === "GENERAL") return true;
    return !!programSchemasMap[`${cleanId}_PROGRAM_SPECIFIC`];
  });
};

const applyAssessmentGroupTemplate = (
  groupData,
  targetType,
  schemas,
  programSchemasMap,
) => {
  const matchingTemplate = groupData?.templates?.find(
    (t) => t.formType === targetType,
  );
  if (!matchingTemplate?.schemaPayload) return;

  const parsedSchema = parseSchemaPayload(matchingTemplate.schemaPayload);
  if (isLifestyleGroup(groupData)) {
    if (!schemas.lifestyleSchema) schemas.lifestyleSchema = parsedSchema;
  } else if (isCarePlanGroup(groupData)) {
    if (!schemas.carePlanSchema) schemas.carePlanSchema = parsedSchema;
  } else if (isGeneralGroup(groupData)) {
    if (!schemas.generalSchema) schemas.generalSchema = parsedSchema;
  } else {
    const cleanedProgId = cleanProgramId(groupData.programId);
    if (cleanedProgId) {
      programSchemasMap[`${cleanedProgId}_PROGRAM_SPECIFIC`] = parsedSchema;
    }
  }
};

const fetchMissingSchemasFromGroupIds = async (
  groupIds,
  targetType,
  schemas,
  programSchemasMap,
) => {
  const results = await Promise.allSettled(
    groupIds.map((id) => fetchAssessmentGroupById(id)),
  );
  results.forEach((result, index) => {
    const id = groupIds[index];
    try {
      if (result.status === "rejected") throw result.reason;
      applyAssessmentGroupTemplate(
        result.value,
        targetType,
        schemas,
        programSchemasMap,
      );
    } catch (error_) {
      console.error(`Error fetching assessment group ${id}:`, error_);
    }
  });
};

const findMatchingGroupId = (groupsList, cleanId, progName) => {
  const progIdNorm = mapLocalProgramId(cleanId);
  const normalizeName = (n) =>
    String(n || "")
      .toLowerCase()
      .replaceAll(/['\s_-]+/g, "");
  const progNameNorm = normalizeName(progName);

  const foundGroup = groupsList.find((g) => {
    const gIdNorm = mapLocalProgramId(g.programId);
    const gNameNorm = normalizeName(g.groupName);
    return (
      (gIdNorm !== "OTHER" && gIdNorm === progIdNorm) ||
      (progNameNorm && gNameNorm.includes(progNameNorm)) ||
      (gNameNorm && progNameNorm.includes(gNameNorm))
    );
  });
  return foundGroup ? foundGroup.id : null;
};

const fetchSingleFallbackProgramSchema = async (
  ap,
  assessmentType,
  programSchemasMap,
  groupsList,
) => {
  const cleanId = cleanProgramId(ap.id);
  const mapKey = `${cleanId}_PROGRAM_SPECIFIC`;
  if (programSchemasMap[mapKey]) return;

  const groupId = findMatchingGroupId(groupsList, cleanId, ap.name) || ap.id;
  if (!groupId) return;

  try {
    const targetGroup = await fetchAssessmentGroupById(groupId);
    if (targetGroup?.templates) {
      const normType = (assessmentType || "INITIAL").toUpperCase();
      const matchedTemplate = targetGroup.templates.find(
        (t) => t.formType === normType,
      );
      if (matchedTemplate?.schemaPayload) {
        programSchemasMap[mapKey] = parseSchemaPayload(
          matchedTemplate.schemaPayload,
        );
      }
    }
  } catch (err) {
    console.error("Error fetching full assessment group by ID:", err);
  }
};

const fetchFallbackProgramSchemas = async (
  programsToFetch,
  assessmentType,
  programSchemasMap,
) => {
  let groupsList = [];
  try {
    const groups = await fetchAssessmentGroups({});
    groupsList = Array.isArray(groups) ? groups : [];
  } catch (err) {
    console.error(
      "Error fetching assessment groups for template fallback:",
      err,
    );
  }

  await Promise.all(
    programsToFetch.map((ap) =>
      fetchSingleFallbackProgramSchema(
        ap,
        assessmentType,
        programSchemasMap,
        groupsList,
      ),
    ),
  );
};

const fetchFallbackGeneralSchemaFromGroups = async (assessmentType) => {
  try {
    const groups = await fetchAssessmentGroups({});
    const generalGroup = (groups || []).find(isGeneralGroup);
    if (generalGroup) {
      const groupData = await fetchAssessmentGroupById(generalGroup.id);
      const targetType = (assessmentType || "INITIAL").toUpperCase();
      const matchingTemplate = groupData?.templates?.find(
        (t) => t.formType === targetType,
      );
      if (matchingTemplate?.schemaPayload) {
        return parseSchemaPayload(matchingTemplate.schemaPayload);
      }
    }
  } catch (err) {
    console.error("Error fetching general assessment group template:", err);
  }
  return null;
};

const applyFollowUpOrDischargeDefaults = async (
  assessmentType,
  programsToFetch,
  hasProgramSpecific,
  schemas,
  programSchemasMap,
) => {
  const isSpecial =
    assessmentType &&
    (assessmentType.toUpperCase() === "FOLLOW_UP" ||
      assessmentType.toUpperCase() === "DISCHARGE");
  if (!isSpecial) return;

  const defaultTemplate = await fetchAssessmentTemplateByType(assessmentType);
  if (!schemas.generalSchema) schemas.generalSchema = defaultTemplate;
  if (!schemas.lifestyleSchema) schemas.lifestyleSchema = defaultTemplate;
  if (!schemas.carePlanSchema) schemas.carePlanSchema = defaultTemplate;

  if (hasProgramSpecific) {
    for (const ap of programsToFetch) {
      const cleanId = cleanProgramId(ap.id);
      const mapKey = `${cleanId}_PROGRAM_SPECIFIC`;
      if (!programSchemasMap[mapKey]) {
        programSchemasMap[mapKey] = defaultTemplate;
      }
    }
  }
};

const fetchAssessmentSchemas = async ({
  res,
  assessmentType,
  hasProgramSpecific,
  programsToFetch,
  generalSchema,
  programSchemasMap,
  lifestyleSchema,
  carePlanSchema,
  clinicalAssessmentId,
}) => {
  const schemas = {
    generalSchema,
    lifestyleSchema,
    carePlanSchema,
  };

  const isFollowUpOrDischarge =
    assessmentType &&
    (assessmentType.toUpperCase() === "FOLLOW_UP" ||
      assessmentType.toUpperCase() === "DISCHARGE") &&
    !clinicalAssessmentId;

  const activeProgs = resolveActiveProgsFromRes(res, programsToFetch);
  const hasAllProgramSchemas = checkAllProgramSchemasLoaded(
    activeProgs,
    hasProgramSpecific,
    programSchemasMap,
  );

  if (
    schemas.generalSchema &&
    schemas.lifestyleSchema &&
    schemas.carePlanSchema &&
    hasAllProgramSchemas
  ) {
    console.log(
      "All schemas successfully resolved from first API payload. Skipping individual template fetches.",
    );
    return schemas;
  }

  const groupIds = res?.assessmentGroupIds || [];
  const needsGroupFetch =
    !schemas.generalSchema ||
    !schemas.lifestyleSchema ||
    !schemas.carePlanSchema ||
    (hasProgramSpecific && !hasAllProgramSchemas);

  if (groupIds.length > 0 && !isFollowUpOrDischarge && needsGroupFetch) {
    const targetType = (assessmentType || "INITIAL").toUpperCase();
    await fetchMissingSchemasFromGroupIds(
      groupIds,
      targetType,
      schemas,
      programSchemasMap,
    );
  }

  if (hasProgramSpecific && !isFollowUpOrDischarge) {
    await fetchFallbackProgramSchemas(
      programsToFetch,
      assessmentType,
      programSchemasMap,
    );
  }

  if (
    !schemas.generalSchema &&
    (res?.schemaPayload || res?.template?.schemaPayload)
  ) {
    schemas.generalSchema = parseSchemaPayload(
      res.schemaPayload || res.template.schemaPayload,
    );
  }

  if (!schemas.generalSchema && !isFollowUpOrDischarge) {
    schemas.generalSchema =
      await fetchFallbackGeneralSchemaFromGroups(assessmentType);
  }

  await applyFollowUpOrDischargeDefaults(
    assessmentType,
    programsToFetch,
    hasProgramSpecific,
    schemas,
    programSchemasMap,
  );

  return schemas;
};

const sortObjectKeys = (obj) => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }
  return Object.keys(obj)
    .sort((a, b) => a.localeCompare(b))
    .reduce((sorted, key) => {
      sorted[key] = sortObjectKeys(obj[key]);
      return sorted;
    }, {});
};

const isPayloadEqual = (p1, p2) => {
  if (p1 === p2) return true;
  if (!p1 || !p2) return false;
  try {
    return (
      JSON.stringify(sortObjectKeys(p1)) === JSON.stringify(sortObjectKeys(p2))
    );
  } catch (error_) {
    console.debug("Payload comparison error:", error_);
    return false;
  }
};

const resolveProgramArrayFromResponse = (res) => {
  if (Array.isArray(res?.programOutcomes) && res.programOutcomes.length > 0) {
    return res.programOutcomes;
  }
  if (Array.isArray(res?.programs) && res.programs.length > 0) {
    return res.programs;
  }
  if (
    Array.isArray(res?.payload?.programs) &&
    res.payload.programs.length > 0
  ) {
    return res.payload.programs;
  }
  if (
    Array.isArray(res?.payload?.programOutcomes) &&
    res.payload.programOutcomes.length > 0
  ) {
    return res.payload.programOutcomes;
  }
  return null;
};

const applySingleProgramOutcome = (prog, maps) => {
  const { outcomesMap, subReasonsMap, reasonsMap, declinedNotesMap } = maps;
  const rawId =
    prog.programId ||
    prog.id ||
    prog.programCode ||
    prog.programName ||
    prog.name;
  if (!rawId) return;

  const canonicalId = mapLocalProgramId(rawId);
  const outcomeVal = prog.outcome || prog.programOutcome || "";
  const subReasonVal = prog.subReason || prog.sub_reason || "";
  const reasonVal =
    prog.reason || prog.outcomeReason || prog.declinedReason || "";
  const declinedNoteVal =
    prog.declinedNote ||
    prog.declinedNotes ||
    prog.declined_note ||
    prog.declined_notes ||
    prog.notes ||
    prog.note ||
    "";

  outcomesMap[rawId] = outcomeVal;
  outcomesMap[canonicalId] = outcomeVal;
  if (prog.programName) outcomesMap[prog.programName] = outcomeVal;
  if (prog.name) outcomesMap[prog.name] = outcomeVal;
  if (prog.id) outcomesMap[prog.id] = outcomeVal;

  subReasonsMap[rawId] = subReasonVal;
  subReasonsMap[canonicalId] = subReasonVal;
  if (prog.programName) subReasonsMap[prog.programName] = subReasonVal;
  if (prog.name) subReasonsMap[prog.name] = subReasonVal;
  if (prog.id) subReasonsMap[prog.id] = subReasonVal;

  reasonsMap[rawId] = reasonVal;
  reasonsMap[canonicalId] = reasonVal;
  if (prog.programName) reasonsMap[prog.programName] = reasonVal;
  if (prog.name) reasonsMap[prog.name] = reasonVal;
  if (prog.id) reasonsMap[prog.id] = reasonVal;

  declinedNotesMap[rawId] = declinedNoteVal;
  declinedNotesMap[canonicalId] = declinedNoteVal;
  if (prog.programName) declinedNotesMap[prog.programName] = declinedNoteVal;
  if (prog.name) declinedNotesMap[prog.name] = declinedNoteVal;
  if (prog.id) declinedNotesMap[prog.id] = declinedNoteVal;
};

const applyDefaultToPrograms = (
  targetMap,
  val,
  programMap,
  selectedProgram,
) => {
  if (!val) return;
  targetMap.OTHER = val;
  if (programMap) {
    Object.entries(programMap).forEach(([id, name]) => {
      const canonicalId = mapLocalProgramId(name || id);
      targetMap[canonicalId] = val;
    });
  }
  if (selectedProgram) {
    const progs = Array.isArray(selectedProgram)
      ? selectedProgram
      : [selectedProgram];
    progs.forEach((p) => {
      const progId =
        p.groupName ||
        p.programName ||
        p.program ||
        p.name ||
        p.programId ||
        p.id;
      const mapped = mapLocalProgramId(progId);
      targetMap[mapped] = val;
    });
  }
};

const extractDraftOutcomesFromPayload = (res, maps) => {
  const { outcomesMap, subReasonsMap, reasonsMap, declinedNotesMap } = maps;
  const draftOutcomes = res?.payload?._outcomes || res?.payload?._outcome;
  if (draftOutcomes) {
    if (draftOutcomes.selectedOutcome) {
      Object.assign(outcomesMap, draftOutcomes.selectedOutcome);
    }
    if (draftOutcomes.selectedSubReason) {
      Object.assign(subReasonsMap, draftOutcomes.selectedSubReason);
    }
    if (draftOutcomes.declinedReason) {
      Object.assign(reasonsMap, draftOutcomes.declinedReason);
    }
    if (draftOutcomes.declinedNotes) {
      Object.assign(declinedNotesMap, draftOutcomes.declinedNotes);
    }
    if (
      draftOutcomes.declinedNote &&
      typeof draftOutcomes.declinedNote === "object"
    ) {
      Object.assign(declinedNotesMap, draftOutcomes.declinedNote);
    }
  }

  const rootDeclinedNotes =
    res?.declinedNotes ||
    res?.declinedNote ||
    res?.payload?.declinedNotes ||
    res?.payload?.declinedNote;
  if (rootDeclinedNotes && typeof rootDeclinedNotes === "object") {
    Object.assign(declinedNotesMap, rootDeclinedNotes);
  }
};

const applyOutcomesFromResponse = (res, maps, programMap, selectedProgram) => {
  const { outcomesMap, subReasonsMap, reasonsMap, declinedNotesMap } = maps;
  const progArray = resolveProgramArrayFromResponse(res);

  if (progArray && progArray.length > 0) {
    progArray.forEach((prog) => {
      applySingleProgramOutcome(prog, maps);
    });
  } else {
    applyDefaultToPrograms(
      outcomesMap,
      res.outcome,
      programMap,
      selectedProgram,
    );
    applyDefaultToPrograms(
      subReasonsMap,
      res.subReason,
      programMap,
      selectedProgram,
    );
    applyDefaultToPrograms(
      reasonsMap,
      res.reason || res.outcomeReason,
      programMap,
      selectedProgram,
    );

    const rootDeclinedNotes =
      res?.declinedNotes ||
      res?.declinedNote ||
      res?.payload?.declinedNotes ||
      res?.payload?.declinedNote;
    const rawDeclinedNotes =
      typeof rootDeclinedNotes === "string"
        ? rootDeclinedNotes
        : res.declinedNote ||
          res.declinedNotes ||
          res.payload?.declinedNote ||
          res.payload?.declinedNotes;
    if (typeof rawDeclinedNotes === "string") {
      applyDefaultToPrograms(
        declinedNotesMap,
        rawDeclinedNotes,
        programMap,
        selectedProgram,
      );
    }
  }
};

const populateOutcomeStates = ({
  res,
  programMap,
  selectedProgram,
  setSelectedOutcome,
  setSelectedSubReason,
  setDeclinedReason,
  setDeclinedNotes,
}) => {
  const maps = {
    outcomesMap: {},
    subReasonsMap: {},
    reasonsMap: {},
    declinedNotesMap: {},
  };

  extractDraftOutcomesFromPayload(res, maps);
  applyOutcomesFromResponse(res, maps, programMap, selectedProgram);

  setSelectedOutcome(maps.outcomesMap);
  setSelectedSubReason(maps.subReasonsMap);
  setDeclinedReason(maps.reasonsMap);
  setDeclinedNotes(maps.declinedNotesMap);

  return maps;
};

const getFormNameForStep = (stepIdx, stepsList) => {
  if (Array.isArray(stepsList) && stepsList[stepIdx]) {
    return stepsList[stepIdx];
  }
  return "";
};

const findStepIndexByKeyword = (stepsList, normActive) => {
  const normalize = (str) =>
    String(str || "")
      .toLowerCase()
      .trim()
      .replaceAll(/['\s_-]+/g, "");

  const matchIndex = stepsList.findIndex((s) => normalize(s) === normActive);
  if (matchIndex !== -1) return matchIndex;

  const keywords = [
    { key: "outcome", pattern: (s) => s.includes("outcome") },
    {
      key: "core",
      pattern: (s) => s.includes("general") || s.includes("core"),
    },
    {
      key: "general",
      pattern: (s) => s.includes("general") || s.includes("core"),
    },
    { key: "program", pattern: (s) => s.includes("program") },
    { key: "lifestyle", pattern: (s) => s.includes("lifestyle") },
    { key: "care", pattern: (s) => s.includes("care") },
  ];

  for (const { key, pattern } of keywords) {
    if (normActive.includes(key)) {
      const idx = stepsList.findIndex((s) => pattern(normalize(s)));
      if (idx !== -1) return idx;
    }
  }
  return -1;
};

const getStepIndexFromUiState = (uiState, stepsList) => {
  if (!uiState) return 0;
  if (!Array.isArray(stepsList)) return 0;

  if (uiState.activeFormName) {
    const normActive = String(uiState.activeFormName)
      .toLowerCase()
      .trim()
      .replaceAll(/['\s_-]+/g, "");
    const foundIdx = findStepIndexByKeyword(stepsList, normActive);
    if (foundIdx !== -1) return foundIdx;
  }

  if (
    typeof uiState.activeStepIndex === "number" &&
    !Number.isNaN(uiState.activeStepIndex)
  ) {
    const idx = uiState.activeStepIndex;
    if (idx >= 0 && idx < stepsList.length) {
      return idx;
    }
    if (idx > 0 && idx - 1 < stepsList.length) {
      return idx - 1;
    }
  }

  return 0;
};

const removeKeysFromMap = (map, keys) => {
  const next = { ...map };
  keys.forEach((k) => {
    delete next[k];
  });
  return next;
};

const upsertProgramInList = (prevList, progObj, matchIdentifiers) => {
  const { upperId, upperClean, upperNorm } = matchIdentifiers;
  const exists = prevList.some((p) => {
    const pid = String(p.programId || p.id || "")
      .toUpperCase()
      .trim();
    return (
      pid === upperId ||
      pid === upperClean ||
      cleanProgramId(pid).toUpperCase() === upperClean ||
      (upperNorm && normalizeProgramId(pid) === upperNorm)
    );
  });
  if (exists) {
    return prevList.map((p) => {
      const pid = String(p.programId || p.id || "")
        .toUpperCase()
        .trim();
      if (
        pid === upperId ||
        pid === upperClean ||
        cleanProgramId(pid).toUpperCase() === upperClean ||
        (upperNorm && normalizeProgramId(pid) === upperNorm)
      ) {
        return { ...p, ...progObj };
      }
      return p;
    });
  }
  return [...prevList, progObj];
};

const applySessionIdsAndVersions = (res, setters) => {
  setters?.setGeneralAssessmentId?.(res.id);
  setters?.setLifestyleAssessmentId?.(res.id);
  setters?.setCarePlanAssessmentId?.(res.id);
  setters?.setGeneralAssessmentVersion?.(res.version || 0);
  const sessionAssigneeRn =
    res.assigneeRnName ||
    res.assignee_rn_name ||
    res.member?.assigneeRnName ||
    res.member?.assignee_rn_name ||
    res.payload?.assigneeRnName ||
    res.payload?._outcomes?.assigneeRnName ||
    res.assigneeName ||
    "";
  if (sessionAssigneeRn) {
    setters?.setAssigneeRnName?.(sessionAssigneeRn);
  }
  if (res.cgPlusDischarge !== undefined) {
    setters?.setCgPlusDischarge?.(res.cgPlusDischarge);
    if (res.cgPlusDischarge === true) {
      setters?.setCurrentAssessmentType?.("DISCHARGE");
    }
  }
};

const applySessionPayloadSchemas = (payload, setters) => {
  if (!payload || !setters) return;
  const {
    genKey,
    lifestyleKey,
    carePlanKey,
    extractedGenSchema,
    extractedLifestyleSchema,
    extractedCarePlanSchema,
    extractedProgramSchemasMap,
  } = extractSessionSchemasFromPayload(payload);

  setters.setGeneralPayloadKey?.(genKey);
  setters.setLifestylePayloadKey?.(lifestyleKey);
  setters.setCarePlanPayloadKey?.(carePlanKey);

  if (extractedGenSchema) {
    setters.setGeneralAssessmentSchema?.(extractedGenSchema);
  }
  if (extractedLifestyleSchema) {
    setters.setLifestyleAssessmentSchema?.(extractedLifestyleSchema);
  }
  if (extractedCarePlanSchema) {
    setters.setCarePlanAssessmentSchema?.(extractedCarePlanSchema);
  }
  if (
    extractedProgramSchemasMap &&
    Object.keys(extractedProgramSchemasMap).length > 0
  ) {
    setters.setProgramAssessmentSchema?.((prev) => ({
      ...prev,
      ...extractedProgramSchemasMap,
    }));
  }
};

const applyInitializedSessionSchemas = (res, setters) => {
  if (!res) return;
  applySessionIdsAndVersions(res, setters);
  applySessionPayloadSchemas(res.payload, setters);
};

const buildReadOnlyFilteredMap = ({
  map,
  seenMappedIds,
  activePrograms,
  selectedOutcome,
  options,
}) => {
  const addProg = (rawKey, name) => {
    addProgramToFilteredMapHelper(map, seenMappedIds, rawKey, name, options);
  };
  if (activePrograms && activePrograms.length > 0) {
    activePrograms.forEach((ap) => {
      const progId = ap.programId || ap.id;
      const progName = ap.name || ap.groupName || progId;
      const canonical = mapLocalProgramId(progId);
      const hasOutcome =
        selectedOutcome?.[progId] ||
        selectedOutcome?.[canonical] ||
        selectedOutcome?.[progName] ||
        (cleanProgramId(progId) && selectedOutcome?.[cleanProgramId(progId)]);
      if (progId && hasOutcome) {
        addProg(progId, progName);
      }
    });
  }
  if (selectedOutcome && Object.keys(selectedOutcome).length > 0) {
    Object.entries(selectedOutcome).forEach(([key, val]) => {
      if (
        val &&
        typeof val === "string" &&
        val.trim() &&
        key !== "OTHER" &&
        !key.startsWith("_") &&
        !key.toUpperCase().includes("NOTE")
      ) {
        const canonicalId = mapLocalProgramId(key);
        const displayName =
          options.referenceProgramsMap[key] ||
          options.referenceProgramsMap[canonicalId] ||
          PROGRAM_DISPLAY_NAMES[key] ||
          PROGRAM_DISPLAY_NAMES[canonicalId] ||
          key;
        addProg(key, displayName);
      }
    });
  }
  return map;
};

const buildActiveSessionFilteredMap = ({
  map,
  seenMappedIds,
  activePrograms,
  addedPrograms,
  options,
}) => {
  const addProg = (rawKey, name) => {
    addProgramToFilteredMapHelper(map, seenMappedIds, rawKey, name, options);
  };
  if (activePrograms && activePrograms.length > 0) {
    activePrograms.forEach((ap) => {
      const progId = ap.programId || ap.id;
      const progName = ap.name || ap.groupName || progId;
      if (progId) {
        addProg(progId, progName);
      }
    });
  }
  if (addedPrograms && addedPrograms.length > 0) {
    addedPrograms.forEach((ap) => {
      const progKey = ap.programId || ap.id || ap.groupName || ap.name;
      const progName =
        ap.programDisplayName ||
        ap.groupName ||
        ap.name ||
        ap.displayName ||
        progKey;
      if (progKey) {
        addProg(progKey, progName);
      }
    });
  }
  return map;
};

const buildFallbackFilteredMap = ({
  map,
  seenMappedIds,
  selectedProgram,
  memberClinicalPrograms,
  programMap,
  options,
}) => {
  const addProg = (rawKey, name) => {
    addProgramToFilteredMapHelper(map, seenMappedIds, rawKey, name, options);
  };
  if (selectedProgram) {
    const progs = Array.isArray(selectedProgram)
      ? selectedProgram
      : [selectedProgram];
    progs.forEach((p) => {
      const progId = p.programId || p.id;
      const pName =
        p.programDisplayName ||
        p.groupName ||
        p.programName ||
        p.program ||
        p.name ||
        progId;
      if (progId) {
        addProg(progId, pName);
      }
    });
  }
  if (
    Array.isArray(memberClinicalPrograms) &&
    memberClinicalPrograms.length > 0
  ) {
    memberClinicalPrograms.forEach((mp) => {
      if (hasTerminalOutcome(mp, options.referenceData)) return;
      const progId = mp.programId || mp.id;
      const progName =
        mp.programDisplayName ||
        mp.groupName ||
        mp.name ||
        mp.programName ||
        progId;
      if (progId) {
        addProg(progId, progName);
      }
    });
  }
  if (
    Object.keys(map).length === 0 &&
    programMap &&
    Object.keys(programMap).length > 0
  ) {
    Object.entries(programMap).forEach(([id, name]) => {
      if (!isNonClinicalProgram(id, name)) {
        const hasTerminal = (memberClinicalPrograms || []).some((mp) => {
          const mpId = mp.programId || mp.id;
          const mpName =
            mp.programDisplayName || mp.groupName || mp.name || mp.programName;
          const matches =
            (id &&
              mpId &&
              (cleanProgramId(mpId) === cleanProgramId(id) ||
                String(mpId).toUpperCase() === String(id).toUpperCase())) ||
            (name &&
              mpName &&
              String(mpName).toUpperCase() === String(name).toUpperCase());
          return matches && hasTerminalOutcome(mp, options.referenceData);
        });
        if (!hasTerminal) {
          addProg(id, name);
        }
      }
    });
  }
  return map;
};

const resolveMapValueForProgram = (map, keys, isBiosimilars) => {
  if (!map) return null;
  for (const k of keys) {
    if (k && map[k]) return map[k];
  }
  if (isBiosimilars) {
    return (
      map.BIOSIMILARS || map.Biosimilars || map.Biosimilar || map.OTHER || null
    );
  }
  return null;
};

const applySingleTemplateToSchemas = (t, normType, schemas) => {
  if (!t?.schemaPayload || (t.formType && t.formType !== normType)) {
    return;
  }
  const parsed = parseSchemaPayload(t.schemaPayload);
  const groupType = String(t.groupType || t.type || "").toUpperCase();
  const progId = String(t.programId || t.program || "").toUpperCase();

  if (groupType === "LIFESTYLE" || progId.includes("LIFESTYLE")) {
    if (!schemas.draftLifestyleSchema) schemas.draftLifestyleSchema = parsed;
  } else if (groupType === "CARE_PLAN" || progId.includes("CARE_PLAN")) {
    if (!schemas.draftCarePlanSchema) schemas.draftCarePlanSchema = parsed;
  } else if (
    groupType === "GENERAL" ||
    progId === "GLOBAL" ||
    progId === "OTHER"
  ) {
    if (!schemas.generalSchema) schemas.generalSchema = parsed;
  } else if (progId) {
    const cleanedProgId = cleanProgramId(progId);
    schemas.programSchemasMap[`${cleanedProgId}_PROGRAM_SPECIFIC`] = parsed;
  }
};

const parseAvailableSessionTemplates = (
  availableTemplates,
  assessmentType,
  currentAssessmentType,
  schemas,
) => {
  if (!availableTemplates || availableTemplates.length === 0) return;
  const normType = (
    assessmentType ||
    currentAssessmentType ||
    "INITIAL"
  ).toUpperCase();
  availableTemplates.forEach((t) => {
    applySingleTemplateToSchemas(t, normType, schemas);
  });
};

const buildLoadedSingleProgramPayload = (
  ap,
  outcomeMaps,
  targetType,
  referenceData,
) => {
  const rawProgId = ap.programId || ap.id || ap.programCode || ap.name;
  const canonicalId = mapLocalProgramId(rawProgId);
  const isBiosimilars =
    canonicalId === "BIOSIMILARS" ||
    String(rawProgId).toUpperCase().includes("BIOSIMILAR") ||
    String(ap.id || "")
      .toUpperCase()
      .includes("BIOSIMILAR") ||
    String(ap.name || "")
      .toUpperCase()
      .includes("BIOSIMILAR");

  const outcomeKeys = [canonicalId, ap.id, rawProgId, ap.name];
  const outcome =
    resolveMapValueForProgram(
      outcomeMaps.outcomesMap,
      outcomeKeys,
      isBiosimilars,
    ) || "";
  const outcomeName = getOutcomeNameFromReferenceData(outcome, referenceData);
  const outcomeStr = outcomeName || "";

  const isDeclined = isDeclinedOutcome(outcome, referenceData);
  const isUnableToReach = isUnableToReachOutcome(outcome, referenceData);
  const hasReasonOrNotes = isDeclined || isUnableToReach;

  const subReasonVal = resolveMapValueForProgram(
    outcomeMaps.subReasonsMap,
    outcomeKeys,
    isBiosimilars,
  );
  let outcomeReasonVal = null;
  if (hasReasonOrNotes) {
    outcomeReasonVal = resolveMapValueForProgram(
      outcomeMaps.reasonsMap,
      outcomeKeys,
      isBiosimilars,
    );
  }
  let declinedNotesVal = null;
  if (isDeclined) {
    declinedNotesVal = resolveMapValueForProgram(
      outcomeMaps.declinedNotesMap,
      outcomeKeys,
      isBiosimilars,
    );
  }

  return {
    programId: rawProgId,
    formType: (targetType || "INITIAL").toUpperCase(),
    outcomeName: outcomeStr,
    outcome: outcomeStr,
    subReason: subReasonVal,
    reason: outcomeReasonVal,
    declinedNote: declinedNotesVal,
  };
};

const buildLoadedProgramsPayload = (
  dedupedProgs,
  outcomeMaps,
  targetType,
  referenceData,
) => {
  const loadedProgramsPayload = [];
  const seenMappedIds = new Set();
  dedupedProgs.forEach((ap) => {
    const rawProgId = ap.programId || ap.id || ap.programCode || ap.name;
    if (seenMappedIds.has(rawProgId)) return;
    seenMappedIds.add(rawProgId);
    loadedProgramsPayload.push(
      buildLoadedSingleProgramPayload(
        ap,
        outcomeMaps,
        targetType,
        referenceData,
      ),
    );
  });
  return loadedProgramsPayload;
};

const fetchInitialAssessmentSessionData = async ({
  initialAssessmentData,
  batchId,
  clinicalAssessmentId,
  isBatch,
  readOnly,
  initialStartTime,
  setAvailableProgramGroups,
  setNoteStartTime,
}) => {
  const targetId = batchId || clinicalAssessmentId;
  const hasBatch = Boolean(batchId || isBatch);

  if (initialAssessmentData) {
    console.log(
      "Using initialAssessmentData from modal initialize:",
      initialAssessmentData,
    );
    return initialAssessmentData;
  }
  if (targetId) {
    console.log(
      "Fetching draft payload with ID:",
      targetId,
      "hasBatch:",
      hasBatch,
    );
    const params = hasBatch ? { isBatch: true } : undefined;
    return await fetchDraftPayloadApi(targetId, params);
  }
  if (readOnly) {
    console.log(
      "ClinicalNotePage in read-only preview, skipping initialize API call.",
    );
    return { id: "preview", version: 0, payload: {} };
  }

  console.log(
    "New assessment startup: deferring /api/v1/assessments API call until Proceed.",
  );
  try {
    const groups = await fetchAssessmentGroups({});
    const programGroups = (groups || []).filter(
      (g) =>
        String(g.groupType || "").toUpperCase() === "PROGRAM_SPECIFIC" &&
        g.status !== "INACTIVE",
    );
    setAvailableProgramGroups(programGroups);
  } catch (err) {
    console.error("Failed to fetch available assessment groups:", err);
  }
  setNoteStartTime(initialStartTime || new Date().toISOString());
  return { id: null, version: 0, payload: {} };
};

const handleFallbackMockSession = async ({
  error_,
  assessmentType,
  selectedProgram,
  generalPayloadKey,
  lifestylePayloadKey,
  carePlanPayloadKey,
  setters,
  lastSavedSignatureRef,
}) => {
  console.error("Error initializing assessment session:", error_);
  if (
    error_.response?.status !== 400 &&
    !String(error_).includes("400") &&
    error_.status !== 400
  ) {
    console.warn(
      "Failed to initialize assessment session:",
      error_.response?.data?.message || error_.message,
    );
    return;
  }

  const {
    setGeneralAssessmentId,
    setLifestyleAssessmentId,
    setCarePlanAssessmentId,
    setGeneralAssessmentVersion,
    setNoteStartTime,
    setNoteCompletionTime,
    setGeneralAssessmentSchema,
    setLifestyleAssessmentSchema,
    setCarePlanAssessmentSchema,
    setProgramAssessmentSchema,
    setActivePrograms,
  } = setters;

  setGeneralAssessmentId("fallback-id");
  setLifestyleAssessmentId("fallback-id");
  setCarePlanAssessmentId("fallback-id");
  setGeneralAssessmentVersion(0);
  setNoteStartTime(new Date().toISOString());
  setNoteCompletionTime(null);

  lastSavedSignatureRef.current = {
    signatureConfirmed: false,
    payloadDelta: {
      [generalPayloadKey]: {},
      [lifestylePayloadKey]: {},
      [carePlanPayloadKey]: {},
    },
    programs: [],
    clinicalNote: "",
  };

  let generalSchema = null;
  try {
    const groups = await fetchAssessmentGroups({});
    const groupsList = Array.isArray(groups) ? groups : [];
    const targetType = (assessmentType || "INITIAL").toUpperCase();

    const generalGroup = groupsList.find((g) => {
      const norm = String(g.programId || "")
        .toUpperCase()
        .trim();
      const type = String(g.groupType || "")
        .toUpperCase()
        .trim();
      const name = String(g.groupName || "").toLowerCase();
      return (
        norm === "OTHER" ||
        norm === "GENERAL" ||
        norm === "OTHER_GENERAL" ||
        type === "GENERAL" ||
        name.includes("general")
      );
    });
    if (generalGroup) {
      const groupData = await fetchAssessmentGroupById(generalGroup.id);
      const matchingTemplate = groupData?.templates?.find(
        (t) => t.formType === targetType,
      );
      if (matchingTemplate?.schemaPayload) {
        generalSchema = parseSchemaPayload(matchingTemplate.schemaPayload);
      }
    }
  } catch (error__) {
    console.debug("Error fetching general fallback schema:", error__);
  }

  setGeneralAssessmentSchema(generalSchema);
  setLifestyleAssessmentSchema(null);
  setCarePlanAssessmentSchema(null);
  setProgramAssessmentSchema(null);

  const initialProgs = [];
  if (selectedProgram) {
    const progs = Array.isArray(selectedProgram)
      ? selectedProgram
      : [selectedProgram];
    progs.forEach((p) => {
      const progId = p.programId || p.id;
      const displayName =
        p.groupName || p.programName || p.program || p.name || progId;
      initialProgs.push({ id: progId, name: displayName });
    });
  }
  setActivePrograms(initialProgs);
};

const buildProgramsToFetchList = (
  dedupedProgs,
  programMap,
  localHasProgramSpecific,
) => {
  const programsToFetch = [...dedupedProgs];
  if (programMap && localHasProgramSpecific) {
    Object.entries(programMap).forEach(([id, name]) => {
      if (isNonClinicalProgram(id, name)) return;
      const upperKey = String(id).toUpperCase();
      const upperName = String(name).toUpperCase();
      if (upperKey.includes("METADATA") || upperName.includes("METADATA"))
        return;
      const cleanId = cleanProgramId(id);
      const mappedId = mapLocalProgramId(name || cleanId);
      if (!programsToFetch.some((p) => mapLocalProgramId(p.id) === mappedId)) {
        const displayName = PROGRAM_DISPLAY_NAMES[mappedId] || name;
        programsToFetch.push({ id: mappedId, name: displayName });
      }
    });
  }
  return programsToFetch;
};

const applyDraftAnswersAndUiState = ({
  draftGeneralAnswers,
  draftLifestyleAnswers,
  draftCarePlanAnswers,
  draftProgramAnswers,
  draftUiState,
  localHasProgramSpecific,
  generalGroupName,
  lifestyleGroupName,
  carePlanGroupName,
  setters,
}) => {
  if (draftGeneralAnswers) setters.setGeneralFormPayload(draftGeneralAnswers);
  if (draftLifestyleAnswers)
    setters.setLifestyleFormPayload(draftLifestyleAnswers);
  if (draftCarePlanAnswers)
    setters.setCarePlanFormPayload(draftCarePlanAnswers);
  if (localHasProgramSpecific && draftProgramAnswers) {
    setters.setProgramFormPayload(draftProgramAnswers);
  }

  if (draftUiState) {
    const currentStepsList = [
      "Eligible Programs",
      generalGroupName || "General Assessment",
      ...(localHasProgramSpecific ? ["Program-Specific Assessment"] : []),
      lifestyleGroupName || "Lifestyle, Barriers and Functional Assessment",
      carePlanGroupName || "Care Plan, Coordination and Completion",
    ];
    const restoredStep = getStepIndexFromUiState(
      draftUiState,
      currentStepsList,
    );
    setters.setActiveStep(restoredStep);
    if (Array.isArray(draftUiState.visitedForms)) {
      setters.setVisitedForms(draftUiState.visitedForms);
    }
    const draftActiveProg =
      draftUiState.activeProgram ||
      draftUiState.activeProgramId ||
      draftUiState.activeProgramTab;
    if (draftActiveProg) {
      setters.setActiveProgramTab(draftActiveProg);
    }
  }
};

const buildLoadedPayloadDeltaAndSignature = ({
  genKey,
  lifestyleKey,
  carePlanKey,
  draftGeneralAnswers,
  draftLifestyleAnswers,
  draftCarePlanAnswers,
  draftProgramAnswers,
  draftUiState,
  localHasProgramSpecific,
  dedupedProgs,
  loadedClinicalNote,
  clinicalNotes,
  outcomeMaps,
  currentAssessmentType,
  res,
  referenceData,
  certVal,
  lastSavedSignatureRef,
}) => {
  const loadedPayloadDelta = {
    [genKey]: draftGeneralAnswers || {},
    [lifestyleKey]: draftLifestyleAnswers || {},
    [carePlanKey]: draftCarePlanAnswers || {},
    clinicalNotes: loadedClinicalNote || clinicalNotes || "",
  };
  if (draftUiState) {
    loadedPayloadDelta._uiState = draftUiState;
  }
  if (localHasProgramSpecific && draftProgramAnswers) {
    dedupedProgs.forEach((ap) => {
      const cleanId = cleanProgramId(ap.id);
      loadedPayloadDelta[`${cleanId}_PROGRAM_SPECIFIC`] =
        draftProgramAnswers[cleanId] || {};
    });
  }

  const loadedProgramsPayload = buildLoadedProgramsPayload(
    dedupedProgs,
    outcomeMaps,
    currentAssessmentType || res.assessmentType,
    referenceData,
  );

  lastSavedSignatureRef.current = {
    signatureConfirmed: certVal,
    payloadDelta: loadedPayloadDelta,
    programs: loadedProgramsPayload,
    clinicalNote: res.clinicalNote || "",
  };
};

const resolveSessionAssigneeRn = (res) =>
  res.assigneeRnName ||
  res.assignee_rn_name ||
  res.member?.assigneeRnName ||
  res.member?.assignee_rn_name ||
  res.payload?.assigneeRnName ||
  res.payload?._outcomes?.assigneeRnName ||
  res.assigneeName ||
  "";

const applyCgPlusDischarge = (
  cgPlusDischarge,
  setCgPlusDischarge,
  setCurrentAssessmentType,
) => {
  if (cgPlusDischarge === undefined) return;
  setCgPlusDischarge?.(cgPlusDischarge);
  if (cgPlusDischarge === true) {
    setCurrentAssessmentType("DISCHARGE");
  }
};

const resolveIsDraftFlag = ({
  res,
  initialAssessmentData,
  isDraftSession,
  readOnly,
}) => {
  const isExistingDraft =
    !initialAssessmentData &&
    (Boolean(isDraftSession) ||
      res.isDraft === true ||
      String(res.status || "").toUpperCase() === "DRAFT");

  return Boolean(
    isExistingDraft &&
    res.id &&
    res.id !== "preview" &&
    !readOnly &&
    !["COMPLETED", "SUBMITTED", "SUCCESS"].includes(res.status),
  );
};

const resolveSessionGeneralSchema = (res, extractedGenSchema) => {
  const schemaPayload = res.schemaPayload || res.template?.schemaPayload;
  return schemaPayload ? parseSchemaPayload(schemaPayload) : extractedGenSchema;
};

const resolveAvailableTemplates = (res) => {
  if (res.templates) return res.templates;
  return res.assessmentGroups
    ? res.assessmentGroups.flatMap((g) => g.templates || [])
    : [];
};

const applyAssessmentSessionSuccessData = async ({
  res,
  clinicalAssessmentId,
  initialAssessmentData,
  isDraftSession,
  readOnly,
  initialStartTime,
  initialClinicalNotes,
  programMap,
  selectedProgram,
  memberClinicalPrograms,
  currentAssessmentType,
  setCurrentAssessmentType,
  generalGroupName,
  lifestyleGroupName,
  carePlanGroupName,
  referenceData,
  setters,
  refs,
}) => {
  const {
    setGeneralAssessmentId,
    setLifestyleAssessmentId,
    setCarePlanAssessmentId,
    setGeneralAssessmentVersion,
    setIsDraft,
    setWarnings,
    setNoteStartTime,
    setNoteCompletionTime,
    setElapsedTime,
    setClinicalNotes,
    setActivePrograms,
    setAddedPrograms,
    setGeneralPayloadKey,
    setLifestylePayloadKey,
    setCarePlanPayloadKey,
    setGeneralAssessmentSchema,
    setLifestyleAssessmentSchema,
    setCarePlanAssessmentSchema,
    setProgramAssessmentSchema,
    setSelectedOutcome,
    setSelectedSubReason,
    setDeclinedReason,
    setDeclinedNotes,
    setCertification,
  } = setters;

  const { elapsedTimeRef, lastSavedSignatureRef, latestAssessmentVersionRef } =
    refs;

  setGeneralAssessmentId(res.id);
  setLifestyleAssessmentId(res.id);
  setCarePlanAssessmentId(res.id);
  const initialVersion = res.version ?? 0;
  setGeneralAssessmentVersion(initialVersion);
  if (latestAssessmentVersionRef) {
    latestAssessmentVersionRef.current = initialVersion;
  }

  const sessionAssigneeRn = resolveSessionAssigneeRn(res);
  if (sessionAssigneeRn) {
    setters.setAssigneeRnName?.(sessionAssigneeRn);
  }
  applyCgPlusDischarge(
    res.cgPlusDischarge,
    setters.setCgPlusDischarge,
    setCurrentAssessmentType,
  );

  if (res.assessmentType) {
    setCurrentAssessmentType(res.assessmentType);
  }

  setIsDraft(
    resolveIsDraftFlag({
      res,
      initialAssessmentData,
      isDraftSession,
      readOnly,
    }),
  );

  setWarnings({
    ...res.warnings,
    ...res.schemaWarnings,
  });
  setNoteStartTime(
    initialStartTime || res.startTime || new Date().toISOString(),
  );
  setNoteCompletionTime(res.completionTime || null);

  const loadedElapsed = parseElapsedTime(res);
  setElapsedTime(loadedElapsed);
  elapsedTimeRef.current = loadedElapsed;

  const loadedClinicalNote = extractClinicalNotesFromResponse(
    res,
    initialClinicalNotes,
  );
  if (loadedClinicalNote) {
    setClinicalNotes(loadedClinicalNote);
  }

  const dedupedProgs = resolveActivePrograms(
    res,
    programMap,
    selectedProgram,
    Boolean(clinicalAssessmentId),
    memberClinicalPrograms,
  );
  setActivePrograms(dedupedProgs);
  const initialManual = dedupedProgs.filter((p) => Boolean(p.manuallyAdded));
  if (initialManual.length > 0) {
    setAddedPrograms((prev) => mergeAddedPrograms(prev, initialManual));
  }

  const localHasProgramSpecific = clinicalAssessmentId
    ? dedupedProgs.length > 0
    : selectedProgram && selectedProgram.groupType !== "GENERAL";

  const programsToFetch = buildProgramsToFetchList(
    dedupedProgs,
    programMap,
    localHasProgramSpecific,
  );

  const {
    genKey,
    lifestyleKey,
    carePlanKey,
    extractedGenSchema,
    extractedLifestyleSchema,
    extractedCarePlanSchema,
    extractedProgramSchemasMap,
    draftProgramAnswers,
    draftGeneralAnswers,
    draftLifestyleAnswers,
    draftCarePlanAnswers,
    draftUiState,
  } = extractSessionSchemasFromPayload(res.payload);

  setGeneralPayloadKey(genKey);
  setLifestylePayloadKey(lifestyleKey);
  setCarePlanPayloadKey(carePlanKey);

  const schemasState = {
    generalSchema: resolveSessionGeneralSchema(res, extractedGenSchema),
    draftLifestyleSchema: extractedLifestyleSchema,
    draftCarePlanSchema: extractedCarePlanSchema,
    programSchemasMap: { ...extractedProgramSchemasMap },
  };

  parseAvailableSessionTemplates(
    resolveAvailableTemplates(res),
    res?.assessmentType,
    currentAssessmentType,
    schemasState,
  );

  let fetchedGenSchema = schemasState.generalSchema;
  let fetchedLifestyleSchema = schemasState.draftLifestyleSchema;
  let fetchedCarePlanSchema = schemasState.draftCarePlanSchema;

  if (clinicalAssessmentId || readOnly) {
    const fetchedSchemas = await fetchAssessmentSchemas({
      res,
      assessmentType: res?.assessmentType || currentAssessmentType,
      hasProgramSpecific: localHasProgramSpecific,
      programsToFetch,
      generalSchema: schemasState.generalSchema,
      programSchemasMap: schemasState.programSchemasMap,
      lifestyleSchema: schemasState.draftLifestyleSchema,
      carePlanSchema: schemasState.draftCarePlanSchema,
      clinicalAssessmentId,
    });
    fetchedGenSchema = fetchedSchemas.generalSchema;
    fetchedLifestyleSchema = fetchedSchemas.lifestyleSchema;
    fetchedCarePlanSchema = fetchedSchemas.carePlanSchema;
  }

  setGeneralAssessmentSchema(fetchedGenSchema);
  if (fetchedLifestyleSchema)
    setLifestyleAssessmentSchema(fetchedLifestyleSchema);
  if (fetchedCarePlanSchema) setCarePlanAssessmentSchema(fetchedCarePlanSchema);
  if (localHasProgramSpecific)
    setProgramAssessmentSchema(schemasState.programSchemasMap);

  applyDraftAnswersAndUiState({
    draftGeneralAnswers,
    draftLifestyleAnswers,
    draftCarePlanAnswers,
    draftProgramAnswers,
    draftUiState,
    localHasProgramSpecific,
    generalGroupName,
    lifestyleGroupName,
    carePlanGroupName,
    setters,
  });

  const outcomeMaps = populateOutcomeStates({
    res,
    programMap,
    selectedProgram,
    setSelectedOutcome,
    setSelectedSubReason,
    setDeclinedReason,
    setDeclinedNotes,
  });

  const certVal = resolveCertificationStatus(res, readOnly);

  buildLoadedPayloadDeltaAndSignature({
    genKey,
    lifestyleKey,
    carePlanKey,
    draftGeneralAnswers,
    draftLifestyleAnswers,
    draftCarePlanAnswers,
    draftProgramAnswers,
    draftUiState,
    localHasProgramSpecific,
    dedupedProgs,
    loadedClinicalNote,
    clinicalNotes: loadedClinicalNote || "",
    outcomeMaps,
    currentAssessmentType,
    res,
    referenceData,
    certVal,
    lastSavedSignatureRef,
  });

  if (loadedClinicalNote) {
    setClinicalNotes(loadedClinicalNote);
  }
  setCertification(certVal);

  return dedupedProgs;
};

const validateOutcomesStep = ({
  programsToValidate,
  getOutcomeForProgram,
  selectedSubReason,
  declinedReason,
  showAssessmentTypeSelect,
  currentAssessmentType,
  isStep0DirectSubmit,
  readOnly,
  certification,
}) => {
  if (!programsToValidate || programsToValidate.length === 0) return true;

  const hasAllOutcomes = programsToValidate.every((ap) => {
    const outcome = getOutcomeForProgram(ap);
    return Boolean(outcome);
  });

  const hasAllReasons = programsToValidate.every((ap) => {
    const outcome = getOutcomeForProgram(ap);
    const canonicalId = mapLocalProgramId(ap.id || ap.name);
    const subReason =
      selectedSubReason[canonicalId] ||
      selectedSubReason[ap.id] ||
      selectedSubReason[ap.name] ||
      selectedSubReason[ap.cleanId] ||
      "";
    const isDeclined =
      outcome === "Declined" ||
      String(outcome).toLowerCase().includes("decline");
    const isOther = String(subReason).toLowerCase() === "other";
    const reason =
      declinedReason[canonicalId] ||
      declinedReason[ap.id] ||
      declinedReason[ap.name] ||
      declinedReason[ap.cleanId] ||
      "";
    return !isDeclined || !isOther || Boolean(reason?.trim());
  });

  if (!hasAllOutcomes || !hasAllReasons) return false;

  if (showAssessmentTypeSelect && !currentAssessmentType) {
    return false;
  }

  if (isStep0DirectSubmit) {
    return readOnly || Boolean(certification);
  }

  return true;
};

const buildInitialProgramsForProceed = ({
  hasEnrolledProgram,
  enrolledProgramList,
  referenceData,
  selectedProgram,
  currentAssessmentType,
}) => {
  if (hasEnrolledProgram && enrolledProgramList.length > 0) {
    return enrolledProgramList.map((p) => {
      const isFormless = checkIsFormless(p, referenceData);
      const selectedType = p.formType || p.assessmentType;
      let pType = "N/A";
      if (!isFormless && selectedType && String(selectedType).trim() !== "") {
        pType = String(selectedType).toUpperCase();
      }
      return {
        ...p,
        formType: pType,
      };
    });
  }
  return buildInitialPrograms(
    selectedProgram,
    currentAssessmentType,
    referenceData,
  );
};

const applyFetchedSchemasHelper = async ({
  resId,
  initialPrograms,
  memberClinicalPrograms,
  clinicalNotePrograms,
  selectedProgram,
  setGeneralAssessmentSchema,
  setLifestyleAssessmentSchema,
  setCarePlanAssessmentSchema,
  setProgramAssessmentSchema,
}) => {
  const fetchedSchemas = await fetchAssessmentSchemas({
    assessmentId: resId,
    activePrograms: initialPrograms,
    memberClinicalPrograms,
    clinicalNotePrograms,
    selectedProgram,
  });

  if (fetchedSchemas?.generalSchema) {
    setGeneralAssessmentSchema?.(
      (prev) => prev || fetchedSchemas.generalSchema,
    );
  }
  if (fetchedSchemas?.lifestyleSchema) {
    setLifestyleAssessmentSchema?.(
      (prev) => prev || fetchedSchemas.lifestyleSchema,
    );
  }
  if (fetchedSchemas?.carePlanSchema) {
    setCarePlanAssessmentSchema?.(
      (prev) => prev || fetchedSchemas.carePlanSchema,
    );
  }
  if (fetchedSchemas?.programSchemasMap) {
    setProgramAssessmentSchema?.((prev) => ({
      ...fetchedSchemas.programSchemasMap,
      ...prev,
    }));
  }
};

const isAllProgramsDischarge = (enrolledActivePrograms, referenceData) =>
  enrolledActivePrograms.length > 0 &&
  enrolledActivePrograms.every(
    (p) =>
      String(p.formType || p.assessmentType).toUpperCase() === "DISCHARGE" ||
      isProgramCompletionOutcome(p.outcome || p.programOutcome, referenceData),
  );

const initializeSessionOnProceed = async ({
  memberData,
  initialPrograms,
  memberClinicalPrograms,
  clinicalNotePrograms,
  selectedProgram,
  setters,
}) => {
  const payload = {
    memberId: Number.parseInt(memberData.memberId || memberData.id, 10),
    encounterId: null,
    programs: initialPrograms,
  };

  const res = await initializeAssessmentApi(payload);
  if (res?.id) {
    applyInitializedSessionSchemas(res, setters);
    await applyFetchedSchemasHelper({
      resId: res.id,
      initialPrograms,
      memberClinicalPrograms,
      clinicalNotePrograms,
      selectedProgram,
      ...setters,
    });
  }
};

const extractErrorMessage = (error_) => {
  if (!error_) return "Failed to auto-save assessment draft. Please try again.";
  if (typeof error_ === "string") return error_;
  const data = error_?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message && typeof data.message === "string") return data.message;
  if (data?.detail && typeof data.detail === "string") return data.detail;
  if (data?.error && typeof data.error === "string") return data.error;
  if (error_?.message && typeof error_?.message === "string")
    return error_.message;
  return "Failed to auto-save assessment draft. Please try again.";
};

const checkTypeConflict = (err) => {
  const errorMsg = extractErrorMessage(err);
  const match =
    /Assessment forms require overall type\s+([A-Z_]+)/i.exec(
      String(errorMsg),
    ) || /changed assessment type to\s+([A-Z_]+)/i.exec(String(errorMsg));
  const isTypeConflict = Boolean(match);
  return { isTypeConflict, match, errorMsg };
};

const executeProceedFromStep0 = async ({
  getEnrolledProgramList,
  hasEnrolledProgram,
  referenceData,
  selectedProgram,
  currentAssessmentType,
  setActivePrograms,
  generalAssessmentId,
  clinicalAssessmentId,
  memberData,
  memberClinicalPrograms,
  clinicalNotePrograms,
  setters,
  handleSaveDraft,
  setCurrentAssessmentType,
  loadTemplatesForVisit,
  setActiveStep,
}) => {
  const { enrolledProgramList, enrolledActivePrograms } =
    getEnrolledProgramList();

  const initialPrograms = buildInitialProgramsForProceed({
    hasEnrolledProgram,
    enrolledProgramList,
    referenceData,
    selectedProgram,
    currentAssessmentType,
  });

  if (enrolledActivePrograms.length > 0) {
    setActivePrograms?.((prev) =>
      upsertEnrolledPrograms(prev, enrolledActivePrograms),
    );
  }

  const effectiveAssessmentId = resolveTargetAssessmentId(
    generalAssessmentId,
    clinicalAssessmentId,
  );

  if (!effectiveAssessmentId) {
    await initializeSessionOnProceed({
      memberData,
      initialPrograms,
      memberClinicalPrograms,
      clinicalNotePrograms,
      selectedProgram,
      setters,
    });
  }

  // Call autosave API on Proceed in Eligible Programs section
  let autoSaveRes = null;
  if (handleSaveDraft) {
    autoSaveRes = await handleSaveDraft({
      silent: false,
      throwOnError: true,
      updatedActivePrograms:
        enrolledActivePrograms.length > 0 ? enrolledActivePrograms : undefined,
    });
  }

  const isAllDischarge = isAllProgramsDischarge(
    enrolledActivePrograms,
    referenceData,
  );

  const isCompleteDischarge =
    autoSaveRes?.cgPlusDischarge === true || isAllDischarge;

  if (isCompleteDischarge) {
    setCurrentAssessmentType?.("DISCHARGE");
  }

  if (loadTemplatesForVisit) {
    await loadTemplatesForVisit(
      enrolledActivePrograms,
      isCompleteDischarge ? "DISCHARGE" : currentAssessmentType,
    );
  }

  setActiveStep(1);
};

const handleProceedFromStep0Helper = async (props) => {
  const {
    isStep0DirectSubmit,
    readOnly,
    handleFinalSubmit,
    setActiveStep,
    setSectionError,
    setTypeConflictModal,
    newlyAddedProgramId,
    getEnrolledProgramList,
    ...rest
  } = props;

  if (isStep0DirectSubmit) {
    if (readOnly) return;
    await handleFinalSubmit();
    return;
  }

  if (readOnly) {
    setActiveStep(1);
    return;
  }

  try {
    await executeProceedFromStep0({
      ...rest,
      getEnrolledProgramList,
      setActiveStep,
    });
  } catch (error_) {
    console.error("Error creating assessment session on Proceed:", error_);
    const { isTypeConflict, match, errorMsg } = checkTypeConflict(error_);

    if (isTypeConflict && match) {
      setSectionError?.(null);
      const { enrolledActivePrograms } = getEnrolledProgramList();
      setTypeConflictModal?.({
        open: true,
        requiredType: match[1].toUpperCase(),
        enrolledActivePrograms,
        sourceStep: 0,
        addedProgramId: newlyAddedProgramId,
      });
      return;
    }

    setSectionError?.(errorMsg);
  }
};

const handleProgramSubTabNavigation = (
  programSpecificRef,
  activeStep,
  steps,
  setActiveStep,
) => {
  if (!programSpecificRef.current?.navigateToNextProgram) return false;
  const advancedSubTab = programSpecificRef.current.navigateToNextProgram();
  if (advancedSubTab) {
    return true;
  }
  if (activeStep < steps.length - 1) {
    setActiveStep(activeStep + 1);
    return true;
  }
  return false;
};

const isProgramStepKey = (hasProgramSpecific, activeStep, currentStepKey) =>
  (hasProgramSpecific && activeStep === 2) ||
  currentStepKey === "Program Specific" ||
  currentStepKey === "Program-Specific Assessment";

const autosaveOnNext = async (
  handleSaveDraft,
  readOnly,
  setSectionError,
  setTypeConflictModal,
  activeStep,
  newlyAddedProgramId = null,
) => {
  if (!handleSaveDraft || readOnly) return { success: true };
  try {
    const draftRes = await handleSaveDraft({
      silent: false,
      throwOnError: true,
    });
    return {
      success: true,
      version: draftRes?.version,
    };
  } catch (err) {
    console.warn("Autosave on Next failed:", err);
    const { isTypeConflict, match, errorMsg } = checkTypeConflict(err);

    if (isTypeConflict && match && setTypeConflictModal) {
      setSectionError?.(null);
      setTypeConflictModal({
        open: true,
        requiredType: match[1].toUpperCase(),
        sourceStep: activeStep,
        addedProgramId: newlyAddedProgramId,
      });
      return {
        success: false,
        error: errorMsg,
        isTypeConflict: true,
      };
    }

    setSectionError?.(errorMsg);
    return {
      success: false,
      error: errorMsg,
      isTypeConflict: false,
    };
  }
};

const handleAdvanceRegularStepHelper = async ({
  activeStep,
  steps,
  hasProgramSpecific,
  programSpecificRef,
  displayActivePrograms,
  setActiveProgramTab,
  setActiveStep,
  readOnly,
  handleFinalSubmit,
  handleSaveDraft,
  setSectionError,
  setTypeConflictModal,
  newlyAddedProgramId = null,
}) => {
  const currentStepKey = steps[activeStep];
  const isProgramStep = isProgramStepKey(
    hasProgramSpecific,
    activeStep,
    currentStepKey,
  );

  // Autosave templates when clicking Next in any section
  const autosaveResult = await autosaveOnNext(
    handleSaveDraft,
    readOnly,
    setSectionError,
    setTypeConflictModal,
    activeStep,
    newlyAddedProgramId,
  );
  if (!autosaveResult.success) {
    return;
  }
  const latestAutosaveVersion = autosaveResult.version;

  if (
    isProgramStep &&
    handleProgramSubTabNavigation(
      programSpecificRef,
      activeStep,
      steps,
      setActiveStep,
    )
  ) {
    return;
  }

  if (activeStep < steps.length - 1) {
    if (
      hasProgramSpecific &&
      activeStep === 1 &&
      displayActivePrograms?.[0]?.id
    ) {
      setActiveProgramTab(displayActivePrograms[0].id);
    }
    setActiveStep(activeStep + 1);
  } else {
    if (readOnly) return;
    await handleFinalSubmit(latestAutosaveVersion);
  }
};

const ensureSessionInitializedHelper = async ({
  currentSessionId,
  readOnly,
  memberData,
  hasEnrolledProgram,
  getEnrolledProgramList,
  referenceData,
  selectedProgram,
  currentAssessmentType,
  setActivePrograms,
  setCurrentAssessmentType,
  setLoading,
  setGeneralAssessmentId,
  setLifestyleAssessmentId,
  setCarePlanAssessmentId,
  setGeneralAssessmentVersion,
  getOutreachProgramsList,
}) => {
  if (currentSessionId || readOnly) return currentSessionId;
  try {
    setLoading(true);
    const { enrolledProgramList, enrolledActivePrograms } =
      getEnrolledProgramList();
    let initialPrograms = [];
    if (hasEnrolledProgram && enrolledProgramList.length > 0) {
      initialPrograms = enrolledProgramList.map((p) => {
        const isFormless = checkIsFormless(p, referenceData);
        const selectedType = p.formType || p.assessmentType;
        let pType = "N/A";
        if (!isFormless && selectedType && String(selectedType).trim() !== "") {
          pType = String(selectedType).toUpperCase();
        }
        return {
          ...p,
          formType: pType,
        };
      });
    } else if (typeof getOutreachProgramsList === "function") {
      initialPrograms = getOutreachProgramsList().map((p) => {
        const isFormless = checkIsFormless(p, referenceData);
        const selectedType = p.formType || p.assessmentType;
        let pType = "N/A";
        if (!isFormless && selectedType && String(selectedType).trim() !== "") {
          pType = String(selectedType).toUpperCase();
        }
        const item = {
          programId: p.cleanId || cleanProgramId(p.id) || p.id,
          formType: pType,
        };
        if (p.manuallyAdded && !isFormless) {
          item.manuallyAdded = true;
        }
        return item;
      });
    } else {
      initialPrograms = buildInitialProgramsForProceed({
        hasEnrolledProgram,
        enrolledProgramList,
        referenceData,
        selectedProgram,
        currentAssessmentType,
      });
    }

    if (hasEnrolledProgram && enrolledActivePrograms.length > 0) {
      setActivePrograms((prev) =>
        upsertEnrolledPrograms(prev, enrolledActivePrograms),
      );
      if (
        enrolledActivePrograms.every(
          (p) =>
            String(p.formType || p.assessmentType).toUpperCase() ===
            "DISCHARGE",
        )
      ) {
        setCurrentAssessmentType("DISCHARGE");
      }
    }

    const payload = {
      memberId: Number.parseInt(memberData.memberId || memberData.id, 10),
      encounterId: null,
      programs: initialPrograms,
    };
    const res = await initializeAssessmentApi(payload);
    if (res?.id) {
      setGeneralAssessmentId(res.id);
      setLifestyleAssessmentId(res.id);
      setCarePlanAssessmentId(res.id);
      setGeneralAssessmentVersion(res.version || 0);
      return res.id;
    }
  } catch (error_) {
    console.error("Failed to create session for draft:", error_);
    return null;
  } finally {
    setLoading(false);
  }
  return null;
};

const ensureStep0SubmitSessionInitializedHelper = async ({
  targetAssessmentId,
  readOnly,
  memberData,
  hasEnrolledProgram,
  getEnrolledProgramList,
  referenceData,
  getOutreachProgramsList,
  setActivePrograms,
  setLoading,
  setGeneralAssessmentId,
  setGeneralAssessmentVersion,
}) => {
  if (targetAssessmentId || readOnly) return targetAssessmentId;
  try {
    setLoading(true);
    const { enrolledProgramList, enrolledActivePrograms } =
      getEnrolledProgramList();
    let initialPrograms = [];
    if (hasEnrolledProgram && enrolledProgramList.length > 0) {
      initialPrograms = enrolledProgramList.map((p) => {
        const isFormless = checkIsFormless(p, referenceData);
        const selectedType = p.formType || p.assessmentType;
        let pType = "N/A";
        if (!isFormless && selectedType && String(selectedType).trim() !== "") {
          pType = String(selectedType).toUpperCase();
        }
        return {
          ...p,
          formType: pType,
        };
      });
    } else {
      initialPrograms = getOutreachProgramsList().map((p) => {
        const isFormless = checkIsFormless(p, referenceData);
        const selectedType = p.formType || p.assessmentType;
        let pType = "N/A";
        if (!isFormless && selectedType && String(selectedType).trim() !== "") {
          pType = String(selectedType).toUpperCase();
        }
        const item = {
          programId: p.cleanId || cleanProgramId(p.id) || p.id,
          formType: pType,
        };
        if (p.manuallyAdded && !isFormless) {
          item.manuallyAdded = true;
        }
        return item;
      });
    }

    if (hasEnrolledProgram && enrolledActivePrograms.length > 0) {
      setActivePrograms((prev) =>
        upsertEnrolledPrograms(prev, enrolledActivePrograms),
      );
    }

    const payload = {
      memberId: Number.parseInt(memberData.memberId || memberData.id, 10),
      encounterId: null,
      programs: initialPrograms,
    };
    const initRes = await initializeAssessmentApi(payload);
    if (initRes?.id) {
      setGeneralAssessmentId(initRes.id);
      if (initRes.version !== undefined) {
        setGeneralAssessmentVersion(initRes.version);
      }
      return { id: initRes.id, version: initRes.version };
    }
  } catch (error_) {
    console.error("Failed to create session for Step 0 submission:", error_);
    return null;
  } finally {
    setLoading(false);
  }
  return null;
};

const resolveConflictPrograms = ({
  displayActivePrograms,
  activePrograms,
  programsPayload,
  getOutreachProgramsList,
}) => {
  if (
    Array.isArray(displayActivePrograms) &&
    displayActivePrograms.length > 0
  ) {
    return displayActivePrograms;
  }
  if (Array.isArray(activePrograms) && activePrograms.length > 0) {
    return activePrograms;
  }
  if (Array.isArray(programsPayload) && programsPayload.length > 0) {
    return programsPayload;
  }
  if (typeof getOutreachProgramsList === "function") {
    const outreach = getOutreachProgramsList();
    if (Array.isArray(outreach) && outreach.length > 0) {
      return outreach;
    }
  }
  return [];
};

const buildAutosaveDeclinedPayload = ({
  targetAssessmentVersion,
  generalAssessmentVersion,
  certification,
  elapsedTimeRef,
  generalPayloadKey,
  generalFormPayload,
  lifestylePayloadKey,
  lifestyleFormPayload,
  carePlanPayloadKey,
  carePlanFormPayload,
  activeProgramTab,
  displayActivePrograms,
  selectedOutcome,
  selectedSubReason,
  declinedReason,
  declinedNotes,
  clinicalNotes,
  programsPayload,
}) => ({
  version: targetAssessmentVersion ?? generalAssessmentVersion ?? 0,
  signatureConfirmed: certification,
  elapsedTime: elapsedTimeRef?.current || 0,
  payloadDelta: {
    [generalPayloadKey || "OTHER_GENERAL"]: generalFormPayload || {},
    [lifestylePayloadKey || "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT"]:
      lifestyleFormPayload || {},
    [carePlanPayloadKey || "CARE_PLAN_COORDINATION_COMPLETION"]:
      carePlanFormPayload || {},
    _uiState: {
      activeFormName: "Eligible Programs",
      activeStepIndex: 0,
      visitedForms: ["Eligible Programs"],
      activeProgram: activeProgramTab || displayActivePrograms[0]?.id || "",
    },
    _outcomes: {
      selectedOutcome,
      selectedSubReason,
      declinedReason,
      declinedNotes,
      clinicalNotes,
    },
  },
  programs: programsPayload,
  clinicalNote: clinicalNotes,
});

const handleDeclinedAutosaveConflict = ({
  error_,
  setTypeConflictModal,
  setSectionError,
  displayActivePrograms,
  programsPayload,
}) => {
  const { isTypeConflict, match, errorMsg } = checkTypeConflict(error_);
  if (isTypeConflict && match && setTypeConflictModal) {
    setSectionError?.(null);
    const conflictPrograms = resolveConflictPrograms({
      displayActivePrograms,
      programsPayload,
    });
    setTypeConflictModal({
      open: true,
      requiredType: match[1].toUpperCase(),
      sourceStep: 0,
      enrolledActivePrograms: conflictPrograms,
    });
    return { success: false, isTypeConflict: true, error: errorMsg };
  }
  setSectionError?.(errorMsg);
  return { success: false, isTypeConflict: false, error: errorMsg };
};

const autosaveDeclinedAssessmentBeforeSubmitHelper = async ({
  targetAssessmentId,
  targetAssessmentVersion,
  generalAssessmentVersion,
  certification,
  elapsedTimeRef,
  generalPayloadKey,
  lifestylePayloadKey,
  carePlanPayloadKey,
  generalFormPayload,
  lifestyleFormPayload,
  carePlanFormPayload,
  activeProgramTab,
  displayActivePrograms,
  selectedOutcome,
  selectedSubReason,
  declinedReason,
  declinedNotes,
  clinicalNotes,
  programsPayload,
  setGeneralAssessmentVersion,
  setTypeConflictModal,
  setSectionError,
}) => {
  const autosavePayload = buildAutosaveDeclinedPayload({
    targetAssessmentVersion,
    generalAssessmentVersion,
    certification,
    elapsedTimeRef,
    generalPayloadKey,
    generalFormPayload,
    lifestylePayloadKey,
    lifestyleFormPayload,
    carePlanPayloadKey,
    carePlanFormPayload,
    activeProgramTab,
    displayActivePrograms,
    selectedOutcome,
    selectedSubReason,
    declinedReason,
    declinedNotes,
    clinicalNotes,
    programsPayload,
  });

  try {
    const autoSaveRes = await autosaveAssessmentApi(
      targetAssessmentId,
      autosavePayload,
    );
    if (autoSaveRes?.version !== undefined && autoSaveRes?.version !== null) {
      setGeneralAssessmentVersion(autoSaveRes.version);
      return { success: true, version: autoSaveRes.version };
    }
    return { success: true, version: targetAssessmentVersion };
  } catch (error_) {
    console.error("Autosave before submit failed:", error_);
    return handleDeclinedAutosaveConflict({
      error_,
      setTypeConflictModal,
      setSectionError,
      displayActivePrograms,
      programsPayload,
    });
  }
};

const buildSingleProgramPayloadItem = (
  ap,
  {
    availableProgramGroups,
    outcomesMap,
    referenceData,
    selectedSubReason,
    declinedReason,
    declinedNotes,
    activeList,
    addedList,
    selectedProgram,
    memberClinicalPrograms,
    getOutcomeForProgram,
  },
) => {
  const rawProgId =
    ap.cleanId || ap.programId || ap.id || ap.programCode || ap.name;
  const progName = ap.name || ap.programName || rawProgId;
  const canonicalId = getCanonicalProgramId(
    rawProgId,
    progName,
    availableProgramGroups,
  );
  const mappedNameKey = mapLocalProgramId(progName);
  const normKey = String(canonicalId || rawProgId)
    .toUpperCase()
    .trim();
  const cleanNorm = cleanProgramId(normKey);
  const normName = normalizeProgramId(progName);

  if (
    !normKey ||
    normKey.includes("UI_STATE") ||
    normKey.includes("UISTATE") ||
    normKey.includes("OUTCOME") ||
    normKey === "GENERAL" ||
    normKey === "OTHER" ||
    normKey === "LIFESTYLE" ||
    normKey === "CARE_PLAN" ||
    normKey === "METADATA" ||
    String(normKey).startsWith("_")
  ) {
    return null;
  }

  const isBiosimilars =
    canonicalId === "BIOSIMILARS" ||
    cleanNorm === "BIOSIMILARS" ||
    normKey === "BIOSIMILARS" ||
    String(rawProgId).toUpperCase().includes("BIOSIMILAR") ||
    String(ap.id || "")
      .toUpperCase()
      .includes("BIOSIMILAR") ||
    String(progName).toUpperCase().includes("BIOSIMILAR");

  const keysList = [
    canonicalId,
    cleanNorm,
    ap.cleanId,
    rawProgId,
    mappedNameKey,
    progName,
  ];

  let outcome = resolveMapValueForProgram(outcomesMap, keysList, isBiosimilars);
  if (!outcome && typeof getOutcomeForProgram === "function") {
    outcome = getOutcomeForProgram(ap, outcomesMap);
  }
  if (!outcome) outcome = "";

  const outcomeName = getOutcomeNameFromReferenceData(outcome, referenceData);
  const outcomeStr = outcomeName || "";

  const isDeclined = isDeclinedOutcome(outcome, referenceData);
  const isUnableToReach = isUnableToReachOutcome(outcome, referenceData);
  const hasReasonOrNotes = isDeclined || isUnableToReach;

  const subReasonVal = resolveMapValueForProgram(
    selectedSubReason,
    keysList,
    isBiosimilars,
  );
  const outcomeReasonVal = hasReasonOrNotes
    ? resolveMapValueForProgram(declinedReason, keysList, isBiosimilars)
    : null;
  const declinedNotesVal = isDeclined
    ? resolveMapValueForProgram(declinedNotes, keysList, isBiosimilars)
    : null;

  const isFormlessProg = Boolean(
    isBiosimilars || ap.isFormless || checkIsFormless(ap, referenceData),
  );
  const matchedActive =
    findProgramMatch(activeList, cleanNorm, progName) ||
    findProgramMatch(addedList, cleanNorm, progName) ||
    findProgramMatch(selectedProgram, cleanNorm, progName) ||
    findProgramMatch(memberClinicalPrograms, cleanNorm, progName);

  const isManual = Boolean(
    ap.manuallyAdded ||
    ap.isManual ||
    matchedActive?.manuallyAdded ||
    matchedActive?.isManual ||
    addedList?.some(
      (ad) => cleanProgramId(ad.id || ad.programId) === cleanNorm,
    ),
  );

  const isCompletion = isProgramCompletionOutcome(outcome, referenceData);
  const selectedType =
    ap.formType ||
    ap.assessmentType ||
    matchedActive?.formType ||
    matchedActive?.assessmentType;

  const resolvedFormType = resolveProgramDraftFormType(
    isCompletion,
    isFormlessProg,
    selectedType,
  );

  const progPayloadItem = {
    programId: isBiosimilars
      ? "BIOSIMILARS"
      : canonicalId || cleanNorm || ap.cleanId || rawProgId,
    outcomeName: outcomeStr,
    outcome: outcomeStr,
    formType: resolvedFormType,
  };

  if (isManual && !isFormlessProg) {
    progPayloadItem.manuallyAdded = true;
  }
  if (subReasonVal) {
    progPayloadItem.subReason = subReasonVal;
  }
  if (outcomeReasonVal) {
    progPayloadItem.reason = outcomeReasonVal;
  }
  if (isDeclined && declinedNotesVal) {
    progPayloadItem.declinedNote = declinedNotesVal;
  }

  return {
    normKey,
    cleanNorm,
    normName,
    item: progPayloadItem,
  };
};

const buildProgramsPayloadList = (combinedPrograms, context) => {
  const programsPayload = [];
  const seenMappedIds = new Set();

  (combinedPrograms || []).forEach((ap) => {
    const res = buildSingleProgramPayloadItem(ap, context);
    if (!res) return;
    const { normKey, cleanNorm, normName, item } = res;
    if (
      seenMappedIds.has(normKey) ||
      seenMappedIds.has(cleanNorm) ||
      (normName && seenMappedIds.has(normName))
    ) {
      return;
    }
    seenMappedIds.add(normKey);
    seenMappedIds.add(cleanNorm);
    if (normName) seenMappedIds.add(normName);

    programsPayload.push(item);
  });

  return programsPayload;
};

const buildProgramSpecificPayloadDelta = (
  activeList,
  outcomesMap,
  referenceData,
  getOutcomeForProgram,
  programFormPayload,
) => {
  const delta = {};
  (activeList || []).forEach((ap) => {
    const outcome =
      outcomesMap[ap.id] ||
      outcomesMap[cleanProgramId(ap.id)] ||
      (typeof getOutcomeForProgram === "function"
        ? getOutcomeForProgram(ap, outcomesMap)
        : "");
    if (
      !isEnrolledOutcome(outcome, referenceData) &&
      !isProgramCompletionOutcome(outcome, referenceData)
    ) {
      return;
    }
    const cleanId = cleanProgramId(ap.id);
    const upperId = String(cleanId).toUpperCase();
    if (
      !cleanId ||
      upperId.includes("UI_STATE") ||
      upperId.includes("UISTATE") ||
      upperId.includes("OUTCOME") ||
      upperId.includes("GENERAL") ||
      upperId.includes("OTHER") ||
      upperId.includes("LIFESTYLE") ||
      upperId.includes("CARE_PLAN") ||
      upperId.includes("METADATA") ||
      cleanId.startsWith("_")
    ) {
      return;
    }
    const mapKey = `${cleanId}_PROGRAM_SPECIFIC`;
    const programAnswers =
      programFormPayload[cleanId] || programFormPayload[ap.id] || {};
    delta[mapKey] = programAnswers;
  });
  return delta;
};

const buildProgramAddObject = (
  programGroup,
  targetId,
  progName,
  { formType, manuallyAdded, isFormless, referenceData },
) => {
  const progIsFormless =
    isFormless || checkIsFormless(programGroup, referenceData);
  const chosenType =
    formType || programGroup.formType || programGroup.assessmentType;
  const isManual = manuallyAdded || Boolean(programGroup.manuallyAdded);

  return {
    ...programGroup,
    id: targetId,
    programId: targetId,
    name: progName,
    programDisplayName: programGroup.programDisplayName || progName,
    isFormless: progIsFormless,
    ...(chosenType && !progIsFormless
      ? { formType: chosenType, assessmentType: chosenType }
      : {}),
    ...(isManual && !progIsFormless ? { manuallyAdded: true } : {}),
  };
};

const unremoveProgramKeys = (
  removedProgramIds,
  { upperId, upperClean, upperNorm, canonicalId, upperName, upperNameNorm },
) => {
  const next = new Set(removedProgramIds);
  next.delete(upperId);
  next.delete(upperClean);
  if (upperNorm) next.delete(upperNorm);
  if (canonicalId) next.delete(String(canonicalId).toUpperCase().trim());
  if (upperName) next.delete(upperName);
  if (upperNameNorm) next.delete(upperNameNorm);
  return next;
};

const computeProgramOutcomeKeys = ({
  rawProgId,
  programGroup,
  targetId,
  upperId,
  cleanId,
  upperClean,
  canonicalId,
  mappedId,
  mappedName,
  progName,
  upperName,
}) =>
  [
    rawProgId,
    programGroup.id,
    programGroup.programId,
    targetId,
    upperId,
    cleanId,
    upperClean,
    canonicalId,
    canonicalId ? String(canonicalId).toUpperCase().trim() : null,
    mappedId,
    mappedName,
    progName,
    upperName,
  ].filter(Boolean);

const resolveTargetAssessmentId = (
  generalAssessmentId,
  clinicalAssessmentId,
) => {
  if (generalAssessmentId && generalAssessmentId !== "fallback-id") {
    return generalAssessmentId;
  }
  return clinicalAssessmentId || null;
};

const buildSubmitFinalPayload = ({
  generalPayloadKey,
  generalFormPayload,
  lifestylePayloadKey,
  lifestyleFormPayload,
  carePlanPayloadKey,
  carePlanFormPayload,
  hasProgramSpecific,
  isStep0EarlySubmit,
  activePrograms,
  selectedOutcome,
  referenceData,
  getOutcomeForProgram,
  programFormPayload,
}) => {
  const finalPayload = {
    [generalPayloadKey || "OTHER_GENERAL"]: generalFormPayload || {},
    [lifestylePayloadKey || "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT"]:
      lifestyleFormPayload || {},
    [carePlanPayloadKey || "CARE_PLAN_COORDINATION_COMPLETION"]:
      carePlanFormPayload || {},
  };

  if (hasProgramSpecific && !isStep0EarlySubmit) {
    const progDelta = buildProgramSpecificPayloadDelta(
      activePrograms,
      selectedOutcome,
      referenceData,
      getOutcomeForProgram,
      programFormPayload,
    );
    Object.assign(finalPayload, progDelta);
  }
  return finalPayload;
};

const resolveSubmitVersion = (
  targetAssessmentVersion,
  generalAssessmentVersion,
) => {
  if (targetAssessmentVersion !== undefined) {
    return targetAssessmentVersion;
  }
  return generalAssessmentVersion || 0;
};

const isPresent = (value) => value !== undefined && value !== null;

const applyInitResultToTarget = (
  initResult,
  targetId,
  targetVersion,
  latestVersionRef,
) => {
  if (!initResult?.id) return { targetId, targetVersion };
  let nextVersion = targetVersion;
  if (initResult.version !== undefined) {
    nextVersion = initResult.version;
    latestVersionRef.current = initResult.version;
  }
  return { targetId: initResult.id, targetVersion: nextVersion };
};

const hasDraftProgramWarning = (warnings, ap) => {
  const warningMessage =
    warnings?.[ap.id] ||
    warnings?.[String(ap.id).toUpperCase()] ||
    warnings?.[String(ap.id).replace("-", "_").toUpperCase()] ||
    warnings?.[mapLocalProgramId(ap.id)] ||
    warnings?.[mapLocalProgramId(ap.name)];
  return Boolean(warningMessage);
};

const resolveProgramFormType = (ap, defaultModalType) => {
  if (ap.formType !== undefined && ap.formType !== "") return ap.formType;
  if (ap.assessmentType !== undefined && ap.assessmentType !== "") {
    return ap.assessmentType;
  }
  return defaultModalType;
};

const NAVIGATION_PORTAL_SELECTORS = [
  ".MuiPopper-root",
  ".MuiPickersPopper-root",
  ".MuiDateCalendar-root",
  ".MuiPickersLayout-root",
  ".MuiPickersDay-root",
  ".MuiCalendarPicker-root",
  ".MuiClockPicker-root",
  ".MuiPickersCalendarHeader-root",
  ".MuiDayCalendar-root",
  ".MuiMonthCalendar-root",
  ".MuiYearCalendar-root",
  ".MuiDigitalClock-root",
  ".MuiMultiSectionDigitalClock-root",
  ".MuiPopover-root",
  ".MuiMenu-root",
  ".MuiDialog-root",
  ".MuiModal-root",
  ".MuiAutocomplete-popper",
  ".MuiTooltip-popper",
  ".react-datepicker",
  ".react-datepicker-popper",
  ".react-datepicker__portal",
  "[data-popper-placement]",
  "[data-mui-popper]",
  "[role='dialog']",
  "[role='tooltip']",
  "[role='listbox']",
  "[role='grid']",
  "[role='gridcell']",
  "[data-phi-toggle]",
  ".phi-toggle-container",
  ".phi-toggle",
  "[data-testid*='phi' i]",
].join(", ");

const isPortalOrPopperTarget = (target) =>
  Boolean(target.closest(NAVIGATION_PORTAL_SELECTORS));

const isPhiToggleTarget = (target) => {
  if (!target?.closest) return false;
  if (
    target.closest("[data-phi-toggle]") ||
    target.closest(".phi-toggle-container") ||
    target.closest(".phi-toggle") ||
    target.closest("[data-testid*='phi' i]") ||
    target.closest("[aria-label*='phi' i]")
  ) {
    return true;
  }
  const textContainer = target.closest("div, button, label, span, p");
  if (textContainer && /mask\s*phi/i.test(textContainer.textContent || "")) {
    return true;
  }
  return false;
};

const findInteractiveElement = (target) => {
  const interactiveEl = target.closest(
    "a, button, [role='button'], [role='menuitem'], [role='tab'], li",
  );
  if (interactiveEl) return interactiveEl;

  const clickable = target.closest(
    "[onclick], [style*='cursor: pointer'], [style*='cursor:pointer']",
  );
  if (clickable) return clickable;

  try {
    if (globalThis.getComputedStyle(target).cursor === "pointer") {
      return target;
    }
  } catch {
    // ignore
  }
  return null;
};

const normalizeProgramMatchString = (str) => {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .trim()
    .replaceAll(/['\s_-]+/g, "");
};

const isSchemaFieldVisible = (f, selectedProgram) => {
  if (!f) return false;
  if (f.specificPrograms && f.specificPrograms.length > 0) {
    if (!selectedProgram) return false;

    const activeProgramStrings = getActiveProgramStrings(selectedProgram);
    const progNames = activeProgramStrings
      .filter(Boolean)
      .map(normalizeProgramMatchString);

    const matched = f.specificPrograms.some((p) => {
      const normP = normalizeProgramMatchString(p);
      return checkProgramMatch(progNames, normP);
    });

    if (!matched) return false;
  }
  return true;
};

const hasSchemaSections = (schema) =>
  Boolean(schema?.sections) && Object.keys(schema.sections).length > 0;

const hasWarningForKeys = (warnings, keys) =>
  keys.some((key) => Boolean(warnings?.[key]));

const programHasWarning = (prog, warnings, availableProgramGroups) => {
  if (!prog) return false;
  const rawId = prog.cleanId || prog.programId || prog.id || prog;
  const rawName = prog.name || prog.groupName || "";
  const canonicalId = getCanonicalProgramId(
    rawId,
    rawName,
    availableProgramGroups,
  );
  const cleanId = cleanProgramId(canonicalId || rawId);
  const upperRawId = String(rawId).toUpperCase().trim();
  const upperName = String(rawName).toUpperCase().trim();
  const mappedId = mapLocalProgramId(rawId || rawName);
  const mappedName = mapLocalProgramId(rawName);

  const keys = [
    canonicalId,
    cleanId,
    mappedId,
    mappedName,
    upperRawId,
    rawId,
    rawName,
    upperName,
  ].filter(Boolean);

  return keys.some((k) => Boolean(warnings?.[k]));
};

const isProgramFormValid = (
  p,
  programAssessmentSchema,
  programFormPayload,
  isFieldVisible,
) => {
  const rawId = p.cleanId || p.programId || p.id || "";
  const cleanId = cleanProgramId(rawId);
  const pSchema =
    programAssessmentSchema?.[`${cleanId}_PROGRAM_SPECIFIC`] ||
    programAssessmentSchema?.[cleanId] ||
    programAssessmentSchema?.[rawId];
  if (!hasSchemaSections(pSchema)) {
    return false;
  }
  const pPayload =
    programFormPayload?.[cleanId] || programFormPayload?.[rawId] || {};
  return validateSchemaForm(pSchema, pPayload, isFieldVisible);
};

const buildTemplateQueryParams = (
  programsList,
  referenceData,
  visitTypeOverride,
  currentAssessmentType,
  cgPlusDischarge,
  getOutcomeForProgram,
) => {
  const vType = (
    visitTypeOverride ||
    currentAssessmentType ||
    "INITIAL"
  ).toUpperCase();

  const nonFormless = programsList.filter(
    (p) => !checkIsFormless(p, referenceData),
  );
  if (nonFormless.length === 0) {
    return { nonFormless: [], queryParams: [], effectiveCoreType: vType };
  }

  const hasManual = nonFormless.some((p) => Boolean(p.manuallyAdded));
  const queryParams = [];
  const seen = new Set();

  nonFormless.forEach((p) => {
    const rawId = p.programId || p.id || p.name;
    const pid = cleanProgramId(rawId) || String(rawId);
    const uPid = pid.toUpperCase();
    if (
      !uPid ||
      uPid === "OTHER" ||
      uPid === "GENERAL" ||
      uPid === "LIFESTYLE" ||
      uPid === "CARE_PLAN" ||
      uPid.includes("BIOSIMILAR")
    ) {
      return;
    }
    const progOutcome = getOutcomeForProgram(p);
    const isProgCompletion = isProgramCompletionOutcome(
      progOutcome,
      referenceData,
    );
    const pType = String(
      isProgCompletion ? "DISCHARGE" : p.formType || p.assessmentType || vType,
    ).toUpperCase();
    const paramStr = `${pid}:${pType}`;
    if (!seen.has(paramStr)) {
      seen.add(paramStr);
      queryParams.push(paramStr);
    }
  });

  const isAllDischarge =
    nonFormless.length > 0 &&
    nonFormless.every((p) => {
      const progOutcome = getOutcomeForProgram(p);
      return (
        String(p.formType || p.assessmentType).toUpperCase() === "DISCHARGE" ||
        isProgramCompletionOutcome(progOutcome, referenceData)
      );
    });

  const effectiveCoreType = (
    visitTypeOverride ||
    (cgPlusDischarge === true || (cgPlusDischarge !== false && isAllDischarge)
      ? "DISCHARGE"
      : currentAssessmentType) ||
    "INITIAL"
  ).toUpperCase();

  if (hasManual || !queryParams.some((q) => q.startsWith("OTHER:"))) {
    queryParams.push(`OTHER:${effectiveCoreType}`);
  }

  return { nonFormless, queryParams, effectiveCoreType };
};

const validateProgramStage = ({
  displayActivePrograms,
  referenceData,
  checkProgramHasWarning,
  programAssessmentSchema,
  programFormPayload,
  isFieldVisible,
}) => {
  const activeFormPrograms = (displayActivePrograms || []).filter(
    (p) => !checkIsFormless(p, referenceData),
  );
  if (activeFormPrograms.length === 0) return true;

  if (
    activeFormPrograms.length >= 2 ||
    (displayActivePrograms || []).length >= 2
  ) {
    return true;
  }

  const hasAnyProgWarning = activeFormPrograms.some((p) =>
    checkProgramHasWarning(p),
  );
  if (hasAnyProgWarning) return false;

  return activeFormPrograms.every((p) =>
    isProgramFormValid(
      p,
      programAssessmentSchema,
      programFormPayload,
      isFieldVisible,
    ),
  );
};

const validateCarePlanStage = ({
  hasGeneralWarning,
  hasLifestyleWarning,
  hasCarePlanWarning,
  displayActivePrograms,
  referenceData,
  checkProgramHasWarning,
  carePlanAssessmentSchema,
  carePlanFormPayload,
  isFieldVisible,
  readOnly,
  certification,
}) => {
  if (hasGeneralWarning || hasLifestyleWarning || hasCarePlanWarning) {
    return false;
  }
  const activeFormPrograms = (displayActivePrograms || []).filter(
    (p) => !checkIsFormless(p, referenceData),
  );
  if (activeFormPrograms.some((p) => checkProgramHasWarning(p))) {
    return false;
  }

  if (!hasSchemaSections(carePlanAssessmentSchema)) {
    return false;
  }

  const isCarePlanValid = validateSchemaForm(
    carePlanAssessmentSchema,
    carePlanFormPayload,
    isFieldVisible,
  );
  if (!isCarePlanValid) return false;

  return readOnly || Boolean(certification);
};

const mapProgramToRequiredType = (
  p,
  cleanAddedId,
  hasMultiplePrograms,
  reqType,
) => {
  const pId = cleanProgramId(p.programId || p.id || "").toUpperCase();
  const isAddedProgram =
    (cleanAddedId && pId === cleanAddedId) ||
    Boolean(p.manuallyAdded && hasMultiplePrograms);
  const isPreviouslyExisting = hasMultiplePrograms && !isAddedProgram;

  if (isPreviouslyExisting) {
    return p;
  }

  const pType = String(p.formType || p.assessmentType || "").toUpperCase();
  if (pType !== "DISCHARGE") {
    return {
      ...p,
      formType: reqType,
      assessmentType: reqType,
      initialFormType: reqType,
      defaultAssessmentType: reqType,
    };
  }
  return p;
};

const mapServerProgramDetails = (
  p,
  serverPrograms,
  cleanAddedId,
  hasMultiplePrograms,
  finalAssessmentType,
) => {
  const pId = String(p.programId || p.id || "").toUpperCase();
  const pClean = cleanProgramId(pId);
  const serverProg = serverPrograms?.find((sp) => {
    const spId = String(sp.programId || sp.id || "").toUpperCase();
    return spId === pId || (pClean && cleanProgramId(spId) === pClean);
  });
  const sType = String(
    serverProg?.formType || serverProg?.assessmentType || "",
  ).toUpperCase();

  const isAddedProgram =
    (cleanAddedId && pClean === cleanAddedId) ||
    Boolean(p.manuallyAdded && hasMultiplePrograms);
  const isPreviouslyExisting = hasMultiplePrograms && !isAddedProgram;

  const existingType = String(
    p.formType ||
      p.assessmentType ||
      p.defaultAssessmentType ||
      p.initialFormType ||
      "",
  ).toUpperCase();

  let progType;
  if (isPreviouslyExisting && existingType) {
    progType = sType || existingType;
  } else if (sType === "DISCHARGE" || existingType === "DISCHARGE") {
    progType = "DISCHARGE";
  } else if (sType) {
    progType = sType;
  } else {
    progType = finalAssessmentType;
  }

  return {
    ...p,
    ...serverProg,
    formType: progType,
    assessmentType: progType,
    initialFormType: isPreviouslyExisting
      ? p.initialFormType || existingType || progType
      : progType,
    defaultAssessmentType: isPreviouslyExisting
      ? p.defaultAssessmentType || existingType || progType
      : progType,
  };
};

const executeAutosaveWithStaleRetry = async (
  currentSessionId,
  payload,
  latestVersionRef,
  setVersionState,
) => {
  try {
    return await autosaveAssessmentApi(currentSessionId, payload);
  } catch (error_) {
    const errStatus = error_?.response?.status;
    const errMsg = String(
      error_?.response?.data?.message || error_?.message || "",
    );
    const isStale =
      errStatus === 409 &&
      (errMsg.toLowerCase().includes("stale") ||
        errMsg.toLowerCase().includes("expected v") ||
        errMsg.toLowerCase().includes("version"));

    if (isStale) {
      console.warn(
        "Autosave hit stale version conflict, fetching latest assessment version from API...",
      );
      const freshData = await fetchDraftPayloadApi(currentSessionId);
      if (freshData?.version !== undefined && freshData?.version !== null) {
        latestVersionRef.current = freshData.version;
        setVersionState?.(freshData.version);
        const retryPayload = {
          ...payload,
          version: freshData.version,
        };
        return await autosaveAssessmentApi(currentSessionId, retryPayload);
      }
    }
    throw error_;
  }
};

const buildTypeConflictPayloadDelta = ({
  parsedRes,
  reqType,
  activeStep,
  steps,
  visitedForms,
  activeProgramTab,
  displayActivePrograms,
  selectedOutcome,
  selectedSubReason,
  declinedReason,
  declinedNotes,
  clinicalNotes,
  generalPayloadKey,
  lifestylePayloadKey,
  carePlanPayloadKey,
  hasProgramSpecific,
  updatedActiveList,
  referenceData,
  getOutcomeForProgram,
}) => {
  const genKey =
    parsedRes?.generalKey ||
    (reqType === "INITIAL" ? "OTHER_GENERAL" : `OTHER_GENERAL_${reqType}`);
  const lifestyleKey =
    parsedRes?.lifestyleKey ||
    (reqType === "INITIAL"
      ? "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT"
      : `LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT_GENERAL_${reqType}`);
  const carePlanKey =
    parsedRes?.carePlanKey ||
    (reqType === "INITIAL"
      ? "CARE_PLAN_COORDINATION_COMPLETION"
      : `CARE_PLAN_COORDINATION_COMPLETION_GENERAL_${reqType}`);

  const activeFormName = getFormNameForStep(activeStep, steps);
  const currentVisited = visitedForms.includes(activeFormName)
    ? visitedForms
    : [...visitedForms, activeFormName];

  const genSchemaPayload =
    parsedRes?.generalSchemaPayload || parsedRes?.generalSchema || {};
  const lifestyleSchemaPayload =
    parsedRes?.lifestyleSchemaPayload || parsedRes?.lifestyleSchema || {};
  const carePlanSchemaPayload =
    parsedRes?.carePlanSchemaPayload || parsedRes?.carePlanSchema || {};

  const payloadDelta = {
    [genKey]: genSchemaPayload,
    [lifestyleKey]: lifestyleSchemaPayload,
    [carePlanKey]: carePlanSchemaPayload,
    _uiState: {
      activeFormName,
      activeStepIndex: activeStep,
      visitedForms: currentVisited,
      activeProgram: activeProgramTab || displayActivePrograms[0]?.id || "",
    },
    _outcomes: {
      selectedOutcome: selectedOutcome || {},
      selectedSubReason,
      declinedReason,
      declinedNotes,
      clinicalNotes,
    },
  };

  if (generalPayloadKey && generalPayloadKey !== genKey) {
    delete payloadDelta[generalPayloadKey];
  }
  if (lifestylePayloadKey && lifestylePayloadKey !== lifestyleKey) {
    delete payloadDelta[lifestylePayloadKey];
  }
  if (carePlanPayloadKey && carePlanPayloadKey !== carePlanKey) {
    delete payloadDelta[carePlanPayloadKey];
  }

  if (hasProgramSpecific) {
    const progDelta = buildProgramSpecificPayloadDelta(
      updatedActiveList,
      selectedOutcome || {},
      referenceData,
      getOutcomeForProgram,
      {},
    );
    Object.assign(payloadDelta, progDelta);
  }

  return { payloadDelta, genKey, lifestyleKey, carePlanKey };
};

const applyTypeConflictAutosaveResponse = ({
  res,
  reqType,
  cleanAddedId,
  hasMultiplePrograms,
  updatedActiveList,
  filteredProgramMap,
  selectedProgram,
  latestAssessmentVersionRef,
  setGeneralAssessmentVersion,
  setCgPlusDischarge,
  setCurrentAssessmentType,
  setModalAssessmentTypeOverride,
  setAssigneeRnName,
  setClinicalNotes,
  setCertification,
  setActivePrograms,
  setAddedPrograms,
  setSelectedOutcome,
  setSelectedSubReason,
  setDeclinedReason,
  setDeclinedNotes,
}) => {
  if (res?.version !== undefined && res?.version !== null) {
    latestAssessmentVersionRef.current = res.version;
    setGeneralAssessmentVersion(res.version);
  }
  applyCgPlusDischarge(
    res?.cgPlusDischarge,
    setCgPlusDischarge,
    setCurrentAssessmentType,
  );
  if (res?.assigneeRnName) {
    setAssigneeRnName(res.assigneeRnName);
  }
  if (res?.clinicalNote !== undefined && res?.clinicalNote !== null) {
    setClinicalNotes(res.clinicalNote);
  }
  if (
    res?.signatureConfirmed !== undefined &&
    res?.signatureConfirmed !== null
  ) {
    setCertification(Boolean(res.signatureConfirmed));
  }

  const finalAssessmentType = (
    res?.assessmentType ||
    res?.data?.assessmentType ||
    reqType
  ).toUpperCase();
  setCurrentAssessmentType(finalAssessmentType);
  setModalAssessmentTypeOverride(finalAssessmentType);

  const serverPrograms =
    (Array.isArray(res?.programs) ? res.programs : null) ||
    (Array.isArray(res?.data?.programs) ? res.data.programs : null) ||
    (Array.isArray(res?.programOutcomes) ? res.programOutcomes : null) ||
    (Array.isArray(res?.data?.programOutcomes)
      ? res.data.programOutcomes
      : null);

  setActivePrograms((prev) => {
    const baseList = prev && prev.length > 0 ? prev : updatedActiveList;
    if (!baseList || baseList.length === 0) return prev;
    return baseList.map((p) =>
      mapServerProgramDetails(
        p,
        serverPrograms,
        cleanAddedId,
        hasMultiplePrograms,
        finalAssessmentType,
      ),
    );
  });

  setAddedPrograms((prev) =>
    (prev || []).map((p) =>
      mapServerProgramDetails(
        p,
        serverPrograms,
        cleanAddedId,
        hasMultiplePrograms,
        finalAssessmentType,
      ),
    ),
  );

  if (
    (serverPrograms && serverPrograms.length > 0) ||
    res?.payload?._outcomes ||
    res?.payload?.outcomes
  ) {
    populateOutcomeStates({
      res,
      programMap: filteredProgramMap,
      selectedProgram,
      setSelectedOutcome,
      setSelectedSubReason,
      setDeclinedReason,
      setDeclinedNotes,
    });
  }
};

const buildAutosavePayloadDelta = ({
  generalPayloadKey,
  generalFormPayload,
  lifestylePayloadKey,
  lifestyleFormPayload,
  carePlanPayloadKey,
  carePlanFormPayload,
  activeStep,
  steps,
  visitedForms,
  activeProgramTab,
  displayActivePrograms,
  outcomesMap,
  selectedSubReason,
  declinedReason,
  declinedNotes,
  clinicalNotes,
  hasProgramSpecific,
  activeList,
  referenceData,
  getOutcomeForProgram,
  programFormPayload,
}) => {
  const activeFormName = getFormNameForStep(activeStep, steps);
  const currentVisited = visitedForms.includes(activeFormName)
    ? visitedForms
    : [...visitedForms, activeFormName];

  const payloadDelta = {
    [generalPayloadKey]: generalFormPayload,
    [lifestylePayloadKey]: lifestyleFormPayload,
    [carePlanPayloadKey]: carePlanFormPayload,
    _uiState: {
      activeFormName,
      activeStepIndex: activeStep,
      visitedForms: currentVisited,
      activeProgram: activeProgramTab || displayActivePrograms[0]?.id || "",
    },
    _outcomes: {
      selectedOutcome: outcomesMap,
      selectedSubReason,
      declinedReason,
      declinedNotes,
      clinicalNotes,
    },
  };

  if (hasProgramSpecific) {
    const progDelta = buildProgramSpecificPayloadDelta(
      activeList,
      outcomesMap,
      referenceData,
      getOutcomeForProgram,
      programFormPayload,
    );
    Object.assign(payloadDelta, progDelta);
  }

  return payloadDelta;
};

const applyAutosaveResponse = ({
  res,
  latestAssessmentVersionRef,
  setGeneralAssessmentVersion,
  setCgPlusDischarge,
  setCurrentAssessmentType,
  setAssigneeRnName,
  setIsDraft,
  setSectionError,
}) => {
  if (res?.version !== undefined && res?.version !== null) {
    latestAssessmentVersionRef.current = res.version;
    setGeneralAssessmentVersion(res.version);
  }
  applyCgPlusDischarge(
    res?.cgPlusDischarge,
    setCgPlusDischarge,
    setCurrentAssessmentType,
  );
  if (res?.assigneeRnName) {
    setAssigneeRnName(res.assigneeRnName);
  }
  setIsDraft(true);
  setSectionError(null);
};

const triggerTypeConflictModal = ({
  setTypeConflictModal,
  match,
  activeStep,
  newlyAddedProgramId,
  conflictPrograms,
}) => {
  setTypeConflictModal?.({
    open: true,
    requiredType: match[1].toUpperCase(),
    sourceStep: activeStep,
    addedProgramId: newlyAddedProgramId,
    enrolledActivePrograms: conflictPrograms,
  });
};

const handleAutosaveError = ({
  error_,
  throwOnError,
  silent,
  activeStep,
  newlyAddedProgramId,
  setTypeConflictModal,
  setSectionError,
  displayActivePrograms,
  activePrograms,
  programsPayload,
  getOutreachProgramsList,
}) => {
  const { isTypeConflict, match, errorMsg } = checkTypeConflict(error_);

  if (isTypeConflict && match) {
    setSectionError(null);
    if (!throwOnError) {
      const conflictPrograms = resolveConflictPrograms({
        displayActivePrograms,
        activePrograms,
        programsPayload,
        getOutreachProgramsList,
      });
      triggerTypeConflictModal({
        setTypeConflictModal,
        match,
        activeStep,
        newlyAddedProgramId,
        conflictPrograms,
      });
    }
  } else if (!silent) {
    setSectionError(errorMsg);
    console.error("Failed to auto-save assessment draft:", error_);
  }
  if (throwOnError) {
    throw error_;
  }
  return {
    error: errorMsg,
    success: false,
    isTypeConflict: Boolean(isTypeConflict),
    requiredType: match ? match[1].toUpperCase() : null,
  };
};

const formatTypeLabel = (typeStr) => {
  if (!typeStr) return "";
  return String(typeStr)
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("-");
};

const parseAssessmentType = (rawAssessmentType) => {
  if (!rawAssessmentType) return "";
  const upper = String(rawAssessmentType).toUpperCase().trim();
  if (upper === "INITIAL") return "INITIAL";
  if (upper === "FOLLOW_UP") return "FOLLOW_UP";
  if (upper === "DISCHARGE") return "DISCHARGE";
  if (upper === "N/A") return "N/A";
  return upper;
};

const resolveOutcomeForProgram = (
  prog,
  selectedOutcome,
  availableProgramGroups,
) => {
  if (!prog) return "";
  const rawId = prog.cleanId || prog.programId || prog.id || prog;
  const rawName = prog.name || prog.groupName || "";
  const canonicalId = getCanonicalProgramId(
    rawId,
    rawName,
    availableProgramGroups,
  );
  const cleanId = cleanProgramId(canonicalId || rawId);
  const upperRawId = String(rawId).toUpperCase().trim();
  const upperName = String(rawName).toUpperCase().trim();
  const normName = normalizeProgramId(rawName);
  const mappedId = mapLocalProgramId(rawId || rawName);
  const mappedName = mapLocalProgramId(rawName);
  const isBiosimilars =
    cleanId === "BIOSIMILARS" ||
    upperRawId.includes("BIOSIMILAR") ||
    upperName.includes("BIOSIMILAR");

  if (isBiosimilars) {
    if (selectedOutcome.BIOSIMILARS) return selectedOutcome.BIOSIMILARS;
    if (selectedOutcome.Biosimilars) return selectedOutcome.Biosimilars;
    if (selectedOutcome.Biosimilar) return selectedOutcome.Biosimilar;
  }

  const keys = [
    canonicalId,
    cleanId,
    mappedId,
    mappedName,
    upperRawId,
    rawId,
    rawName,
    upperName,
    normName,
  ].filter(Boolean);

  for (const k of keys) {
    if (selectedOutcome[k]) {
      return selectedOutcome[k];
    }
  }
  return "";
};

const loadProgramSchemaForProg = async ({
  prog,
  forcedType = null,
  currentAssessmentType,
  loadedProgramSchemasRef,
  programAssessmentSchema,
  setSchemaLoading,
  availableProgramGroups,
  setProgramAssessmentSchema,
}) => {
  if (!prog) return;
  const rawProgId = prog.programId || prog.id;
  const progName = prog.name || prog.groupName || prog.displayName || "";
  const canonicalId = getCanonicalProgramId(
    rawProgId,
    progName,
    availableProgramGroups,
  );
  const cleanId = cleanProgramId(canonicalId || rawProgId);
  const targetId = canonicalId || cleanId;
  if (!targetId || targetId === "OTHER" || targetId === "GENERAL") {
    return;
  }
  const mapKey = `${targetId}_PROGRAM_SPECIFIC`;
  const desiredType = (
    forcedType ||
    prog.formType ||
    prog.assessmentType ||
    currentAssessmentType ||
    "INITIAL"
  ).toUpperCase();

  const typeKey = `${targetId}_${desiredType}`;

  if (
    !forcedType &&
    loadedProgramSchemasRef.current.has(typeKey) &&
    (programAssessmentSchema?.[mapKey] ||
      programAssessmentSchema?.[`${cleanId}_PROGRAM_SPECIFIC`])
  ) {
    return;
  }

  loadedProgramSchemasRef.current.add(typeKey);
  loadedProgramSchemasRef.current.add(mapKey);
  loadedProgramSchemasRef.current.add(targetId);
  loadedProgramSchemasRef.current.add(cleanId);

  setSchemaLoading((prev) => ({
    ...prev,
    [mapKey]: true,
    [targetId]: true,
    [cleanId]: true,
    [rawProgId]: true,
    ...(prog.id ? { [prog.id]: true } : {}),
  }));

  try {
    let fetchedSchema = null;
    let schemaFromGroup = null;

    const allGroups =
      availableProgramGroups && availableProgramGroups.length > 0
        ? availableProgramGroups
        : await fetchAssessmentGroups();

    const targetGroupSummary = findTargetGroupSummary(
      allGroups,
      targetId,
      rawProgId,
      cleanId,
    );

    if (targetGroupSummary?.id) {
      schemaFromGroup = await fetchAssessmentGroupById(targetGroupSummary.id);
    }

    if (schemaFromGroup?.templates) {
      const matchedTemplate = schemaFromGroup.templates.find(
        (t) => String(t.formType || "").toUpperCase() === desiredType,
      );
      if (matchedTemplate?.schemaPayload) {
        fetchedSchema = parseSchemaPayload(matchedTemplate.schemaPayload);
      }
    }

    if (!fetchedSchema) {
      fetchedSchema = await loadProgramSchemaFallback(
        targetId,
        desiredType,
        mapKey,
        cleanId,
      );
    }

    if (fetchedSchema) {
      setProgramAssessmentSchema((prev) => ({
        ...prev,
        [mapKey]: fetchedSchema,
        [`${cleanId}_PROGRAM_SPECIFIC`]: fetchedSchema,
        [targetId]: fetchedSchema,
        [rawProgId]: fetchedSchema,
        ...(prog.id ? { [prog.id]: fetchedSchema } : {}),
        ...(prog.name ? { [prog.name]: fetchedSchema } : {}),
      }));
    }
  } catch (err) {
    console.error(
      `Error dynamically loading schema for program ${targetId}:`,
      err,
    );
  } finally {
    setSchemaLoading((prev) => ({
      ...prev,
      [mapKey]: false,
      [targetId]: false,
      [cleanId]: false,
      [rawProgId]: false,
      ...(prog.id ? { [prog.id]: false } : {}),
    }));
  }
};

const executeProgramAdd = ({
  programGroup,
  source = "PROGRAM_SPECIFIC",
  formType = null,
  manuallyAdded = false,
  isFormless = false,
  availableProgramGroups,
  referenceData,
  newlyAddedProgramIdRef,
  removedProgramIds,
  setRemovedProgramIds,
  setAddedPrograms,
  setActivePrograms,
  selectedOutcome,
  setSelectedOutcome,
  setSelectedSubReason,
  setDeclinedReason,
  setDeclinedNotes,
  setActiveProgramTab,
  ensureProgramSchemaLoaded,
  activeStep,
  handleSaveDraftRef,
  activePrograms,
  addedPrograms,
}) => {
  if (!programGroup) return;
  const rawProgId =
    programGroup.programId || programGroup.id || programGroup.groupName;
  const progName =
    programGroup.programDisplayName ||
    programGroup.groupName ||
    programGroup.name ||
    programGroup.displayName ||
    rawProgId;
  const canonicalId = getCanonicalProgramId(
    rawProgId,
    progName,
    availableProgramGroups,
  );
  const cleanId = cleanProgramId(canonicalId || rawProgId);
  const targetId = canonicalId || cleanId || rawProgId;

  if (manuallyAdded && targetId) {
    newlyAddedProgramIdRef.current = targetId;
  }

  const upperId = String(rawProgId).toUpperCase().trim();
  const upperClean = String(cleanId).toUpperCase().trim();
  const upperNorm = normalizeProgramId(rawProgId);
  const upperName = String(progName).toUpperCase().trim();
  const upperNameNorm = normalizeProgramId(progName);

  const matchIdentifiers = { upperId, upperClean, upperNorm };

  const nextRemovedIds = unremoveProgramKeys(removedProgramIds, {
    upperId,
    upperClean,
    upperNorm,
    canonicalId,
    upperName,
    upperNameNorm,
  });
  setRemovedProgramIds(nextRemovedIds);

  const progObj = buildProgramAddObject(programGroup, targetId, progName, {
    formType,
    manuallyAdded,
    isFormless,
    referenceData,
  });

  let newAddedPrograms = [];
  setAddedPrograms((prev) => {
    newAddedPrograms = upsertProgramInList(prev, progObj, matchIdentifiers);
    return newAddedPrograms;
  });

  let newActivePrograms = [];
  setActivePrograms((prev) => {
    newActivePrograms = upsertProgramInList(prev, progObj, matchIdentifiers);
    return newActivePrograms;
  });

  const enrolledObj = referenceData?.outcomes?.find((o) =>
    isEnrolledOutcome(o.outcomeName || o.displayName, referenceData),
  );
  const enrolledVal = enrolledObj?.outcomeName || "ENROLLED";

  const mappedId = mapLocalProgramId(rawProgId);
  const mappedName = mapLocalProgramId(progName);

  const keysToUpdate = computeProgramOutcomeKeys({
    rawProgId,
    programGroup,
    targetId,
    upperId,
    cleanId,
    upperClean,
    canonicalId,
    mappedId,
    mappedName,
    progName,
    upperName,
  });

  const nextOutcomes = { ...selectedOutcome };
  keysToUpdate.forEach((k) => {
    nextOutcomes[k] = enrolledVal;
  });
  setSelectedOutcome(nextOutcomes);

  setSelectedSubReason((prev) => removeKeysFromMap(prev, keysToUpdate));
  setDeclinedReason((prev) => removeKeysFromMap(prev, keysToUpdate));
  setDeclinedNotes((prev) => removeKeysFromMap(prev, keysToUpdate));

  if (source === "PROGRAM_SPECIFIC") {
    setActiveProgramTab(targetId);
  }

  ensureProgramSchemaLoaded(progObj);

  if (
    source === "PROGRAM_SPECIFIC" &&
    activeStep > 0 &&
    handleSaveDraftRef.current
  ) {
    const computedActive =
      newActivePrograms.length > 0
        ? newActivePrograms
        : [...activePrograms, progObj];
    const computedAdded =
      newAddedPrograms.length > 0
        ? newAddedPrograms
        : [...addedPrograms, progObj];
    handleSaveDraftRef.current({
      silent: true,
      updatedActivePrograms: computedActive,
      updatedAddedPrograms: computedAdded,
      updatedRemovedIds: nextRemovedIds,
      updatedOutcomes: nextOutcomes,
    });
  }
};

const applyLoadedTemplatesResponse = ({
  data,
  effectiveCoreType,
  setGeneralAssessmentSchema,
  setLifestyleAssessmentSchema,
  setCarePlanAssessmentSchema,
  setGeneralPayloadKey,
  setLifestylePayloadKey,
  setCarePlanPayloadKey,
  setProgramAssessmentSchema,
  setWarnings,
}) => {
  const parsed = parseSchemasResponse(data);

  setGeneralAssessmentSchema(parsed.generalSchema || null);
  setLifestyleAssessmentSchema(parsed.lifestyleSchema || null);
  setCarePlanAssessmentSchema(parsed.carePlanSchema || null);

  const rawPayload = data?.payload || data?.schemas || data;
  const rawKeys =
    rawPayload && typeof rawPayload === "object" && !Array.isArray(rawPayload)
      ? Object.keys(rawPayload)
      : [];

  const genKey =
    rawKeys.find((k) => classifyPayloadKey(k) === "general") ||
    (effectiveCoreType === "INITIAL"
      ? "OTHER_GENERAL"
      : `OTHER_GENERAL_${effectiveCoreType}`);

  const lifestyleKey =
    rawKeys.find((k) => classifyPayloadKey(k) === "lifestyle") ||
    (effectiveCoreType === "INITIAL"
      ? "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT"
      : `LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT_GENERAL_${effectiveCoreType}`);

  const carePlanKey =
    rawKeys.find((k) => classifyPayloadKey(k) === "carePlan") ||
    (effectiveCoreType === "INITIAL"
      ? "CARE_PLAN_COORDINATION_COMPLETION"
      : `CARE_PLAN_COORDINATION_COMPLETION_GENERAL_${effectiveCoreType}`);

  setGeneralPayloadKey(genKey);
  setLifestylePayloadKey(lifestyleKey);
  setCarePlanPayloadKey(carePlanKey);

  if (
    parsed.programSchemasMap &&
    Object.keys(parsed.programSchemasMap).length > 0
  ) {
    setProgramAssessmentSchema((prev) => ({
      ...prev,
      ...parsed.programSchemasMap,
    }));
  }
  if (parsed.warnings && Object.keys(parsed.warnings).length > 0) {
    setWarnings((prev) => ({
      ...prev,
      ...parsed.warnings,
    }));
  }
  return {
    ...parsed,
    data,
    generalKey: genKey,
    lifestyleKey,
    carePlanKey,
    generalSchemaPayload: rawPayload?.[genKey] || parsed.generalSchema || null,
    lifestyleSchemaPayload:
      rawPayload?.[lifestyleKey] || parsed.lifestyleSchema || null,
    carePlanSchemaPayload:
      rawPayload?.[carePlanKey] || parsed.carePlanSchema || null,
  };
};

const renderClinicalNoteStepContent = ({
  stageKey,
  memberData,
  assigneeRnName,
  selectedOutcome,
  setSelectedOutcome,
  clinicalNotes,
  setClinicalNotes,
  certification,
  setCertification,
  secondaryColor,
  borderColor,
  primaryColor,
  filteredProgramMap,
  allDisplayActivePrograms,
  initialModalAssessmentTypes,
  referenceData,
  readOnly,
  noteStartTime,
  noteCompletionTime,
  declinedReason,
  setDeclinedReason,
  selectedSubReason,
  setSelectedSubReason,
  declinedNotes,
  setDeclinedNotes,
  isStep0DirectSubmit,
  availableProgramGroups,
  showAssessmentTypeSelect,
  generalAssessmentId,
  clinicalAssessmentId,
  currentAssessmentType,
  handleAssessmentTypeChange,
  handleProgramAssessmentTypeChange,
  handleStep0AddProgram,
  handleRemoveProgram,
  selectedProgram,
  generalAssessmentSchema,
  generalFormPayload,
  setGeneralAssessmentId,
  setGeneralAssessmentVersion,
  setGeneralAssessmentSchema,
  setGeneralFormPayload,
  bgColor,
  columnsCount,
  warnings,
  programSpecificRef,
  displayActivePrograms,
  handleSetActivePrograms,
  availablePrograms,
  schemaLoading,
  activeProgramTab,
  handleProgramSubTabChange,
  handleProgramSpecificAddProgram,
  getOutcomeForProgram,
  programAssessmentSchema,
  programFormPayload,
  setProgramFormPayload,
  lifestyleAssessmentId,
  lifestyleAssessmentSchema,
  lifestyleFormPayload,
  setLifestyleAssessmentId,
  setLifestyleAssessmentSchema,
  setLifestyleFormPayload,
  carePlanAssessmentId,
  carePlanAssessmentSchema,
  carePlanFormPayload,
  setCarePlanAssessmentId,
  setCarePlanAssessmentSchema,
  setCarePlanFormPayload,
}) => {
  const effectiveRnName =
    assigneeRnName ||
    memberData?.assigneeRnName ||
    memberData?.assignee_rn_name ||
    memberData?.member?.assigneeRnName ||
    "";

  switch (stageKey) {
    case "OUTCOMES":
      return (
        <OutcomeAndSign
          memberData={{
            ...memberData,
            assigneeRnName: effectiveRnName,
          }}
          assigneeRnName={effectiveRnName}
          selectedOutcome={selectedOutcome}
          setSelectedOutcome={setSelectedOutcome}
          clinicalNotes={clinicalNotes}
          setClinicalNotes={setClinicalNotes}
          certification={certification}
          setCertification={setCertification}
          secondaryColor={secondaryColor}
          borderColor={borderColor}
          primaryColor={primaryColor}
          programMap={filteredProgramMap}
          activePrograms={allDisplayActivePrograms}
          initialModalAssessmentTypes={initialModalAssessmentTypes}
          outcomes={
            referenceData?.outcomes
              ?.map((o) => o.displayName)
              .filter(Boolean) || []
          }
          readOnly={readOnly}
          noteStartTime={noteStartTime}
          noteCompletionTime={noteCompletionTime}
          declinedReason={declinedReason}
          setDeclinedReason={setDeclinedReason}
          selectedSubReason={selectedSubReason}
          setSelectedSubReason={setSelectedSubReason}
          declinedNotes={declinedNotes}
          setDeclinedNotes={setDeclinedNotes}
          referenceData={referenceData}
          showOutcomesOnly={!isStep0DirectSubmit}
          showPreAssessmentHeader={true}
          showNotesAndSign={isStep0DirectSubmit}
          availableProgramGroups={availableProgramGroups}
          showAssessmentTypeSelect={showAssessmentTypeSelect}
          isAssessmentTypeDisabled={Boolean(
            generalAssessmentId || clinicalAssessmentId || readOnly,
          )}
          assessmentType={currentAssessmentType || ""}
          onAssessmentTypeChange={handleAssessmentTypeChange}
          onProgramAssessmentTypeChange={handleProgramAssessmentTypeChange}
          isAddProgramDisabled={false}
          onAddProgram={handleStep0AddProgram}
          onRemoveProgram={handleRemoveProgram}
        />
      );
    case "CORE":
      return (
        <GeneralAssessment
          memberData={memberData}
          assessmentType={currentAssessmentType || "initial"}
          selectedProgram={selectedProgram}
          generalAssessmentId={generalAssessmentId}
          generalAssessmentSchema={generalAssessmentSchema}
          generalFormPayload={generalFormPayload}
          onInitialize={(data) => {
            setGeneralAssessmentId(data.id);
            setGeneralAssessmentVersion(data.version);
            if (data.schema) {
              setGeneralAssessmentSchema(data.schema);
            }
          }}
          onFormStateChange={setGeneralFormPayload}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          borderColor={borderColor}
          bgColor={bgColor}
          columnsCount={columnsCount}
          readOnly={readOnly}
          warnings={warnings}
          payloadKey="GENERAL"
        />
      );
    case "PROGRAM":
      return (
        <ProgramSpecific
          ref={programSpecificRef}
          memberData={memberData}
          selectedProgram={selectedProgram}
          assessmentType={currentAssessmentType}
          onFormStateChange={setProgramFormPayload}
          secondaryColor={secondaryColor}
          borderColor={borderColor}
          primaryColor={primaryColor}
          programAssessmentSchema={programAssessmentSchema}
          programFormPayload={programFormPayload}
          columnsCount={columnsCount}
          readOnly={readOnly}
          programMap={filteredProgramMap}
          activePrograms={displayActivePrograms}
          setActivePrograms={handleSetActivePrograms}
          warnings={warnings}
          availablePrograms={
            availableProgramGroups && availableProgramGroups.length > 0
              ? availableProgramGroups
              : availablePrograms
          }
          schemaLoading={schemaLoading}
          referenceData={referenceData}
          activeProgramTab={activeProgramTab}
          onTabChange={handleProgramSubTabChange}
          onAddProgram={handleProgramSpecificAddProgram}
          onRemoveProgram={handleRemoveProgram}
          selectedOutcome={selectedOutcome}
          getOutcomeForProgram={getOutcomeForProgram}
          onAssessmentTypeChange={handleProgramAssessmentTypeChange}
        />
      );
    case "LIFESTYLE":
      return (
        <GeneralAssessment
          memberData={memberData}
          assessmentType={currentAssessmentType || "initial"}
          selectedProgram={selectedProgram}
          generalAssessmentId={lifestyleAssessmentId || generalAssessmentId}
          generalAssessmentSchema={lifestyleAssessmentSchema}
          generalFormPayload={lifestyleFormPayload}
          onInitialize={(data) => {
            setLifestyleAssessmentId(data.id);
            if (data.schema) {
              setLifestyleAssessmentSchema(data.schema);
            }
          }}
          onFormStateChange={setLifestyleFormPayload}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          borderColor={borderColor}
          bgColor={bgColor}
          columnsCount={columnsCount}
          readOnly={readOnly}
          warnings={warnings}
          payloadKey="LIFESTYLE"
        />
      );
    case "CARE_PLAN":
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <GeneralAssessment
            memberData={memberData}
            assessmentType={currentAssessmentType || "initial"}
            selectedProgram={selectedProgram}
            generalAssessmentId={carePlanAssessmentId || generalAssessmentId}
            generalAssessmentSchema={carePlanAssessmentSchema}
            generalFormPayload={carePlanFormPayload}
            onInitialize={(data) => {
              setCarePlanAssessmentId(data.id);
              if (data.schema) {
                setCarePlanAssessmentSchema(data.schema);
              }
            }}
            onFormStateChange={setCarePlanFormPayload}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            borderColor={borderColor}
            bgColor={bgColor}
            columnsCount={columnsCount}
            readOnly={readOnly}
            warnings={warnings}
            payloadKey="CARE_PLAN"
          />
          <OutcomeAndSign
            memberData={{
              ...memberData,
              assigneeRnName: effectiveRnName,
            }}
            assigneeRnName={effectiveRnName}
            selectedOutcome={selectedOutcome}
            setSelectedOutcome={setSelectedOutcome}
            clinicalNotes={clinicalNotes}
            setClinicalNotes={setClinicalNotes}
            certification={certification}
            setCertification={setCertification}
            secondaryColor={secondaryColor}
            borderColor={borderColor}
            primaryColor={primaryColor}
            programMap={filteredProgramMap}
            activePrograms={allDisplayActivePrograms}
            initialModalAssessmentTypes={initialModalAssessmentTypes}
            outcomes={
              referenceData?.outcomes
                ?.map((o) => o.displayName)
                .filter(Boolean) || []
            }
            readOnly={readOnly}
            noteStartTime={noteStartTime}
            noteCompletionTime={noteCompletionTime}
            declinedReason={declinedReason}
            setDeclinedReason={setDeclinedReason}
            selectedSubReason={selectedSubReason}
            setSelectedSubReason={setSelectedSubReason}
            declinedNotes={declinedNotes}
            setDeclinedNotes={setDeclinedNotes}
            referenceData={referenceData}
            showSignOnly={true}
          />
        </Box>
      );
    default:
      return null;
  }
};

const renderClinicalNoteAddProgramModal = ({
  pendingAddProgram,
  onClose,
  pendingAssessmentType,
  setPendingAssessmentType,
  primaryColor,
  handleConfirmAddProgram,
}) => (
  <Dialog
    open={Boolean(pendingAddProgram)}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    slotProps={{
      paper: {
        sx: {
          borderRadius: "16px",
          p: 2.5,
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
        },
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2.5,
      }}
    >
      <Typography
        sx={{
          color: "#1B2F3F",
          fontWeight: 700,
          fontSize: "16px",
        }}
      >
        Add Program Assessment
      </Typography>
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          color: "#7F7C9F",
          p: 0.5,
          "&:hover": {
            color: "#403C6F",
            backgroundColor: "rgba(64, 60, 111, 0.04)",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: "20px" }} />
      </IconButton>
    </Box>

    <DialogContent sx={{ p: 0 }}>
      <Typography
        sx={{
          fontSize: "13px",
          color: "#403C6F",
          fontWeight: 600,
          mb: 1,
        }}
      >
        Assigned CG+ Programs
      </Typography>
      <Box
        sx={{
          border: "1px solid #E8E8E9",
          borderRadius: "10px",
          p: "8px 12px",
          minHeight: "44px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          backgroundColor: "#FFFFFF",
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#F0EFFB",
            color: "#403C6F",
            borderRadius: "16px",
            px: 1.5,
            py: 0.5,
            fontSize: "13px",
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          {pendingAddProgram?.programGroup?.programDisplayName ||
            pendingAddProgram?.programGroup?.displayName ||
            pendingAddProgram?.programGroup?.groupName ||
            pendingAddProgram?.programGroup?.name}
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: "13px",
          color: "#403C6F",
          fontWeight: 600,
          mb: 1,
        }}
      >
        Assessment Type
      </Typography>
      <FormControl fullWidth size="small">
        <Select
          displayEmpty
          value={pendingAssessmentType}
          onChange={(e) => setPendingAssessmentType(e.target.value)}
          sx={{
            borderRadius: "10px",
            height: "44px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E8E8E9",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: primaryColor,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${primaryColor} !important`,
            },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
            },
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#9F9DB6", fontSize: "14px" }}>
                  Select Assessment Type
                </span>
              );
            }
            const labels = {
              INITIAL: "Initial Assessment",
              FOLLOW_UP: "Follow-up Assessment",
              DISCHARGE: "Discharge Assessment",
            };
            return labels[selected] || selected;
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            slotProps: {
              paper: {
                sx: {
                  backgroundColor: "#6E6D92",
                  color: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                  mt: 0.5,
                  boxSizing: "border-box",
                  "& .MuiMenuItem-root": {
                    fontSize: "13px",
                    color: "#fff",
                    py: 1,
                    px: 2,
                    borderRadius: "4px",
                    mx: 0.5,
                    my: 0.25,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#403C6F !important",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#403C6F !important",
                      },
                    },
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="INITIAL">Initial Assessment</MenuItem>
          <MenuItem value="FOLLOW_UP">Follow-up Assessment</MenuItem>
          <MenuItem value="DISCHARGE">Discharge Assessment</MenuItem>
        </Select>
      </FormControl>
    </DialogContent>

    <DialogActions
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 2,
        pt: 3,
        pb: 0.5,
        px: 0,
      }}
    >
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{
          borderRadius: "50px",
          borderColor: "#403C6F",
          color: "#403C6F",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          px: 3.5,
          py: 0.8,
          minWidth: "110px",
          "&:hover": {
            backgroundColor: "rgba(64, 60, 111, 0.04)",
            borderColor: "#312e5e",
          },
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={() =>
          handleConfirmAddProgram(
            pendingAddProgram.programGroup,
            pendingAssessmentType,
            pendingAddProgram.source,
          )
        }
        disabled={!pendingAssessmentType}
        variant="contained"
        sx={{
          borderRadius: "50px",
          backgroundColor: primaryColor,
          color: "#FFFFFF",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          px: 3.5,
          py: 0.8,
          minWidth: "110px",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#312e5e",
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            backgroundColor: "#C4C2D3",
            color: "#FFFFFF",
          },
        }}
      >
        Next
      </Button>
    </DialogActions>
  </Dialog>
);

const renderClinicalNoteDiscardModal = ({
  showDiscardWarning,
  onClose,
  handleSaveDraft,
  readOnly,
  bypassWarningRef,
  pendingNavigationActionRef,
  onBack,
}) => (
  <Dialog
    open={showDiscardWarning}
    onClose={onClose}
    slotProps={{
      paper: {
        sx: {
          borderRadius: "16px",
          p: 1.5,
          maxWidth: "440px",
          width: "100%",
        },
      },
    }}
  >
    <DialogTitle
      sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1.5 }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: "#FEF0C7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <WarningAmberIcon sx={{ color: "#DC6803", fontSize: 24 }} />
      </Box>
      <Typography
        component="span"
        sx={{ fontWeight: 600, color: "#101828", fontSize: "18px" }}
      >
        Save Assessment Changes?
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ pb: 2, pt: 0.5 }}>
      <Typography sx={{ color: "#475467", fontSize: "14px", lineHeight: 1.5 }}>
        You have unsaved changes in this assessment. Please save your changes as
        a draft before leaving.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{
          borderRadius: "50px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          borderColor: "#D0D5DD",
          color: "#344054",
          px: 2.5,
          py: 0.8,
          "&:hover": {
            backgroundColor: "#F9FAFB",
            borderColor: "#D0D5DD",
          },
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={async () => {
          try {
            if (handleSaveDraft && !readOnly) {
              await handleSaveDraft({ silent: true });
            }
          } catch (e) {
            console.error("Failed to save draft on exit:", e);
          } finally {
            onClose();
            bypassWarningRef.current = true;
            if (pendingNavigationActionRef.current) {
              const action = pendingNavigationActionRef.current;
              pendingNavigationActionRef.current = null;
              action();
            } else {
              onBack?.();
            }
          }
        }}
        variant="contained"
        sx={{
          borderRadius: "50px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          backgroundColor: "#403C6F",
          color: "#FFFFFF",
          px: 2.5,
          py: 0.8,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#312e5e",
            boxShadow: "none",
          },
        }}
      >
        Save & Exit
      </Button>
    </DialogActions>
  </Dialog>
);

const renderClinicalNoteTypeConflictModal = ({
  typeConflictModal,
  onClose,
  typeConflictModalQuestion,
  typeConflictModalWarning,
  isConflictProceedLoading,
  handleConfirmTypeConflictProceed,
}) => (
  <Dialog
    open={Boolean(typeConflictModal?.open)}
    onClose={onClose}
    slotProps={{
      paper: {
        sx: {
          borderRadius: "16px",
          p: 1.5,
          maxWidth: "520px",
          width: "100%",
        },
      },
    }}
  >
    <DialogTitle
      sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1.5 }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: "#FEF0C7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <WarningAmberIcon sx={{ color: "#DC6803", fontSize: 24 }} />
      </Box>
      <Typography
        component="span"
        sx={{ fontWeight: 600, color: "#101828", fontSize: "18px" }}
      >
        Change Assessment Type?
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ pb: 2, pt: 0.5 }}>
      <Typography
        sx={{
          color: "#101828",
          fontSize: "14px",
          fontWeight: 500,
          mb: 1.5,
        }}
      >
        {typeConflictModalQuestion}
      </Typography>
      <Typography sx={{ color: "#475467", fontSize: "14px", lineHeight: 1.5 }}>
        {typeConflictModalWarning}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
      <Button
        onClick={onClose}
        variant="outlined"
        disabled={isConflictProceedLoading}
        sx={{
          borderRadius: "50px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          borderColor: "#D0D5DD",
          color: "#344054",
          px: 2.5,
          py: 0.8,
          "&:hover": {
            backgroundColor: "#F9FAFB",
            borderColor: "#D0D5DD",
          },
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={handleConfirmTypeConflictProceed}
        variant="contained"
        disabled={isConflictProceedLoading}
        sx={{
          borderRadius: "50px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          backgroundColor: "#403C6F",
          color: "#FFFFFF",
          px: 2.5,
          py: 0.8,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#312e5e",
            boxShadow: "none",
          },
        }}
      >
        {isConflictProceedLoading ? "Proceeding..." : "Proceed"}
      </Button>
    </DialogActions>
  </Dialog>
);

const useReduxPhiToggle = (reduxContext, isShowPhiEnabledProp) => {
  const reduxPhiToggle = React.useSyncExternalStore(
    reduxContext?.store ? reduxContext.store.subscribe : () => () => {},
    reduxContext?.store
      ? () => reduxContext.store.getState()?.toggles?.phi ?? false
      : () => false,
    () => false,
  );
  return isShowPhiEnabledProp === undefined
    ? !reduxPhiToggle
    : isShowPhiEnabledProp;
};

const resolveNextLoadingLabel = (activeStep, isStep0DirectSubmit) => {
  if (activeStep === 0) {
    return isStep0DirectSubmit ? "Submitting..." : "Proceed";
  }
  return "Saving...";
};

const resolveExportPdfHandler = (
  readOnly,
  noteCompletionTime,
  handleExportPdf,
) => {
  return readOnly || Boolean(noteCompletionTime) ? handleExportPdf : undefined;
};

const renderClinicalNoteFooter = ({
  handlePrevStep,
  handleRequestClose,
  handleSaveDraft,
  loading,
  readOnly,
  isNextLoading,
  activeStep,
  isStep0DirectSubmit,
  generalAssessmentSchema,
  hasProgramSpecific,
  isProgramFormEmpty,
  handleNextStep,
  isSubmitting,
  stepsLength,
  buttonLabel,
  isStepValid,
  nextLoadingLabel,
  borderColor,
}) => {
  const isDraftDisabled =
    loading ||
    readOnly ||
    isNextLoading ||
    (activeStep === 0
      ? !isStep0DirectSubmit
      : !generalAssessmentSchema?.sections ||
        Object.keys(generalAssessmentSchema.sections).length === 0) ||
    (hasProgramSpecific && activeStep === 2 && isProgramFormEmpty());

  const isNextDisabled =
    loading ||
    isSubmitting ||
    isNextLoading ||
    (readOnly &&
      (buttonLabel === "Submit" ||
        activeStep === stepsLength - 1 ||
        (activeStep === 0 && isStep0DirectSubmit))) ||
    (!readOnly && !isStepValid());

  const showEndIcon =
    !isNextLoading &&
    activeStep !== stepsLength - 1 &&
    !(activeStep === 0 && isStep0DirectSubmit);

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#fff",
        borderTop: `1px solid ${borderColor}`,
        display: "flex",
        justifyContent: "space-between",
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          onClick={handlePrevStep}
          disabled={loading || activeStep === 0}
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            color: "#403C6F",
            borderColor: "#403C6F",
            px: 4,
            py: 1,
            "&:hover": {
              backgroundColor: "rgba(64, 60, 111, 0.04)",
              borderColor: "#312e5e",
              borderWidth: "1px",
            },
            "&.Mui-disabled": {
              pointerEvents: "auto",
              cursor: "not-allowed",
            },
          }}
        >
          Prev
        </Button>
        <Button
          variant="text"
          onClick={handleRequestClose}
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            color: "#667085",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "rgba(102, 112, 133, 0.08)",
              color: "#344054",
            },
          }}
        >
          Cancel
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => handleSaveDraft()}
          disabled={isDraftDisabled}
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            color: "#403C6F",
            borderColor: "#403C6F",
            px: 4,
            py: 1,
            "&:hover": {
              backgroundColor: "rgba(64, 60, 111, 0.04)",
              borderColor: "#312e5e",
              borderWidth: "1px",
            },
            "&.Mui-disabled": {
              pointerEvents: "auto",
              cursor: "not-allowed",
            },
          }}
        >
          Save Draft
        </Button>
        <Button
          endIcon={showEndIcon ? <ArrowForwardIcon /> : null}
          variant="contained"
          onClick={handleNextStep}
          disabled={isNextDisabled}
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            backgroundColor: "#403C6F",
            color: "#FFF",
            px: 4,
            py: 1,
            boxShadow: "none",
            "&.Mui-disabled": {
              backgroundColor: "rgba(64, 60, 111, 0.4)",
              color: "rgba(255, 255, 255, 0.7)",
              pointerEvents: "auto",
              cursor: "not-allowed",
            },
            "&:hover": {
              backgroundColor: "#312e5e",
              boxShadow: "none",
            },
          }}
        >
          {isNextLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#FFF" }} />
              <span>{nextLoadingLabel}</span>
            </Box>
          ) : (
            buttonLabel
          )}
        </Button>
      </Box>
    </Box>
  );
};

const resolveFallbackSelected = (selectedProgram) => {
  if (Array.isArray(selectedProgram) && selectedProgram.length > 0) {
    return selectedProgram;
  }
  if (selectedProgram && typeof selectedProgram === "object") {
    return [selectedProgram];
  }
  return null;
};

const resolveTypeConflictActiveList = ({
  typeConflictModal,
  activePrograms,
  allDisplayActivePrograms,
  getOutreachProgramsList,
  selectedProgram,
  memberClinicalPrograms,
}) => {
  if (
    Array.isArray(typeConflictModal?.enrolledActivePrograms) &&
    typeConflictModal.enrolledActivePrograms.length > 0
  ) {
    return typeConflictModal.enrolledActivePrograms;
  }
  if (Array.isArray(activePrograms) && activePrograms.length > 0) {
    return activePrograms;
  }
  if (
    Array.isArray(allDisplayActivePrograms) &&
    allDisplayActivePrograms.length > 0
  ) {
    return allDisplayActivePrograms;
  }
  if (typeof getOutreachProgramsList === "function") {
    const outreach = getOutreachProgramsList();
    if (Array.isArray(outreach) && outreach.length > 0) {
      return outreach;
    }
  }
  const fallbackSel = resolveFallbackSelected(selectedProgram);
  if (fallbackSel) {
    return fallbackSel;
  }
  if (
    Array.isArray(memberClinicalPrograms) &&
    memberClinicalPrograms.length > 0
  ) {
    return memberClinicalPrograms;
  }
  return [];
};

const resolveTypeConflictCombinedPrograms = ({
  isStep0Early,
  updatedActiveList,
  currentOutreach,
  addedPrograms,
  removedProgramIds,
  getCombinedPrograms,
}) => {
  if (isStep0Early) {
    if (updatedActiveList.length > 0) {
      return updatedActiveList;
    }
    return currentOutreach || [];
  }
  return getCombinedPrograms(
    updatedActiveList,
    addedPrograms || [],
    removedProgramIds || new Set(),
  );
};

const resolveNextStepAfterConflict = (sourceStep, isStep0DirectSubmit) => {
  if (sourceStep === 0 && !isStep0DirectSubmit) {
    return 1;
  }
  return 0;
};

const clearAssessmentFormPayloads = (setters, loadedProgramSchemasRef) => {
  setters.setGeneralFormPayload({});
  setters.setLifestyleFormPayload({});
  setters.setCarePlanFormPayload({});
  setters.setProgramFormPayload({});
  loadedProgramSchemasRef.current?.clear();
};

const resolveSafeCurrentType = (currentType, requiredType) => {
  const normCurrent = (currentType || "INITIAL").toUpperCase();
  const normRequired = (requiredType || "FOLLOW_UP").toUpperCase();
  if (normCurrent === normRequired) {
    if (normRequired === "INITIAL") {
      return "FOLLOW_UP";
    }
    return "INITIAL";
  }
  return currentType || "INITIAL";
};

const resolveSubmitTargetSession = async ({
  targetAssessmentId,
  targetAssessmentVersion,
  readOnly,
  memberData,
  hasEnrolledProgram,
  getEnrolledProgramList,
  referenceData,
  getOutreachProgramsList,
  setActivePrograms,
  setLoading,
  setGeneralAssessmentId,
  setGeneralAssessmentVersion,
  latestAssessmentVersionRef,
}) => {
  if (targetAssessmentId || readOnly) {
    return {
      targetId: targetAssessmentId,
      targetVersion: targetAssessmentVersion,
    };
  }
  const initResult = await ensureStep0SubmitSessionInitializedHelper({
    targetAssessmentId,
    readOnly,
    memberData,
    hasEnrolledProgram,
    getEnrolledProgramList,
    referenceData,
    getOutreachProgramsList,
    setActivePrograms,
    setLoading,
    setGeneralAssessmentId,
    setGeneralAssessmentVersion,
  });
  return applyInitResultToTarget(
    initResult,
    targetAssessmentId,
    targetAssessmentVersion,
    latestAssessmentVersionRef,
  );
};

const handlePreSubmitDeclinedAutosave = async ({
  targetAssessmentId,
  targetAssessmentVersion,
  generalAssessmentVersion,
  certification,
  elapsedTimeRef,
  generalPayloadKey,
  lifestylePayloadKey,
  carePlanPayloadKey,
  generalFormPayload,
  lifestyleFormPayload,
  carePlanFormPayload,
  activeProgramTab,
  displayActivePrograms,
  selectedOutcome,
  selectedSubReason,
  declinedReason,
  declinedNotes,
  clinicalNotes,
  programsPayload,
  setGeneralAssessmentVersion,
  setTypeConflictModal,
  setSectionError,
  latestAssessmentVersionRef,
}) => {
  const autoSaveResult = await autosaveDeclinedAssessmentBeforeSubmitHelper({
    targetAssessmentId,
    targetAssessmentVersion,
    generalAssessmentVersion,
    certification,
    elapsedTimeRef,
    generalPayloadKey,
    lifestylePayloadKey,
    carePlanPayloadKey,
    generalFormPayload,
    lifestyleFormPayload,
    carePlanFormPayload,
    activeProgramTab,
    displayActivePrograms,
    selectedOutcome,
    selectedSubReason,
    declinedReason,
    declinedNotes,
    clinicalNotes,
    programsPayload,
    setGeneralAssessmentVersion,
    setTypeConflictModal,
    setSectionError,
  });

  if (autoSaveResult?.isTypeConflict || autoSaveResult?.success === false) {
    return { shouldHalt: true, updatedVersion: targetAssessmentVersion };
  }

  const autoVersion = autoSaveResult?.version ?? autoSaveResult;
  if (isPresent(autoVersion)) {
    latestAssessmentVersionRef.current = autoVersion;
    return { shouldHalt: false, updatedVersion: autoVersion };
  }
  return { shouldHalt: false, updatedVersion: targetAssessmentVersion };
};

const handleSubmitError = ({
  error_,
  setTypeConflictModal,
  setSectionError,
  displayActivePrograms,
  programsPayload,
  activePrograms,
  activeStep,
}) => {
  console.error("Failed to submit assessment:", error_);
  const { isTypeConflict, match, errorMsg } = checkTypeConflict(error_);
  if (isTypeConflict && match && setTypeConflictModal) {
    setSectionError(null);
    const conflictPrograms = resolveConflictPrograms({
      displayActivePrograms,
      activePrograms,
      programsPayload,
    });
    setTypeConflictModal({
      open: true,
      requiredType: match[1].toUpperCase(),
      sourceStep: activeStep,
      enrolledActivePrograms: conflictPrograms,
    });
    return;
  }
  setSectionError(errorMsg);
};

const ClinicalNotePage = forwardRef((props, ref) => {
  const {
    memberData,
    selectedProgram,
    assessmentType: rawAssessmentType,
    clinicalAssessmentId,
    batchId,
    isBatch,
    onBack,
    onSave,
    readOnly = false,
    programMap,
    initialStartTime = null,
    initialClinicalNotes = "",
    clinicalNotePrograms = [],
    memberClinicalPrograms = [],
    onDraftStatusChange,
    initialAssessmentData = null,
    isDraftSession = false,
    isShowPhiEnabled: isShowPhiEnabledProp,
  } = props;

  const reduxContext = React.useContext(ReactReduxContext);
  const isShowPhiEnabled = useReduxPhiToggle(
    reduxContext,
    isShowPhiEnabledProp,
  );
  const assessmentType = useMemo(
    () => parseAssessmentType(rawAssessmentType),
    [rawAssessmentType],
  );

  const [currentAssessmentType, setCurrentAssessmentType] =
    useState(assessmentType);

  useEffect(() => {
    setCurrentAssessmentType(assessmentType);
  }, [assessmentType]);

  const [activeStep, setActiveStep] = useState(0);
  const [activeProgramTab, setActiveProgramTab] = useState("");
  const activeProgramTabRef = useRef(activeProgramTab);
  useEffect(() => {
    activeProgramTabRef.current = activeProgramTab;
  }, [activeProgramTab]);
  const columnsCount = 3;
  const [activePrograms, setActivePrograms] = useState([]);

  const hasProgramSpecific = true;

  const [generalGroupName, setGeneralGroupName] = useState("");
  const [lifestyleGroupName, setLifestyleGroupName] = useState("");
  const [carePlanGroupName, setCarePlanGroupName] = useState("");
  const [isGroupNamesLoading, setIsGroupNamesLoading] = useState(true);

  const step0Title = "Eligible Programs";
  const step1Title = generalGroupName || "General Assessment";
  const step2Title = "Program-Specific Assessment";
  const step3Title = lifestyleGroupName;
  const step4Title = carePlanGroupName;

  const steps = useMemo(
    () => [
      step0Title,
      step1Title,
      ...(hasProgramSpecific ? [step2Title] : []),
      step3Title,
      step4Title,
    ],
    [
      step0Title,
      step1Title,
      hasProgramSpecific,
      step2Title,
      step3Title,
      step4Title,
    ],
  );

  const [visitedForms, setVisitedForms] = useState([step0Title]);

  useEffect(() => {
    const currentFormName = getFormNameForStep(activeStep, steps);
    if (!currentFormName) return;
    setVisitedForms((prev) => {
      if (!prev.includes(currentFormName)) {
        return [...prev, currentFormName];
      }
      return prev;
    });
  }, [activeStep, steps]);

  // Assessment Session IDs, Deltas & Schemas for Step 3 & Step 4
  const [lifestyleAssessmentId, setLifestyleAssessmentId] = useState(null);
  const [lifestyleFormPayload, setLifestyleFormPayload] = useState({});
  const [lifestyleAssessmentSchema, setLifestyleAssessmentSchema] =
    useState(null);

  const [carePlanAssessmentId, setCarePlanAssessmentId] = useState(null);
  const [carePlanFormPayload, setCarePlanFormPayload] = useState({});
  const [carePlanAssessmentSchema, setCarePlanAssessmentSchema] =
    useState(null);

  // Assessment Form Answer Deltas
  const [generalAssessmentId, setGeneralAssessmentId] = useState(null);
  const [generalAssessmentVersion, setGeneralAssessmentVersion] = useState(0);
  const latestAssessmentVersionRef = useRef(0);
  useEffect(() => {
    if (
      generalAssessmentVersion !== undefined &&
      generalAssessmentVersion !== null
    ) {
      latestAssessmentVersionRef.current = generalAssessmentVersion;
    }
  }, [generalAssessmentVersion]);
  const [generalFormPayload, setGeneralFormPayload] = useState({});
  const [programFormPayload, setProgramFormPayload] = useState({});

  // Loaded General Assessment and Program Specific Template Schema
  const [generalAssessmentSchema, setGeneralAssessmentSchema] = useState(null);
  const [programAssessmentSchema, setProgramAssessmentSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState({});

  // Dynamic payload keys fetched from the API response
  const [generalPayloadKey, setGeneralPayloadKey] = useState("OTHER_GENERAL");
  const [lifestylePayloadKey, setLifestylePayloadKey] = useState(
    "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT",
  );
  const [carePlanPayloadKey, setCarePlanPayloadKey] = useState(
    "CARE_PLAN_COORDINATION_COMPLETION",
  );

  // Outcome and Sign Off States
  const [selectedOutcome, setSelectedOutcome] = useState({});
  const [clinicalNotes, setClinicalNotes] = useState(
    initialClinicalNotes || "",
  );
  const [isDraft, setIsDraft] = useState(false);
  const [availableProgramGroups, setAvailableProgramGroups] = useState([]);
  const [addedPrograms, setAddedPrograms] = useState([]);
  const [removedProgramIds, setRemovedProgramIds] = useState(new Set());
  const [referenceData, setReferenceData] = useState(
    () => getCachedAssessmentReferenceData() || null,
  );
  const loadedProgramSchemasRef = useRef(new Set());
  const [pendingAddProgram, setPendingAddProgram] = useState(null);
  const [pendingAssessmentType, setPendingAssessmentType] = useState("");
  const [cgPlusDischarge, setCgPlusDischarge] = useState(null);

  const getOutcomeForProgram = useCallback(
    (prog) =>
      resolveOutcomeForProgram(prog, selectedOutcome, availableProgramGroups),
    [availableProgramGroups, selectedOutcome],
  );

  // Immediately sync available assessment groups from clinicalNotePrograms prop if available
  useEffect(() => {
    if (
      Array.isArray(clinicalNotePrograms) &&
      clinicalNotePrograms.length > 0
    ) {
      const programGroups = clinicalNotePrograms.filter(
        (g) =>
          String(g.groupType || "").toUpperCase() === "PROGRAM_SPECIFIC" &&
          g.status !== "INACTIVE",
      );
      setAvailableProgramGroups(programGroups);
    }
  }, [clinicalNotePrograms]);

  const ensureProgramSchemaLoaded = useCallback(
    (prog, forcedType = null) =>
      loadProgramSchemaForProg({
        prog,
        forcedType,
        currentAssessmentType,
        loadedProgramSchemasRef,
        programAssessmentSchema,
        setSchemaLoading,
        availableProgramGroups,
        setProgramAssessmentSchema,
      }),
    [availableProgramGroups, currentAssessmentType, programAssessmentSchema],
  );

  const handleProgramAdd = useCallback(
    (
      programGroup,
      {
        source = "PROGRAM_SPECIFIC",
        formType = null,
        manuallyAdded = false,
        isFormless = false,
      } = {},
    ) =>
      executeProgramAdd({
        programGroup,
        source,
        formType,
        manuallyAdded,
        isFormless,
        availableProgramGroups,
        referenceData,
        newlyAddedProgramIdRef,
        removedProgramIds,
        setRemovedProgramIds,
        setAddedPrograms,
        setActivePrograms,
        selectedOutcome,
        setSelectedOutcome,
        setSelectedSubReason,
        setDeclinedReason,
        setDeclinedNotes,
        setActiveProgramTab,
        ensureProgramSchemaLoaded,
        activeStep,
        handleSaveDraftRef,
        activePrograms,
        addedPrograms,
      }),
    [
      availableProgramGroups,
      referenceData,
      ensureProgramSchemaLoaded,
      removedProgramIds,
      activePrograms,
      addedPrograms,
      selectedOutcome,
      activeStep,
    ],
  );

  const loadTemplatesForVisit = useCallback(
    async (programsToLoad, visitTypeOverride) => {
      const programsList = programsToLoad || activePrograms || [];
      const { nonFormless, queryParams, effectiveCoreType } =
        buildTemplateQueryParams(
          programsList,
          referenceData,
          visitTypeOverride,
          currentAssessmentType,
          cgPlusDischarge,
          getOutcomeForProgram,
        );

      if (queryParams.length === 0) return null;

      setSchemaLoading((prev) =>
        setNonFormlessLoading(prev, nonFormless, true),
      );

      try {
        console.log(
          "Loading templates via /api/v1/assessments/schemas with params:",
          queryParams,
        );
        const data = await fetchAssessmentSchemasApi(queryParams);
        return applyLoadedTemplatesResponse({
          data,
          effectiveCoreType,
          setGeneralAssessmentSchema,
          setLifestyleAssessmentSchema,
          setCarePlanAssessmentSchema,
          setGeneralPayloadKey,
          setLifestylePayloadKey,
          setCarePlanPayloadKey,
          setProgramAssessmentSchema,
          setWarnings,
        });
      } catch (err) {
        console.error("Failed to load assessment templates:", err);
        return null;
      } finally {
        setSchemaLoading((prev) =>
          setNonFormlessLoading(prev, nonFormless, false),
        );
      }
    },
    [
      activePrograms,
      referenceData,
      currentAssessmentType,
      cgPlusDischarge,
      getOutcomeForProgram,
      setGeneralPayloadKey,
      setLifestylePayloadKey,
      setCarePlanPayloadKey,
    ],
  );

  const handleConfirmAddProgram = useCallback(
    async (programGroup, selectedType, source = "PROGRAM_SPECIFIC") => {
      if (!programGroup || !selectedType) return;
      const formattedType = selectedType.toUpperCase();
      handleProgramAdd(programGroup, {
        source,
        formType: formattedType,
        manuallyAdded: true,
      });

      setPendingAddProgram(null);
      setPendingAssessmentType("");

      const rawProgId =
        programGroup.programId || programGroup.id || programGroup.groupName;
      const canonicalId = getCanonicalProgramId(
        rawProgId,
        programGroup.name,
        availableProgramGroups,
      );
      const cleanId = cleanProgramId(canonicalId || rawProgId);
      const targetId = canonicalId || cleanId || rawProgId;
      newlyAddedProgramIdRef.current = targetId;

      const newProg = {
        ...programGroup,
        id: targetId,
        programId: targetId,
        formType: formattedType,
        assessmentType: formattedType,
        manuallyAdded: true,
      };

      const updatedPrograms = [
        ...activePrograms.filter(
          (p) =>
            cleanProgramId(p.id || p.programId) !== cleanProgramId(targetId),
        ),
        newProg,
      ];
      await loadTemplatesForVisit(updatedPrograms, currentAssessmentType);
    },
    [
      handleProgramAdd,
      activePrograms,
      availableProgramGroups,
      loadTemplatesForVisit,
      currentAssessmentType,
    ],
  );

  const handleProgramAssessmentTypeChange = useCallback(
    async (programId, newType) => {
      if (!programId || newType === undefined || newType === null) return;
      const cleanId = cleanProgramId(programId);
      const upperClean = cleanId.toUpperCase();
      const normType = newType ? String(newType).toUpperCase().trim() : "";

      // Clear that program's saved answers:
      setProgramFormPayload((prev) => {
        const next = { ...prev };
        delete next[cleanId];
        delete next[programId];
        delete next[upperClean];
        delete next[`${upperClean}_PROGRAM_SPECIFIC`];
        return next;
      });

      // Clear loaded schemas cache for this program so it can be reloaded fresh:
      loadedProgramSchemasRef.current.delete(`${upperClean}_PROGRAM_SPECIFIC`);
      loadedProgramSchemasRef.current.delete(cleanId);
      loadedProgramSchemasRef.current.delete(upperClean);
      loadedProgramSchemasRef.current.delete(programId);

      // Compute updated programs list synchronously:
      const currentList = activePrograms || [];
      let matched = false;
      const updatedList = currentList.map((p) => {
        const pid = cleanProgramId(p.programId || p.id).toUpperCase();
        if (
          pid === upperClean ||
          normalizeProgramId(p.programId || p.id) ===
            normalizeProgramId(programId) ||
          normalizeProgramId(p.name || p.displayName) ===
            normalizeProgramId(programId)
        ) {
          matched = true;
          return {
            ...p,
            formType: normType,
            assessmentType: normType,
            manuallyAdded: Boolean(p.manuallyAdded),
          };
        }
        return p;
      });

      let finalActivePrograms = updatedList;
      if (!matched) {
        const matchedSrc =
          findProgramMatch(addedPrograms, programId) ||
          findProgramMatch(selectedProgram, programId) ||
          findProgramMatch(memberClinicalPrograms, programId);
        const progName =
          matchedSrc?.name || matchedSrc?.displayName || programId;
        const isManual = Boolean(
          matchedSrc?.manuallyAdded || matchedSrc?.isManual,
        );
        const newProg = {
          id: cleanId,
          programId: cleanId,
          name: progName,
          formType: normType,
          assessmentType: normType,
          manuallyAdded: isManual,
        };
        finalActivePrograms = [...updatedList, newProg];
      }

      setActivePrograms(finalActivePrograms);

      setAddedPrograms((prev) =>
        prev.map((p) => {
          const pid = cleanProgramId(p.programId || p.id).toUpperCase();
          if (
            pid === upperClean ||
            normalizeProgramId(p.programId || p.id) ===
              normalizeProgramId(programId) ||
            normalizeProgramId(p.name || p.displayName) ===
              normalizeProgramId(programId)
          ) {
            return {
              ...p,
              formType: normType,
              assessmentType: normType,
              manuallyAdded: Boolean(p.manuallyAdded),
            };
          }
          return p;
        }),
      );

      // Immediately fetch schema for this program using the new assessment type:
      const targetProg = finalActivePrograms.find(
        (p) =>
          cleanProgramId(p.programId || p.id).toUpperCase() === upperClean ||
          normalizeProgramId(p.programId || p.id) ===
            normalizeProgramId(programId) ||
          normalizeProgramId(p.name || p.displayName) ===
            normalizeProgramId(programId),
      );

      if (targetProg && normType) {
        await ensureProgramSchemaLoaded(targetProg, normType);
      }
      await loadTemplatesForVisit(finalActivePrograms, currentAssessmentType);
    },
    [
      activePrograms,
      ensureProgramSchemaLoaded,
      loadTemplatesForVisit,
      currentAssessmentType,
      addedPrograms,
      selectedProgram,
      memberClinicalPrograms,
    ],
  );

  const handleStep0AddProgram = useCallback(
    (programGroup) => {
      if (checkIsFormless(programGroup, referenceData)) {
        handleProgramAdd(programGroup, { source: "ELIGIBLE_PROGRAMS" });
      } else {
        setPendingAddProgram({ programGroup, source: "ELIGIBLE_PROGRAMS" });
        setPendingAssessmentType("");
      }
    },
    [handleProgramAdd, referenceData],
  );

  const handleAssessmentTypeChange = useCallback(
    async (type) => {
      if (!type) return;
      setCurrentAssessmentType(type);

      // "Once user adds the assessment type then create the assessement and start the assessment"
      if (!generalAssessmentId && !clinicalAssessmentId && !readOnly) {
        try {
          setLoading(true);
          const formattedType = type.toUpperCase();
          const combinedPrograms = [
            ...(activePrograms || []),
            ...(addedPrograms || []),
          ];
          const initialPrograms = buildInitialProgramsForType(
            combinedPrograms,
            formattedType,
            referenceData,
          );

          const payload = {
            memberId: Number.parseInt(
              memberData?.memberId || memberData?.id,
              10,
            ),
            encounterId: null,
            programs: initialPrograms,
          };

          console.log(
            "Creating assessment session upon selecting assessment type:",
            payload,
          );
          const res = await initializeAssessmentApi(payload);
          if (res?.id) {
            applyInitializedSessionSchemas(res, {
              setGeneralAssessmentId,
              setLifestyleAssessmentId,
              setCarePlanAssessmentId,
              setGeneralAssessmentVersion,
              setGeneralPayloadKey,
              setLifestylePayloadKey,
              setCarePlanPayloadKey,
              setGeneralAssessmentSchema,
              setLifestyleAssessmentSchema,
              setCarePlanAssessmentSchema,
              setProgramAssessmentSchema,
              setAssigneeRnName,
            });
          }
        } catch (err) {
          console.error(
            "Failed to initialize assessment on selecting assessment type:",
            err,
          );
        } finally {
          setLoading(false);
        }
      }
    },
    [
      generalAssessmentId,
      clinicalAssessmentId,
      readOnly,
      activePrograms,
      addedPrograms,
      memberData,
      referenceData,
    ],
  );

  const handleProgramSpecificAddProgram = useCallback(
    (programGroup) => {
      if (checkIsFormless(programGroup, referenceData)) {
        handleProgramAdd(programGroup, { source: "PROGRAM_SPECIFIC" });
      } else {
        setPendingAddProgram({ programGroup, source: "PROGRAM_SPECIFIC" });
        setPendingAssessmentType("");
      }
    },
    [handleProgramAdd, referenceData],
  );

  const handleRemoveProgram = useCallback(
    (prog) => {
      if (!prog) return;
      const rawId =
        typeof prog === "string"
          ? prog
          : prog.programId || prog.id || prog.groupName || prog.name || "";
      const progId = String(rawId).trim();
      const matchedProgram =
        typeof prog === "object"
          ? prog
          : activePrograms.find(
              (p) =>
                p.id === prog ||
                p.programId === prog ||
                cleanProgramId(p.id) === cleanProgramId(prog),
            ) ||
            addedPrograms.find(
              (p) =>
                p.id === prog ||
                p.programId === prog ||
                cleanProgramId(p.id) === cleanProgramId(prog),
            );
      const progName = matchedProgram
        ? String(
            matchedProgram.name ||
              matchedProgram.groupName ||
              matchedProgram.displayName ||
              "",
          ).toUpperCase()
        : "";
      const canonicalId = getCanonicalProgramId(
        progId,
        progName,
        availableProgramGroups,
      );
      const cleanId = cleanProgramId(canonicalId || progId);
      const normId = normalizeProgramId(progId);
      const upperId = progId.toUpperCase();
      const upperClean = cleanId.toUpperCase();
      const progNameNorm = normalizeProgramId(progName);

      const nextRemovedIds = new Set(removedProgramIds);
      nextRemovedIds.add(upperId);
      nextRemovedIds.add(upperClean);
      if (normId) nextRemovedIds.add(normId);
      if (canonicalId)
        nextRemovedIds.add(String(canonicalId).toUpperCase().trim());
      if (progName) nextRemovedIds.add(progName);
      if (progNameNorm) nextRemovedIds.add(progNameNorm);
      setRemovedProgramIds(nextRemovedIds);

      const remainingAdded = addedPrograms.filter((p) => {
        const pId = String(p.programId || p.id || p.groupName || p.name || "")
          .trim()
          .toUpperCase();
        const pClean = cleanProgramId(pId);
        const pNorm = normalizeProgramId(pId);
        const pName = String(p.name || p.groupName || "")
          .trim()
          .toUpperCase();
        return (
          pId !== upperId &&
          pClean !== upperClean &&
          pNorm !== normId &&
          (!progName || pName !== progName)
        );
      });
      setAddedPrograms(remainingAdded);

      const remainingActive = activePrograms.filter((ap) => {
        const apId = String(ap.programId || ap.id || ap.name || "")
          .trim()
          .toUpperCase();
        const apClean = cleanProgramId(apId);
        const apNorm = normalizeProgramId(apId);
        const apName = String(ap.name || ap.groupName || "")
          .trim()
          .toUpperCase();
        return (
          apId !== upperId &&
          apClean !== upperClean &&
          apNorm !== normId &&
          (!progName || apName !== progName)
        );
      });
      setActivePrograms(remainingActive);
      loadTemplatesForVisit(remainingActive, currentAssessmentType);

      const nextOutcome = { ...selectedOutcome };
      delete nextOutcome[progId];
      delete nextOutcome[upperId];
      delete nextOutcome[cleanId];
      delete nextOutcome[upperClean];
      delete nextOutcome[canonicalId];
      if (canonicalId) delete nextOutcome[String(canonicalId).toUpperCase()];
      if (progName) {
        delete nextOutcome[progName];
        delete nextOutcome[String(progName).toUpperCase()];
      }
      setSelectedOutcome(nextOutcome);

      // Autosave immediately upon removing a program ONLY when not on Step 0 (Eligible Programs)
      if (activeStep > 0 && handleSaveDraftRef.current) {
        handleSaveDraftRef.current({
          silent: true,
          updatedActivePrograms: remainingActive,
          updatedAddedPrograms: remainingAdded,
          updatedRemovedIds: nextRemovedIds,
          updatedOutcomes: nextOutcome,
        });
      }
    },
    [
      activeStep,
      activePrograms,
      addedPrograms,
      availableProgramGroups,
      loadTemplatesForVisit,
      currentAssessmentType,
      removedProgramIds,
      selectedOutcome,
    ],
  );

  useEffect(() => {
    if (initialClinicalNotes) {
      setClinicalNotes(initialClinicalNotes);
    }
  }, [initialClinicalNotes]);

  useEffect(() => {
    if (onDraftStatusChange) {
      onDraftStatusChange(isDraft);
    }
  }, [isDraft, onDraftStatusChange]);
  const [certification, setCertification] = useState(false);
  const [noteStartTime, setNoteStartTime] = useState(null);
  const [noteCompletionTime, setNoteCompletionTime] = useState(null);
  const [assigneeRnName, setAssigneeRnName] = useState(
    memberData?.assigneeRnName ||
      memberData?.assignee_rn_name ||
      memberData?.member?.assigneeRnName ||
      memberData?.assigneeName ||
      memberData?.assignee?.name ||
      "",
  );

  useEffect(() => {
    const rawAssignee =
      memberData?.assigneeRnName ||
      memberData?.assignee_rn_name ||
      memberData?.member?.assigneeRnName ||
      memberData?.assigneeName ||
      memberData?.assignee?.name ||
      "";
    if (rawAssignee) {
      setAssigneeRnName(rawAssignee);
    }
  }, [memberData]);

  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedTimeRef = useRef(0);
  const [declinedReason, setDeclinedReason] = useState({});
  const [selectedSubReason, setSelectedSubReason] = useState({});
  const [declinedNotes, setDeclinedNotes] = useState({});
  const [warnings, setWarnings] = useState({});
  const [referenceProgramsMap, setReferenceProgramsMap] = useState({});

  useEffect(() => {
    const loadRefData = async () => {
      try {
        const data = await fetchAssessmentReferenceData();
        setReferenceData(data);
        if (data?.programs && Array.isArray(data.programs)) {
          const pMap = {};
          data.programs.forEach((p) => {
            const code = p.programCode || p.programId || p.id || p.program;
            const name = p.displayName || p.name || p.programName || code;
            if (code) {
              const cleaned = cleanProgramId(code);
              const mapped = mapLocalProgramId(code);
              pMap[code] = name;
              pMap[cleaned] = name;
              pMap[mapped] = name;
              pMap[String(code).toUpperCase()] = name;
            }
          });
          setReferenceProgramsMap(pMap);
        }
      } catch (err) {
        console.error(
          "Failed to fetch assessment reference data in ClinicalNotePage:",
          err,
        );
      }

      try {
        const groups = await fetchAssessmentGroups();
        if (Array.isArray(groups)) {
          const programGroups = groups.filter(
            (g) =>
              String(g.groupType || "").toUpperCase() === "PROGRAM_SPECIFIC" &&
              g.status !== "INACTIVE",
          );
          if (programGroups.length > 0) {
            setAvailableProgramGroups(programGroups);
          }
          groups.forEach((g) => {
            const pid = String(g.programId || "").toUpperCase();
            const gType = String(g.groupType || "").toUpperCase();
            const gName = String(g.groupName || "");
            const nameLower = gName.toLowerCase();

            if (
              pid.includes("LIFESTYLE") ||
              gType === "LIFESTYLE" ||
              nameLower.includes("lifestyle")
            ) {
              if (gName) setLifestyleGroupName(gName);
            } else if (
              pid.includes("CARE_PLAN") ||
              gType === "CARE_PLAN" ||
              nameLower.includes("care plan") ||
              nameLower.includes("careplan")
            ) {
              if (gName) setCarePlanGroupName(gName);
            } else if (
              pid === "OTHER" ||
              pid === "GENERAL" ||
              pid === "GLOBAL" ||
              gType === "GENERAL"
            ) {
              if (gName) setGeneralGroupName(gName);
            }
          });
        }
      } catch (err) {
        console.error(
          "Failed to fetch assessment groups in ClinicalNotePage:",
          err,
        );
      } finally {
        setIsGroupNamesLoading(false);
      }
    };
    void loadRefData();
  }, []);

  // Loading and Error States for Initialization
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [sectionError, setSectionError] = useState(null);
  const [typeConflictModal, setTypeConflictModal] = useState(null);
  const [modalAssessmentTypeOverride, setModalAssessmentTypeOverride] =
    useState(null);
  const [isConflictProceedLoading, setIsConflictProceedLoading] =
    useState(false);
  const initializedRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const newlyAddedProgramIdRef = useRef(null);

  // Live timer interval: increments elapsedTime by 1 second every second when session is active
  useEffect(() => {
    if (readOnly || loading) return undefined;

    const timer = setInterval(() => {
      setElapsedTime((prev) => {
        const next = prev + 1;
        elapsedTimeRef.current = next;
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [readOnly, loading]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const isSubmittedRef = useRef(false);
  const bypassWarningRef = useRef(false);
  const pendingNavigationActionRef = useRef(null);
  const pageContainerRef = useRef(null);

  const handleRequestClose = useCallback(() => {
    if (readOnly || noteCompletionTime || isSubmittedRef.current) {
      onBack?.();
      return;
    }
    pendingNavigationActionRef.current = () => {
      bypassWarningRef.current = true;
      onBack?.();
    };
    setShowDiscardWarning(true);
  }, [readOnly, noteCompletionTime, onBack]);

  const handleRequestCloseWithAction = useCallback(
    (action) => {
      if (readOnly || noteCompletionTime || isSubmittedRef.current) {
        action?.();
        return;
      }
      pendingNavigationActionRef.current = () => {
        bypassWarningRef.current = true;
        action?.();
      };
      setShowDiscardWarning(true);
    },
    [readOnly, noteCompletionTime],
  );

  useImperativeHandle(
    ref,
    () => ({
      requestClose: handleRequestClose,
      requestCloseWithAction: handleRequestCloseWithAction,
      saveDraft: (opts) => handleSaveDraftRef.current?.(opts),
    }),
    [handleRequestClose, handleRequestCloseWithAction],
  );

  // Warn user on tab close / reload
  useEffect(() => {
    if (readOnly || noteCompletionTime) return undefined;

    const handleBeforeUnload = (e) => {
      if (bypassWarningRef.current || isSubmittedRef.current) return undefined;
      e.preventDefault();
      e.returnValue =
        "You have unsaved changes in this assessment. Please save your changes as a draft before leaving.";
      return "You have unsaved changes in this assessment. Please save your changes as a draft before leaving.";
    };

    globalThis.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      globalThis.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [readOnly, noteCompletionTime]);

  // Intercept keyboard reload shortcuts (Cmd+R, Ctrl+R, F5) to display the custom discard warning modal
  useEffect(() => {
    if (readOnly || noteCompletionTime) return undefined;

    const handleKeyDown = (e) => {
      if (bypassWarningRef.current || isSubmittedRef.current) return;

      const isReload =
        ((e.metaKey || e.ctrlKey) && (e.key === "r" || e.key === "R")) ||
        e.key === "F5";

      if (isReload) {
        e.preventDefault();
        e.stopPropagation();
        pendingNavigationActionRef.current = () => {
          bypassWarningRef.current = true;
          globalThis.location.reload();
        };
        setShowDiscardWarning(true);
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown, true);
    return () => {
      globalThis.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [readOnly, noteCompletionTime]);

  // Warn user on browser back / forward button
  useEffect(() => {
    if (readOnly || noteCompletionTime) return undefined;

    globalThis.history.pushState({ clinicalAssessmentActive: true }, "");

    const handlePopState = () => {
      if (bypassWarningRef.current || isSubmittedRef.current) return;
      globalThis.history.pushState({ clinicalAssessmentActive: true }, "");
      pendingNavigationActionRef.current = () => {
        bypassWarningRef.current = true;
        globalThis.history.back();
      };
      setShowDiscardWarning(true);
    };

    globalThis.addEventListener("popstate", handlePopState);
    return () => {
      globalThis.removeEventListener("popstate", handlePopState);
    };
  }, [readOnly, noteCompletionTime]);

  // Intercept navigation clicks outside the assessment (sidebar, top navigation, breadcrumbs, tabs)
  useEffect(() => {
    if (readOnly || noteCompletionTime) return undefined;

    const handleDocumentClick = (e) => {
      if (bypassWarningRef.current || isSubmittedRef.current) return;

      // 1. If click is inside the assessment page container, allow it completely
      if (pageContainerRef.current?.contains(e.target)) {
        return;
      }

      // 2. If click is outside the main React root (#root), it is a Portal (e.g. DatePicker, TimePicker, Popper, Menu, Modal)
      const rootEl = document.getElementById("root");
      if (rootEl && !rootEl.contains(e.target)) {
        return;
      }

      // 3. If click is inside any portal, popper, datepicker, timepicker, dialog, menu, tooltip, or dropdown
      if (
        isPortalOrPopperTarget(e.target) ||
        isPhiToggleTarget(e.target) ||
        e.target.closest(".breadcrumb-item")
      ) {
        return;
      }

      const interactiveEl = findInteractiveElement(e.target);

      if (interactiveEl) {
        e.preventDefault();
        e.stopPropagation();
        pendingNavigationActionRef.current = () => {
          bypassWarningRef.current = true;
          interactiveEl.click();
        };
        setShowDiscardWarning(true);
      }
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [readOnly, noteCompletionTime]);

  const filteredProgramMap = useMemo(() => {
    const map = {};
    const seenMappedIds = new Set();
    const options = {
      removedProgramIds,
      availableProgramGroups,
      referenceProgramsMap,
      referenceData,
    };

    if (readOnly) {
      return buildReadOnlyFilteredMap({
        map,
        seenMappedIds,
        activePrograms,
        selectedOutcome,
        options,
      });
    }

    const hasActiveOrSession = Boolean(
      (activePrograms && activePrograms.length > 0) ||
      (addedPrograms && addedPrograms.length > 0) ||
      generalAssessmentId ||
      clinicalAssessmentId ||
      isDraftSession,
    );

    if (hasActiveOrSession) {
      return buildActiveSessionFilteredMap({
        map,
        seenMappedIds,
        activePrograms,
        addedPrograms,
        options,
      });
    }

    return buildFallbackFilteredMap({
      map,
      seenMappedIds,
      selectedProgram,
      memberClinicalPrograms,
      programMap,
      options,
    });
  }, [
    readOnly,
    selectedOutcome,
    selectedProgram,
    memberClinicalPrograms,
    programMap,
    activePrograms,
    addedPrograms,
    removedProgramIds,
    referenceProgramsMap,
    availableProgramGroups,
    generalAssessmentId,
    clinicalAssessmentId,
    isDraftSession,
    referenceData,
  ]);

  useEffect(() => {
    if (readOnly) return;
    if (filteredProgramMap && Object.keys(filteredProgramMap).length > 0) {
      const enrolledObj = referenceData?.outcomes?.find((o) =>
        isEnrolledOutcome(o.outcomeName || o.displayName, referenceData),
      );
      const enrolledVal = enrolledObj?.outcomeName || "ENROLLED";

      setSelectedOutcome((prev) => {
        const next = { ...prev };
        let updated = false;
        Object.keys(filteredProgramMap).forEach((key) => {
          const canonicalId = mapLocalProgramId(key);
          if (!next[key] && !next[canonicalId]) {
            next[key] = enrolledVal;
            next[canonicalId] = enrolledVal;
            updated = true;
          }
        });
        return updated ? next : prev;
      });
    }
  }, [filteredProgramMap, referenceData, readOnly]);

  const getOutreachProgramsList = useCallback(() => {
    const list = [];
    const seenKeys = new Set();
    const seenNames = new Set();
    Object.entries(filteredProgramMap || {}).forEach(([id, name]) => {
      let normKey = String(id).toUpperCase().trim();
      if (
        normKey.includes("BIOSIMILAR") ||
        String(name || "")
          .toUpperCase()
          .includes("BIOSIMILAR")
      ) {
        return;
      }
      const cleanKey = cleanProgramId(normKey);
      const normKeyClean = normalizeProgramId(normKey);
      const normName = normalizeProgramId(name);

      if (shouldSkipOutreachKey(normKey, name, normName)) {
        return;
      }

      if (readOnly) {
        const outcome = getOutcomeForProgram({
          id: normKey,
          cleanId: cleanKey,
          name,
        });
        if (!outcome) {
          return;
        }
      }

      if (
        !seenKeys.has(normKey) &&
        !seenKeys.has(cleanKey) &&
        !seenKeys.has(normKeyClean) &&
        (!normName || !seenNames.has(normName))
      ) {
        seenKeys.add(normKey);
        seenKeys.add(cleanKey);
        seenKeys.add(normKeyClean);
        if (normName) seenNames.add(normName);

        const item = buildOutreachProgramEntry(
          normKey,
          cleanKey,
          normKeyClean,
          name,
          {
            activePrograms,
            addedPrograms,
            selectedProgram,
            memberClinicalPrograms,
            referenceData,
            getOutcomeForProgram,
          },
        );
        list.push(item);
      }
    });

    return list;
  }, [
    filteredProgramMap,
    readOnly,
    getOutcomeForProgram,
    activePrograms,
    addedPrograms,
    selectedProgram,
    memberClinicalPrograms,
    referenceData,
  ]);

  const hasEnrolledProgram = useMemo(() => {
    const list = getOutreachProgramsList();
    if (list.length === 0) return false;

    return list.some((prog) => {
      const outcome = getOutcomeForProgram(prog);
      if (!outcome) return false;
      return (
        isEnrolledOutcome(outcome, referenceData) ||
        isProgramCompletionOutcome(outcome, referenceData)
      );
    });
  }, [getOutreachProgramsList, getOutcomeForProgram, referenceData]);

  const allProgramsDeclinedOrUnableToReach = useMemo(() => {
    const list = getOutreachProgramsList();
    if (list.length === 0) return false;

    return list.every((prog) => {
      const outcome = getOutcomeForProgram(prog);
      return (
        !!outcome &&
        isDeclinedOrUnableToReach(outcome, referenceData) &&
        !isProgramCompletionOutcome(outcome, referenceData)
      );
    });
  }, [getOutreachProgramsList, getOutcomeForProgram, referenceData]);

  const isOnlyFormlessAvailable = useMemo(() => {
    const list = getOutreachProgramsList();
    if (list.length === 0) return false;
    return list.every((prog) => checkIsFormless(prog, referenceData));
  }, [getOutreachProgramsList, referenceData]);

  const isOnlyFormlessInitial = useMemo(() => {
    if (Array.isArray(selectedProgram) && selectedProgram.length > 0) {
      return selectedProgram.every((p) => checkIsFormless(p, referenceData));
    }
    if (selectedProgram && typeof selectedProgram === "object") {
      return checkIsFormless(selectedProgram, referenceData);
    }
    return isOnlyFormlessAvailable;
  }, [selectedProgram, isOnlyFormlessAvailable, referenceData]);

  const hasAddedFormProgram = useMemo(() => {
    const list = [...(activePrograms || []), ...(addedPrograms || [])];
    return list.some((p) => {
      const id = String(p.programId || p.id || p.cleanId || "").toUpperCase();
      const name = String(
        p.name || p.displayName || p.groupName || "",
      ).toUpperCase();
      const isRemoved =
        removedProgramIds.has(id) ||
        removedProgramIds.has(cleanProgramId(id).toUpperCase()) ||
        removedProgramIds.has(name);
      if (isRemoved) return false;
      return (
        !checkIsFormless(p, referenceData) &&
        id !== "GENERAL" &&
        id !== "LIFESTYLE" &&
        id !== "CARE_PLAN" &&
        id !== "OTHER" &&
        id !== "METADATA"
      );
    });
  }, [activePrograms, addedPrograms, removedProgramIds, referenceData]);

  const showAssessmentTypeSelect = useMemo(() => {
    return (
      (isOnlyFormlessInitial || isOnlyFormlessAvailable) &&
      (hasAddedFormProgram || Boolean(generalAssessmentId)) &&
      !clinicalAssessmentId &&
      !readOnly
    );
  }, [
    isOnlyFormlessInitial,
    isOnlyFormlessAvailable,
    hasAddedFormProgram,
    generalAssessmentId,
    clinicalAssessmentId,
    readOnly,
  ]);

  const hasOtherEnrolledProgram = useMemo(() => {
    const list = getOutreachProgramsList();
    return list.some((prog) => {
      if (checkIsFormless(prog, referenceData)) return false;
      const outcome = getOutcomeForProgram(prog);
      return (
        !!outcome &&
        (isEnrolledOutcome(outcome, referenceData) ||
          isProgramCompletionOutcome(outcome, referenceData))
      );
    });
  }, [getOutreachProgramsList, getOutcomeForProgram, referenceData]);

  const isStep0DirectSubmit = useMemo(() => {
    if (allProgramsDeclinedOrUnableToReach || !hasEnrolledProgram) return true;
    if (isOnlyFormlessAvailable) return true;
    if (!hasOtherEnrolledProgram) return true;
    return false;
  }, [
    allProgramsDeclinedOrUnableToReach,
    hasEnrolledProgram,
    isOnlyFormlessAvailable,
    hasOtherEnrolledProgram,
  ]);

  const getCombinedPrograms = useCallback(
    (overrideActive = null, overrideAdded = null, overrideRemoved = null) => {
      const activeList = overrideActive || activePrograms || [];
      const addedList = overrideAdded || addedPrograms || [];
      const removedSet = overrideRemoved || removedProgramIds || new Set();
      const combinedMap = new Map();

      const mergeOpts = {
        availableProgramGroups,
        removedSet,
        referenceData,
        activeList,
        addedList,
        selectedProgram,
        memberClinicalPrograms,
        getOutcomeForProgram,
      };

      if (
        (!generalAssessmentId &&
          !clinicalAssessmentId &&
          activeList.length === 0) ||
        (isStep0DirectSubmit && activeList.length === 0)
      ) {
        const outreachList = getOutreachProgramsList();
        outreachList.forEach((p) =>
          mergeSingleProgramItem(p, combinedMap, mergeOpts),
        );
      }

      (activeList || []).forEach((p) =>
        mergeSingleProgramItem(p, combinedMap, mergeOpts),
      );
      (addedList || []).forEach((p) =>
        mergeSingleProgramItem(p, combinedMap, mergeOpts),
      );

      return Array.from(combinedMap.values()).filter(
        (p) => !isProgramItemRemoved(p, availableProgramGroups, removedSet),
      );
    },
    [
      getOutreachProgramsList,
      getOutcomeForProgram,
      activePrograms,
      addedPrograms,
      removedProgramIds,
      selectedProgram,
      memberClinicalPrograms,
      availableProgramGroups,
      referenceData,
      generalAssessmentId,
      clinicalAssessmentId,
      isStep0DirectSubmit,
    ],
  );

  const initialModalAssessmentTypes = useMemo(() => {
    const map = {};
    const addEntry = (id, type) => {
      if (!id || !type || type === "N/A") return;
      const clean = cleanProgramId(id).toUpperCase();
      const norm = normalizeProgramId(id);
      const upper = String(id).toUpperCase().trim();
      const upperType = String(type).toUpperCase().trim();
      if (clean) map[clean] = upperType;
      if (norm) map[norm] = upperType;
      if (upper) map[upper] = upperType;
    };

    // 1. Baseline from memberClinicalPrograms (fallback)
    if (Array.isArray(memberClinicalPrograms)) {
      memberClinicalPrograms.forEach((mp) => {
        if (!mp) return;
        const type = mp.nextAssessmentType || mp.assessmentType || mp.formType;
        const id = mp.programId || mp.id || mp.cleanId || mp.name;
        addEntry(id, type);
        if (mp.name) addEntry(mp.name, type);
        if (mp.programName) addEntry(mp.programName, type);
        if (mp.displayName) addEntry(mp.displayName, type);
        if (mp.programDisplayName) addEntry(mp.programDisplayName, type);
      });
    }

    // 2. Initial assessment data programs
    if (
      initialAssessmentData?.programs &&
      Array.isArray(initialAssessmentData.programs)
    ) {
      initialAssessmentData.programs.forEach((p) => {
        if (!p) return;
        const type = p.formType || p.assessmentType;
        const id = p.programId || p.id || p.name;
        addEntry(id, type);
        if (p.name) addEntry(p.name, type);
        if (p.programName) addEntry(p.programName, type);
      });
    }

    // 3. User's explicit modal selection from selectedProgram (HIGHEST PRIORITY - overwrites baseline)
    if (Array.isArray(selectedProgram)) {
      selectedProgram.forEach((p) => {
        if (!p) return;
        const type = p.formType || p.assessmentType || p.nextAssessmentType;
        const id = p.programId || p.id || p.cleanId || p.name;
        addEntry(id, type);
        if (p.name) addEntry(p.name, type);
        if (p.programName) addEntry(p.programName, type);
        if (p.displayName) addEntry(p.displayName, type);
        if (p.programDisplayName) addEntry(p.programDisplayName, type);
      });
    } else if (selectedProgram && typeof selectedProgram === "object") {
      const type =
        selectedProgram.formType ||
        selectedProgram.assessmentType ||
        selectedProgram.nextAssessmentType;
      const id =
        selectedProgram.programId || selectedProgram.id || selectedProgram.name;
      addEntry(id, type);
      if (selectedProgram.name) addEntry(selectedProgram.name, type);
      if (selectedProgram.programName)
        addEntry(selectedProgram.programName, type);
    }

    if (modalAssessmentTypeOverride) {
      Object.keys(map).forEach((k) => {
        if (map[k] !== "DISCHARGE") {
          map[k] = modalAssessmentTypeOverride;
        }
      });
    }

    return map;
  }, [
    selectedProgram,
    memberClinicalPrograms,
    initialAssessmentData,
    modalAssessmentTypeOverride,
  ]);

  const getProgramDefaultAssessmentType = useCallback(
    (programId, programName) => {
      const clean = cleanProgramId(programId || programName).toUpperCase();
      const norm = normalizeProgramId(programId || programName);
      const cleanName = cleanProgramId(programName || "").toUpperCase();
      const upper = String(programId || "")
        .toUpperCase()
        .trim();
      const upperName = String(programName || "")
        .toUpperCase()
        .trim();
      const normName = normalizeProgramId(programName);

      const found =
        initialModalAssessmentTypes[clean] ||
        (cleanName && initialModalAssessmentTypes[cleanName]) ||
        initialModalAssessmentTypes[norm] ||
        initialModalAssessmentTypes[upper] ||
        (upperName && initialModalAssessmentTypes[upperName]) ||
        (normName && initialModalAssessmentTypes[normName]);

      if (found) return found;

      const activeProg = (activePrograms || []).find((p) => {
        const pid = cleanProgramId(p.programId || p.id).toUpperCase();
        return (
          pid === clean ||
          (cleanName && pid === cleanName) ||
          normalizeProgramId(p.programId || p.id) === norm ||
          normalizeProgramId(p.name || p.displayName) === norm
        );
      });
      if (activeProg?.initialFormType) return activeProg.initialFormType;
      if (activeProg?.formType) return activeProg.formType;
      if (activeProg?.assessmentType) return activeProg.assessmentType;

      return (currentAssessmentType || "INITIAL").toUpperCase();
    },
    [initialModalAssessmentTypes, activePrograms, currentAssessmentType],
  );

  const allCombinedActiveProgramsList = useMemo(() => {
    const list = [];
    const seen = new Set();

    const isRemoved = (p) => {
      const pid = String(p.programId || p.id || "")
        .toUpperCase()
        .trim();
      const pclean = cleanProgramId(pid).toUpperCase().trim();
      const pnorm = normalizeProgramId(pid);
      const pname = String(p.name || p.groupName || "")
        .toUpperCase()
        .trim();
      const pnamenorm = normalizeProgramId(pname);
      return (
        removedProgramIds.has(pid) ||
        removedProgramIds.has(pclean) ||
        (pnorm && removedProgramIds.has(pnorm)) ||
        (pname && removedProgramIds.has(pname)) ||
        (pnamenorm && removedProgramIds.has(pnamenorm))
      );
    };

    [...activePrograms, ...addedPrograms].forEach((ap) => {
      if (!ap || isRemoved(ap)) return;
      const progId = ap.programId || ap.id;
      const progName =
        ap.programDisplayName ||
        ap.groupName ||
        ap.name ||
        ap.displayName ||
        progId;
      const mapped = mapLocalProgramId(progId || progName);
      const isBio =
        mapped === "BIOSIMILARS" ||
        String(progId).toUpperCase().includes("BIOSIMILAR") ||
        String(progName).toUpperCase().includes("BIOSIMILAR");
      if (isBio) return;

      const normId = cleanProgramId(progId).toUpperCase();
      if (!seen.has(normId)) {
        seen.add(normId);
        const defaultModalType =
          ap.initialFormType ||
          ap.formType ||
          ap.assessmentType ||
          getProgramDefaultAssessmentType(progId, progName);
        const resolvedProgType = resolveProgramFormType(ap, defaultModalType);
        list.push({
          ...ap,
          id: progId,
          name:
            ap.programDisplayName ||
            ap.groupName ||
            ap.name ||
            ap.displayName ||
            progId,
          programDisplayName: ap.programDisplayName,
          programId: progId,
          formType: resolvedProgType,
          assessmentType: resolvedProgType,
          initialFormType: defaultModalType,
          defaultAssessmentType: defaultModalType,
          manuallyAdded: Boolean(ap.manuallyAdded),
          isManual: Boolean(ap.manuallyAdded || ap.isManual),
          isFormless: Boolean(ap.isFormless),
        });
      }
    });
    return list;
  }, [
    activePrograms,
    addedPrograms,
    removedProgramIds,
    getProgramDefaultAssessmentType,
  ]);

  const combinedActiveProgramsList = useMemo(() => {
    return allCombinedActiveProgramsList
      .filter((ap) => {
        const outcome = getOutcomeForProgram(ap);
        const isEnrolled = isEnrolledOutcome(outcome, referenceData);
        const isCompletion = isProgramCompletionOutcome(outcome, referenceData);
        return isEnrolled || isCompletion;
      })
      .map((ap) => {
        const outcome = getOutcomeForProgram(ap);
        const isCompletion = isProgramCompletionOutcome(outcome, referenceData);
        const resolvedFormType = isCompletion
          ? "DISCHARGE"
          : ap.formType || ap.assessmentType;
        return {
          ...ap,
          formType: resolvedFormType,
          assessmentType: resolvedFormType,
        };
      });
  }, [allCombinedActiveProgramsList, getOutcomeForProgram, referenceData]);

  const mapActiveProgramWithGroup = useCallback(
    (ap) => {
      const mapped = mapLocalProgramId(ap.name || ap.id);
      const matchedGroup = (availableProgramGroups || []).find((g) => {
        const gProgId = String(g.programId || g.id || "")
          .toUpperCase()
          .trim();
        const gCleanId = cleanProgramId(gProgId);
        const gCanonicalId = getCanonicalProgramId(
          g.programId,
          g.groupName,
          availableProgramGroups,
        );
        const apId = String(ap.id || "")
          .toUpperCase()
          .trim();
        const apClean = cleanProgramId(apId);
        return (
          gProgId === apId ||
          gCleanId === apClean ||
          gCanonicalId === apId ||
          gCanonicalId === mapped ||
          normalizeProgramId(g.groupName) ===
            normalizeProgramId(ap.name || ap.id) ||
          normalizeProgramId(g.programDisplayName) ===
            normalizeProgramId(ap.name || ap.id)
        );
      });
      const groupDisplayName =
        matchedGroup?.programDisplayName || matchedGroup?.groupName;
      const displayName =
        ap.programDisplayName ||
        groupDisplayName ||
        ap.name ||
        referenceProgramsMap[ap.id] ||
        referenceProgramsMap[ap.name] ||
        referenceProgramsMap[mapped] ||
        programMap?.[ap.id] ||
        programMap?.[mapped] ||
        PROGRAM_DISPLAY_NAMES[mapped] ||
        ap.name;
      return { ...ap, name: displayName };
    },
    [availableProgramGroups, referenceProgramsMap, programMap],
  );

  const displayActivePrograms = useMemo(() => {
    return combinedActiveProgramsList.map(mapActiveProgramWithGroup);
  }, [combinedActiveProgramsList, mapActiveProgramWithGroup]);

  const allDisplayActivePrograms = useMemo(() => {
    return allCombinedActiveProgramsList.map(mapActiveProgramWithGroup);
  }, [allCombinedActiveProgramsList, mapActiveProgramWithGroup]);

  useEffect(() => {
    if (displayActivePrograms.length > 0) {
      const isValidTab = displayActivePrograms.some(
        (p) =>
          p.id === activeProgramTab ||
          cleanProgramId(p.id) === cleanProgramId(activeProgramTab),
      );
      if (!isValidTab) {
        setActiveProgramTab(displayActivePrograms[0].id);
      }
    }
  }, [displayActivePrograms, activeProgramTab]);

  const getEnrolledProgramList = useCallback(() => {
    const enrolledProgramList = [];
    const enrolledActivePrograms = [];
    const seenIds = new Set();

    const keysToCheck = new Map();
    if (filteredProgramMap && Object.keys(filteredProgramMap).length > 0) {
      Object.entries(filteredProgramMap).forEach(([k, name]) => {
        if (Number.isNaN(Number(k))) {
          keysToCheck.set(k, name);
        }
      });
    }
    if (displayActivePrograms) {
      displayActivePrograms.forEach((p) => {
        const pId =
          p.programId || (Number.isNaN(Number(p.id)) ? p.id : null) || p.name;
        if (pId) keysToCheck.set(pId, p.name || p.groupName || pId);
      });
    }
    if (addedPrograms) {
      addedPrograms.forEach((p) => {
        const pId =
          p.programId ||
          (Number.isNaN(Number(p.id)) ? p.id : null) ||
          p.groupName;
        if (pId)
          keysToCheck.set(pId, p.groupName || p.name || p.displayName || pId);
      });
    }

    keysToCheck.forEach((pName, rawPId) => {
      processEnrolledCandidate(pName, rawPId, {
        availableProgramGroups,
        getOutcomeForProgram,
        referenceData,
        activePrograms: displayActivePrograms || activePrograms,
        addedPrograms,
        selectedProgram,
        memberClinicalPrograms,
        seenIds,
        enrolledProgramList,
        enrolledActivePrograms,
        currentAssessmentType,
      });
    });

    return { enrolledProgramList, enrolledActivePrograms };
  }, [
    filteredProgramMap,
    displayActivePrograms,
    addedPrograms,
    activePrograms,
    selectedProgram,
    memberClinicalPrograms,
    availableProgramGroups,
    getOutcomeForProgram,
    referenceData,
    currentAssessmentType,
  ]);

  useEffect(() => {
    if (displayActivePrograms.length > 0) {
      displayActivePrograms.forEach((p) => {
        ensureProgramSchemaLoaded(p);
      });
    }
  }, [displayActivePrograms, ensureProgramSchemaLoaded]);

  const handleSetActivePrograms = async (newProgramsOrUpdater) => {
    let nextPrograms;
    if (typeof newProgramsOrUpdater === "function") {
      nextPrograms = newProgramsOrUpdater(displayActivePrograms);
    } else {
      nextPrograms = newProgramsOrUpdater;
    }

    const newlyAdded = (nextPrograms || []).filter(
      (np) =>
        !displayActivePrograms.some((ap) => {
          const npId = String(np.programId || np.id || "")
            .toUpperCase()
            .trim();
          const apId = String(ap.programId || ap.id || "")
            .toUpperCase()
            .trim();
          return npId === apId || cleanProgramId(npId) === cleanProgramId(apId);
        }),
    );

    const newlyRemoved = displayActivePrograms.filter(
      (ap) =>
        !(nextPrograms || []).some((np) => {
          const npId = String(np.programId || np.id || "")
            .toUpperCase()
            .trim();
          const apId = String(ap.programId || ap.id || "")
            .toUpperCase()
            .trim();
          return npId === apId || cleanProgramId(npId) === cleanProgramId(apId);
        }),
    );

    if (newlyRemoved.length > 0) {
      newlyRemoved.forEach((rp) => {
        handleRemoveProgram(rp);
      });
    }

    if (newlyAdded.length > 0) {
      newlyAdded.forEach((prog) => {
        handleProgramAdd(prog, { source: "PROGRAM_SPECIFIC" });
      });
    }
  };

  const availablePrograms = useMemo(() => {
    const allAvailablePrograms = [];
    if (programMap) {
      Object.entries(programMap).forEach(([id, name]) => {
        if (isNonClinicalProgram(id, name)) {
          return;
        }
        const cleanedId = cleanProgramId(id);
        const mappedId = mapLocalProgramId(name || cleanedId);
        const displayName =
          referenceProgramsMap[id] ||
          referenceProgramsMap[name] ||
          referenceProgramsMap[mappedId] ||
          name ||
          PROGRAM_DISPLAY_NAMES[mappedId];
        allAvailablePrograms.push({
          id: mappedId,
          name: displayName,
          mappedId: mappedId,
        });
      });
    }

    const seenAvailable = new Set();
    const dedupedAvailable = [];
    allAvailablePrograms.forEach((p) => {
      const norm = p.mappedId;
      if (!seenAvailable.has(norm)) {
        seenAvailable.add(norm);
        dedupedAvailable.push(p);
      }
    });

    displayActivePrograms.forEach((p) => {
      const norm = mapLocalProgramId(p.name || p.id);
      if (!seenAvailable.has(norm)) {
        seenAvailable.add(norm);
        dedupedAvailable.push({
          id: norm,
          name:
            p.name ||
            referenceProgramsMap[p.id] ||
            referenceProgramsMap[p.name] ||
            referenceProgramsMap[norm] ||
            PROGRAM_DISPLAY_NAMES[norm],
          mappedId: norm,
        });
      }
    });

    return dedupedAvailable;
  }, [programMap, displayActivePrograms, referenceProgramsMap]);

  const initializeAssessmentSession = async () => {
    setLoading(true);
    try {
      const res = await fetchInitialAssessmentSessionData({
        initialAssessmentData,
        batchId,
        clinicalAssessmentId,
        isBatch,
        readOnly,
        initialStartTime,
        setAvailableProgramGroups,
        setNoteStartTime,
      });

      console.log("Assessment session API response:", res);

      if (res?.id) {
        const dedupedProgs = await applyAssessmentSessionSuccessData({
          res,
          initialAssessmentData,
          isDraftSession,
          readOnly,
          initialStartTime,
          initialClinicalNotes,
          clinicalNotes,
          clinicalAssessmentId,
          programMap,
          selectedProgram,
          memberClinicalPrograms,
          currentAssessmentType,
          setCurrentAssessmentType,
          generalGroupName,
          lifestyleGroupName,
          carePlanGroupName,
          referenceData,
          setters: {
            setGeneralAssessmentId,
            setLifestyleAssessmentId,
            setCarePlanAssessmentId,
            setGeneralAssessmentVersion,
            setIsDraft,
            setWarnings,
            setNoteStartTime,
            setNoteCompletionTime,
            setElapsedTime,
            setClinicalNotes,
            setActivePrograms,
            setAddedPrograms,
            setGeneralPayloadKey,
            setLifestylePayloadKey,
            setCarePlanPayloadKey,
            setGeneralAssessmentSchema,
            setLifestyleAssessmentSchema,
            setCarePlanAssessmentSchema,
            setProgramAssessmentSchema,
            setGeneralFormPayload,
            setLifestyleFormPayload,
            setCarePlanFormPayload,
            setProgramFormPayload,
            setActiveStep,
            setVisitedForms,
            setActiveProgramTab,
            setSelectedOutcome,
            setSelectedSubReason,
            setDeclinedReason,
            setDeclinedNotes,
            setCertification,
            setAssigneeRnName,
            setCgPlusDischarge,
          },
          refs: {
            elapsedTimeRef,
            lastSavedSignatureRef,
            latestAssessmentVersionRef,
          },
        });
        loadTemplatesForVisit(
          dedupedProgs,
          res?.assessmentType || currentAssessmentType,
        );
      } else {
        const initialActive = resolveActivePrograms(
          res,
          programMap,
          selectedProgram,
          false,
          memberClinicalPrograms,
        );
        setActivePrograms(initialActive);
        const initialManual = initialActive.filter((p) =>
          Boolean(p.manuallyAdded),
        );
        if (initialManual.length > 0) {
          setAddedPrograms((prev) => mergeAddedPrograms(prev, initialManual));
        }
      }
    } catch (error_) {
      await handleFallbackMockSession({
        error_,
        assessmentType,
        selectedProgram,
        generalPayloadKey,
        lifestylePayloadKey,
        carePlanPayloadKey,
        setters: {
          setGeneralAssessmentId,
          setLifestyleAssessmentId,
          setCarePlanAssessmentId,
          setGeneralAssessmentVersion,
          setNoteStartTime,
          setNoteCompletionTime,
          setGeneralAssessmentSchema,
          setLifestyleAssessmentSchema,
          setCarePlanAssessmentSchema,
          setProgramAssessmentSchema,
          setActivePrograms,
        },
        lastSavedSignatureRef,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = `${memberData.memberId || memberData.id}_${JSON.stringify(selectedProgram)}_${assessmentType}_${clinicalAssessmentId}`;
    if (initializedRef.current === key) {
      return;
    }
    initializedRef.current = key;
    void initializeAssessmentSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memberData.memberId,
    memberData.id,
    selectedProgram,
    assessmentType,
    clinicalAssessmentId,
  ]);

  useEffect(() => {
    // Only schedule auto-save if session is initialized and not a preview or fallback-id
    if (
      !generalAssessmentId ||
      generalAssessmentId === "fallback-id" ||
      readOnly
    ) {
      return;
    }

    const interval = setInterval(() => {
      if (handleSaveDraftRef.current) {
        handleSaveDraftRef.current({ silent: true });
      }
    }, 120000); // 2 minutes in milliseconds

    return () => {
      clearInterval(interval);
    };
  }, [generalAssessmentId, readOnly]);

  const isProgramFormEmpty = () => {
    if (!programFormPayload || typeof programFormPayload !== "object")
      return true;

    const checkValue = (val) => {
      if (val === undefined || val === null || val === "") return true;
      if (Array.isArray(val)) {
        return val.every((item) => checkValue(item));
      }
      if (typeof val === "object") {
        if ("value" in val) {
          const isValEmpty = checkValue(val.value);
          const areBranchesEmpty = val.branches
            ? Object.keys(val.branches).every((bKey) => {
                const subQ = val.branches[bKey]?.subQuestion;
                return subQ ? checkValue(subQ.value) : true;
              })
            : true;
          return isValEmpty && areBranchesEmpty;
        }
        return Object.values(val).every(checkValue);
      }
      if (val === false) return true;
      return false;
    };
    return Object.values(programFormPayload).every((section) => {
      if (!section || typeof section !== "object") return true;
      return Object.values(section).every(checkValue);
    });
  };

  const isStepValid = () => {
    const isFieldVisible = (f) => isSchemaFieldVisible(f, selectedProgram);

    const stageKey = getStageKeyForStep(activeStep, hasProgramSpecific);

    if (stageKey === "OUTCOMES") {
      return validateOutcomesStep({
        programsToValidate: getOutreachProgramsList(),
        getOutcomeForProgram,
        selectedSubReason,
        declinedReason,
        showAssessmentTypeSelect,
        currentAssessmentType,
        isStep0DirectSubmit,
        readOnly,
        certification,
      });
    }

    const hasGeneralWarning = hasWarningForKeys(warnings, [
      "OTHER_GENERAL",
      "GENERAL",
      "general",
      "OTHER",
      "other",
    ]);

    const hasLifestyleWarning = hasWarningForKeys(warnings, [
      "LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT",
      "LIFESTYLE",
      "lifestyle",
    ]);

    const hasCarePlanWarning = hasWarningForKeys(warnings, [
      "CARE_PLAN_COORDINATION_COMPLETION",
      "CARE_PLAN",
      "care_plan",
      "CAREPLAN",
      "careplan",
    ]);

    const checkProgramHasWarning = (prog) =>
      programHasWarning(prog, warnings, availableProgramGroups);

    if (stageKey === "CORE") {
      if (hasGeneralWarning || !hasSchemaSections(generalAssessmentSchema)) {
        return false;
      }
      return validateSchemaForm(
        generalAssessmentSchema,
        generalFormPayload,
        isFieldVisible,
      );
    }

    if (stageKey === "PROGRAM") {
      return validateProgramStage({
        displayActivePrograms,
        referenceData,
        checkProgramHasWarning,
        programAssessmentSchema,
        programFormPayload,
        isFieldVisible,
      });
    }

    if (stageKey === "LIFESTYLE") {
      if (
        hasLifestyleWarning ||
        !hasSchemaSections(lifestyleAssessmentSchema)
      ) {
        return false;
      }
      return validateSchemaForm(
        lifestyleAssessmentSchema,
        lifestyleFormPayload,
        isFieldVisible,
      );
    }

    if (stageKey === "CARE_PLAN") {
      return validateCarePlanStage({
        hasGeneralWarning,
        hasLifestyleWarning,
        hasCarePlanWarning,
        displayActivePrograms,
        referenceData,
        checkProgramHasWarning,
        carePlanAssessmentSchema,
        carePlanFormPayload,
        isFieldVisible,
        readOnly,
        certification,
      });
    }

    return true;
  };

  const programSpecificRef = useRef(null);

  const handleNextStep = async () => {
    if (isNextLoading) return;
    setIsNextLoading(true);
    setSectionError(null);
    try {
      if (activeStep === 0) {
        await handleProceedFromStep0Helper({
          isStep0DirectSubmit,
          readOnly,
          generalAssessmentId,
          clinicalAssessmentId,
          memberData,
          hasEnrolledProgram,
          getEnrolledProgramList,
          referenceData,
          selectedProgram,
          currentAssessmentType,
          setActivePrograms,
          setCurrentAssessmentType,
          setGeneralAssessmentId,
          setLifestyleAssessmentId,
          setCarePlanAssessmentId,
          setGeneralAssessmentVersion,
          setGeneralPayloadKey,
          setLifestylePayloadKey,
          setCarePlanPayloadKey,
          setGeneralAssessmentSchema,
          setLifestyleAssessmentSchema,
          setCarePlanAssessmentSchema,
          setProgramAssessmentSchema,
          memberClinicalPrograms,
          clinicalNotePrograms,
          handleFinalSubmit,
          setActiveStep,
          setAssigneeRnName,
          setCgPlusDischarge,
          handleSaveDraft,
          loadTemplatesForVisit,
          setSectionError,
          setTypeConflictModal,
          newlyAddedProgramId: newlyAddedProgramIdRef.current,
        });
        return;
      }

      await handleAdvanceRegularStepHelper({
        activeStep,
        steps,
        hasProgramSpecific,
        programSpecificRef,
        displayActivePrograms,
        setActiveProgramTab,
        setActiveStep,
        readOnly,
        handleFinalSubmit,
        handleSaveDraft,
        setSectionError,
        setTypeConflictModal,
        newlyAddedProgramId: newlyAddedProgramIdRef.current,
      });
    } finally {
      setIsNextLoading(false);
    }
  };

  const handleConfirmTypeConflictProceed = async () => {
    if (!typeConflictModal?.requiredType) return;
    setIsConflictProceedLoading(true);
    setSectionError(null);

    const reqType = typeConflictModal.requiredType.toUpperCase();
    const isStep0Early =
      (typeConflictModal?.sourceStep === 0 || activeStep === 0) &&
      isStep0DirectSubmit;

    const activeList = resolveTypeConflictActiveList({
      typeConflictModal,
      activePrograms,
      allDisplayActivePrograms,
      getOutreachProgramsList,
      selectedProgram,
      memberClinicalPrograms,
    });

    try {
      setModalAssessmentTypeOverride(reqType);

      const addedId =
        typeConflictModal.addedProgramId || newlyAddedProgramIdRef.current;
      const cleanAddedId = cleanProgramId(addedId || "").toUpperCase();
      const hasMultiplePrograms = (activeList || []).length > 1;

      const updatedActiveList = (activeList || []).map((p) =>
        mapProgramToRequiredType(p, cleanAddedId, hasMultiplePrograms, reqType),
      );
      if (updatedActiveList.length > 0) {
        setActivePrograms(updatedActiveList);
      }

      setAddedPrograms((prev) =>
        (prev || []).map((p) =>
          mapProgramToRequiredType(
            p,
            cleanAddedId,
            hasMultiplePrograms,
            reqType,
          ),
        ),
      );

      clearAssessmentFormPayloads(
        {
          setGeneralFormPayload,
          setLifestyleFormPayload,
          setCarePlanFormPayload,
          setProgramFormPayload,
        },
        loadedProgramSchemasRef,
      );

      const parsedRes = await loadTemplatesForVisit(updatedActiveList, reqType);

      const { payloadDelta, genKey, lifestyleKey, carePlanKey } =
        buildTypeConflictPayloadDelta({
          parsedRes,
          reqType,
          activeStep,
          steps,
          visitedForms,
          activeProgramTab,
          displayActivePrograms,
          selectedOutcome,
          selectedSubReason,
          declinedReason,
          declinedNotes,
          clinicalNotes,
          generalPayloadKey,
          lifestylePayloadKey,
          carePlanPayloadKey,
          hasProgramSpecific,
          updatedActiveList,
          referenceData,
          getOutcomeForProgram,
        });

      setGeneralPayloadKey(genKey);
      setLifestylePayloadKey(lifestyleKey);
      setCarePlanPayloadKey(carePlanKey);

      const currentSessionId = resolveTargetAssessmentId(
        generalAssessmentId,
        clinicalAssessmentId,
      );

      const outreachList =
        typeof getOutreachProgramsList === "function"
          ? getOutreachProgramsList()
          : null;
      const currentOutreach =
        Array.isArray(outreachList) && outreachList.length > 0
          ? outreachList
          : null;

      const combinedPrograms = resolveTypeConflictCombinedPrograms({
        isStep0Early,
        updatedActiveList,
        currentOutreach,
        addedPrograms,
        removedProgramIds,
        getCombinedPrograms,
      });

      const programsPayload = buildProgramsPayloadList(combinedPrograms, {
        availableProgramGroups,
        outcomesMap: selectedOutcome || {},
        referenceData,
        selectedSubReason,
        declinedReason,
        declinedNotes,
        activeList: updatedActiveList,
        addedList: addedPrograms || [],
        selectedProgram,
        memberClinicalPrograms,
        getOutcomeForProgram,
      });

      const effectiveVersion =
        [latestAssessmentVersionRef.current, generalAssessmentVersion].find(
          isPresent,
        ) ?? generalAssessmentVersion;

      const autosavePayload = {
        version: effectiveVersion,
        signatureConfirmed: certification,
        payloadDelta,
        programs: programsPayload,
        clinicalNote: clinicalNotes,
        elapsedTime: elapsedTimeRef.current,
        assessmentType: reqType,
      };

      const res = await executeAutosaveWithStaleRetry(
        currentSessionId,
        autosavePayload,
        latestAssessmentVersionRef,
        setGeneralAssessmentVersion,
      );

      applyTypeConflictAutosaveResponse({
        res,
        reqType,
        cleanAddedId,
        hasMultiplePrograms,
        updatedActiveList,
        filteredProgramMap,
        selectedProgram,
        latestAssessmentVersionRef,
        setGeneralAssessmentVersion,
        setCgPlusDischarge,
        setCurrentAssessmentType,
        setModalAssessmentTypeOverride,
        setAssigneeRnName,
        setClinicalNotes,
        setCertification,
        setActivePrograms,
        setAddedPrograms,
        setSelectedOutcome,
        setSelectedSubReason,
        setDeclinedReason,
        setDeclinedNotes,
      });

      lastSavedSignatureRef.current = {
        signatureConfirmed: certification,
        payloadDelta,
        programs: programsPayload,
        clinicalNote: clinicalNotes,
      };
      setCurrentAssessmentType(reqType);
      setIsDraft(true);
      const nextStep = resolveNextStepAfterConflict(
        typeConflictModal?.sourceStep,
        isStep0DirectSubmit,
      );
      setTypeConflictModal(null);
      setActiveStep(nextStep);
    } catch (err) {
      console.error("Failed to auto-save after changing assessment type:", err);
      const errorMsg = extractErrorMessage(err);
      setSectionError(errorMsg);
      setTypeConflictModal(null);
    } finally {
      setIsConflictProceedLoading(false);
    }
  };

  const currentTypeDisplay = formatTypeLabel(
    resolveSafeCurrentType(
      currentAssessmentType,
      typeConflictModal?.requiredType,
    ),
  );
  const requiredTypeDisplay = formatTypeLabel(
    typeConflictModal?.requiredType || "FOLLOW_UP",
  );
  const typeConflictModalQuestion = `Are you sure you want to change the assessment type from ${currentTypeDisplay} to ${requiredTypeDisplay}?`;
  const typeConflictModalWarning =
    "Changing the assessment type will clear all previously entered information in the affected sections and replace them with blank templates for the new assessment type.";

  const handlePrevStep = () => {
    setSectionError(null);
    if (activeStep <= 0) return;

    const currentStepKey = steps[activeStep];
    const isProgramStep = isProgramStepKey(
      hasProgramSpecific,
      activeStep,
      currentStepKey,
    );

    if (isProgramStep && programSpecificRef.current?.navigateToPrevProgram) {
      const movedSubTab = programSpecificRef.current.navigateToPrevProgram();
      if (movedSubTab) {
        return; // Stayed on Program Specific step, moved to previous program tab
      }
      // If navigateToPrevProgram returned false, it means all programs visited and user is at first program
      // Proceed to previous main step (Core Assessment)
      setActiveStep((prev) => Math.max(0, prev - 1));
      return;
    }

    // When moving backwards from Step 3 (Lifestyle) to Step 2 (Program Specific),
    // set the active tab to the last active program!
    if (hasProgramSpecific && activeStep === 3) {
      if (displayActivePrograms && displayActivePrograms.length > 0) {
        const lastProg = displayActivePrograms.at(-1);
        if (lastProg?.id) {
          setActiveProgramTab(lastProg.id);
        }
      }
    }

    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const isSavingRef = useRef(false);
  const lastSavedSignatureRef = useRef(null);
  const handleSaveDraftRef = useRef(null);

  const handleSaveDraft = async ({
    silent = false,
    throwOnError = false,
    updatedActivePrograms = null,
    updatedAddedPrograms = null,
    updatedRemovedIds = null,
    updatedOutcomes = null,
  } = {}) => {
    let currentSessionId = resolveTargetAssessmentId(
      generalAssessmentId,
      clinicalAssessmentId,
    );

    const activeList = updatedActivePrograms || activePrograms || [];
    const addedList = updatedAddedPrograms || addedPrograms || [];
    const removedSet = updatedRemovedIds || removedProgramIds || new Set();
    const outcomesMap = updatedOutcomes || selectedOutcome || {};

    if (!currentSessionId) {
      currentSessionId = await ensureSessionInitializedHelper({
        currentSessionId,
        readOnly,
        memberData,
        hasEnrolledProgram,
        getEnrolledProgramList,
        referenceData,
        selectedProgram,
        currentAssessmentType,
        setActivePrograms,
        setCurrentAssessmentType,
        setLoading,
        setGeneralAssessmentId,
        setLifestyleAssessmentId,
        setCarePlanAssessmentId,
        setGeneralAssessmentVersion,
        getOutreachProgramsList,
      });
    }
    if (!currentSessionId) return;

    const isSaveDraftDisabled =
      readOnly ||
      isSubmitting ||
      activeList.some((ap) => hasDraftProgramWarning(warnings, ap));

    if (isSaveDraftDisabled || isSavingRef.current) return;
    isSavingRef.current = true;

    let programsPayload = null;
    try {
      const combinedPrograms = getCombinedPrograms(
        activeList,
        addedList,
        removedSet,
      );

      programsPayload = buildProgramsPayloadList(combinedPrograms, {
        availableProgramGroups,
        outcomesMap,
        referenceData,
        selectedSubReason,
        declinedReason,
        declinedNotes,
        activeList,
        addedList,
        selectedProgram,
        memberClinicalPrograms,
        getOutcomeForProgram,
      });

      const payloadDelta = buildAutosavePayloadDelta({
        generalPayloadKey,
        generalFormPayload,
        lifestylePayloadKey,
        lifestyleFormPayload,
        carePlanPayloadKey,
        carePlanFormPayload,
        activeStep,
        steps,
        visitedForms,
        activeProgramTab,
        displayActivePrograms,
        outcomesMap,
        selectedSubReason,
        declinedReason,
        declinedNotes,
        clinicalNotes,
        hasProgramSpecific,
        activeList,
        referenceData,
        getOutcomeForProgram,
        programFormPayload,
      });

      const currentPayloadToCompare = {
        signatureConfirmed: certification,
        payloadDelta,
        programs: programsPayload,
        clinicalNote: clinicalNotes,
      };

      if (
        isPayloadEqual(currentPayloadToCompare, lastSavedSignatureRef.current)
      ) {
        return;
      }

      lastSavedSignatureRef.current = currentPayloadToCompare;

      const effectiveVersion =
        [latestAssessmentVersionRef.current, generalAssessmentVersion].find(
          isPresent,
        ) ?? generalAssessmentVersion;

      const payload = {
        version: effectiveVersion,
        signatureConfirmed: certification,
        payloadDelta,
        programs: programsPayload,
        clinicalNote: clinicalNotes,
        elapsedTime: elapsedTimeRef.current,
      };

      const res = await executeAutosaveWithStaleRetry(
        currentSessionId,
        payload,
        latestAssessmentVersionRef,
        setGeneralAssessmentVersion,
      );

      applyAutosaveResponse({
        res,
        latestAssessmentVersionRef,
        setGeneralAssessmentVersion,
        setCgPlusDischarge,
        setCurrentAssessmentType,
        setAssigneeRnName,
        setIsDraft,
        setSectionError,
      });
      return res;
    } catch (error_) {
      lastSavedSignatureRef.current = null;
      return handleAutosaveError({
        error_,
        throwOnError,
        silent,
        activeStep,
        newlyAddedProgramId: newlyAddedProgramIdRef.current,
        setTypeConflictModal,
        setSectionError,
        displayActivePrograms,
        activePrograms,
        programsPayload,
        getOutreachProgramsList,
      });
    } finally {
      isSavingRef.current = false;
    }
  };

  handleSaveDraftRef.current = handleSaveDraft;

  const handleProgramSubTabChange = useCallback(
    (newTab) => {
      setSectionError(null);
      if (!newTab || newTab === activeProgramTabRef.current) {
        return;
      }
      activeProgramTabRef.current = newTab;
      setActiveProgramTab(newTab);
      if (!readOnly && handleSaveDraftRef.current) {
        handleSaveDraftRef.current({ silent: true })?.catch?.(() => {});
      }
    },
    [readOnly],
  );

  const handleFinalSubmit = async (overrideVersion) => {
    if (readOnly || !certification) {
      return;
    }

    const isStep0EarlySubmit = activeStep === 0 && isStep0DirectSubmit;

    let targetAssessmentId = resolveTargetAssessmentId(
      generalAssessmentId,
      clinicalAssessmentId,
    );
    let targetAssessmentVersion =
      [overrideVersion, latestAssessmentVersionRef.current].find(isPresent) ??
      generalAssessmentVersion;

    ({ targetId: targetAssessmentId, targetVersion: targetAssessmentVersion } =
      await resolveSubmitTargetSession({
        targetAssessmentId,
        targetAssessmentVersion,
        readOnly,
        memberData,
        hasEnrolledProgram,
        getEnrolledProgramList,
        referenceData,
        getOutreachProgramsList,
        setActivePrograms,
        setLoading,
        setGeneralAssessmentId,
        setGeneralAssessmentVersion,
        latestAssessmentVersionRef,
      }));

    if (!targetAssessmentId) {
      console.error("No valid assessment ID found for submission.");
      return;
    }

    const combinedPrograms = isStep0EarlySubmit
      ? getOutreachProgramsList()
      : getCombinedPrograms();

    const programsPayload = buildProgramsPayloadList(combinedPrograms, {
      availableProgramGroups,
      outcomesMap: selectedOutcome,
      referenceData,
      selectedSubReason,
      declinedReason,
      declinedNotes,
      activeList: activePrograms,
      addedList: addedPrograms,
      selectedProgram,
      memberClinicalPrograms,
      getOutcomeForProgram,
    });

    setIsSubmitting(true);
    try {
      const isAllDeclinedOrUnableToReach =
        allProgramsDeclinedOrUnableToReach ||
        !hasEnrolledProgram ||
        isStep0EarlySubmit;

      if (isAllDeclinedOrUnableToReach) {
        const { shouldHalt, updatedVersion } =
          await handlePreSubmitDeclinedAutosave({
            targetAssessmentId,
            targetAssessmentVersion,
            generalAssessmentVersion,
            certification,
            elapsedTimeRef,
            generalPayloadKey,
            lifestylePayloadKey,
            carePlanPayloadKey,
            generalFormPayload,
            lifestyleFormPayload,
            carePlanFormPayload,
            activeProgramTab,
            displayActivePrograms,
            selectedOutcome,
            selectedSubReason,
            declinedReason,
            declinedNotes,
            clinicalNotes,
            programsPayload,
            setGeneralAssessmentVersion,
            setTypeConflictModal,
            setSectionError,
            latestAssessmentVersionRef,
          });

        if (shouldHalt) {
          return;
        }
        targetAssessmentVersion = updatedVersion;
      }

      const finalPayload = buildSubmitFinalPayload({
        generalPayloadKey,
        generalFormPayload,
        lifestylePayloadKey,
        lifestyleFormPayload,
        carePlanPayloadKey,
        carePlanFormPayload,
        hasProgramSpecific,
        isStep0EarlySubmit,
        activePrograms,
        selectedOutcome,
        referenceData,
        getOutcomeForProgram,
        programFormPayload,
      });

      const resolvedVersion =
        [
          overrideVersion,
          targetAssessmentVersion,
          latestAssessmentVersionRef.current,
        ].find(isPresent) ??
        resolveSubmitVersion(targetAssessmentVersion, generalAssessmentVersion);

      const payload = {
        version: resolvedVersion,
        signatureConfirmed: true,
        finalPayload,
        programs: programsPayload,
        clinicalNote: clinicalNotes,
        elapsedTime: elapsedTimeRef.current || 0,
      };

      console.log(
        "Submitting assessment payload via submit API:",
        targetAssessmentId,
        payload,
      );
      await submitAssessmentApi(targetAssessmentId, payload);
      isSubmittedRef.current = true;
      bypassWarningRef.current = true;
      setIsDraft(false);
      onSave(clinicalNotes);
    } catch (error_) {
      handleSubmitError({
        error_,
        setTypeConflictModal,
        setSectionError,
        displayActivePrograms,
        programsPayload,
        activePrograms,
        activeStep,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportPdf = () => {
    try {
      const activeProgramCandidates = (activePrograms || []).map((p) => ({
        id: p.id || p.programId,
        name: p.name || p.displayName || p.groupName || p.id,
        manuallyAdded: Boolean(p.manuallyAdded || p.isManual),
        isManual: Boolean(p.manuallyAdded || p.isManual),
        assessmentType: p.assessmentType || p.formType,
        formType: p.formType || p.assessmentType,
      }));

      const programsPayload = buildExportPdfProgramsPayload(
        filteredProgramMap || programMap || {},
        selectedOutcome,
        {
          referenceData,
          declinedNotes,
          declinedReason,
          selectedSubReason,
          activePrograms,
          addedPrograms,
          selectedProgram,
          memberClinicalPrograms,
          initialModalAssessmentTypes,
          currentAssessmentType,
          assessmentType,
        },
      );

      const resolvedAssigneeRn =
        assigneeRnName ||
        memberData?.assigneeRnName ||
        memberData?.assignee_rn_name ||
        memberData?.member?.assigneeRnName ||
        memberData?.assigneeName ||
        memberData?.assignee?.name ||
        "";
      const signeeName =
        resolvedAssigneeRn ||
        memberData?.submittedBy ||
        memberData?.submittedByName ||
        "";

      const reportData = compileAssessmentReportData({
        memberData: {
          ...memberData,
          assigneeRnName: resolvedAssigneeRn,
        },
        assessmentType: currentAssessmentType || assessmentType || "INITIAL",
        status: noteCompletionTime ? "Completed" : "Draft / In Progress",
        completionTime: noteCompletionTime,
        startTime: noteStartTime,
        duration: null,
        assigneeRnName: resolvedAssigneeRn,
        submittedBy: memberData?.submittedBy || signeeName || "",
        signeeName: signeeName || memberData?.submittedBy || "",
        signatureDate: noteCompletionTime || noteStartTime || null,
        signatureConfirmed: certification ?? true,
        programs: programsPayload,
        clinicalNotes: clinicalNotes || "",
        generalSchema: generalAssessmentSchema,
        generalPayload: generalFormPayload,
        generalTitle: generalGroupName || "General Assessment",
        programSchemas: programAssessmentSchema,
        programPayloads: programFormPayload,
        activePrograms: activeProgramCandidates,
        lifestyleSchema: lifestyleAssessmentSchema,
        lifestylePayload: lifestyleFormPayload,
        lifestyleTitle:
          lifestyleGroupName || "Lifestyle, Barriers and Functional Assessment",
        carePlanSchema: carePlanAssessmentSchema,
        carePlanPayload: carePlanFormPayload,
        carePlanTitle:
          carePlanGroupName || "Care Plan, Coordination and Completion",
        referenceData,
      });

      generateClinicalAssessmentPdf(reportData);
    } catch (error_) {
      console.error("Failed to generate clinical assessment PDF:", error_);
    }
  };

  const primaryColor = "#403C6F";
  const secondaryColor = "#7F7C9F";
  const borderColor = "#E8E8E9";
  const bgColor = "#F7F8FB";

  const buttonLabel = resolveNextButtonLabel(
    activeStep,
    isStep0DirectSubmit,
    isSubmitting,
    steps.length,
  );

  const nextLoadingLabel = resolveNextLoadingLabel(
    activeStep,
    isStep0DirectSubmit,
  );

  return (
    <Box
      ref={pageContainerRef}
      sx={{
        mt: 2,
        height: {
          xs: "calc(100vh - 220px)",
          sm: "calc(100vh - 180px)",
          md: "100%",
        },
        flex: { md: "1 1 0%" },
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${borderColor}`,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Scrollable Container Wrapper */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Header Section */}
          <Box sx={{ flexShrink: 0 }}>
            <Box sx={{ p: 2, pt: 1 }}>
              <MemberDetails
                opportunity={memberData}
                startTime={noteStartTime}
                completionTime={noteCompletionTime}
                readOnly={readOnly}
                isDraft={isDraft}
                elapsedTime={elapsedTime}
                isShowPhiEnabled={isShowPhiEnabled}
                onExportPdf={resolveExportPdfHandler(
                  readOnly,
                  noteCompletionTime,
                  handleExportPdf,
                )}
              />
            </Box>

            <Divider />

            {/* Stepper Section */}
            <Box sx={{ width: "95%", mx: "auto", pb: 3, pt: 3 }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  "& .MuiStepLabel-label": {
                    fontWeight: 500,
                    fontSize: "13px",
                    color: "#A09EBA",
                    marginTop: "6px",
                  },
                  "& .MuiStepLabel-label.Mui-active": {
                    color: "#403C6F",
                    fontWeight: 600,
                  },
                  "& .MuiStepLabel-iconContainer": {
                    padding: 0,
                  },
                  "& .MuiStepConnector-root": {
                    top: "6px",
                    left: "calc(-50% + 7px)",
                    right: "calc(50% + 7px)",
                  },
                  "& .MuiStepConnector-line": {
                    borderColor: "#C4C2D3",
                    borderTopWidth: 2,
                  },
                }}
              >
                {steps.map((label, idx) => (
                  <Step key={label ? `step-${label}` : `step-idx-${idx}`}>
                    <StepLabel slots={{ stepIcon: CustomStepIcon }}>
                      {isGroupNamesLoading || (!label && idx !== 1) ? (
                        <LoadingWaveDots label="Loading step title" />
                      ) : (
                        label
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Divider />
          </Box>

          {/* Main Content Area */}
          <Box
            sx={{
              p: 4,
              backgroundColor: "#fff",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {sectionError && (
              <Alert
                severity="error"
                onClose={() => setSectionError(null)}
                sx={{
                  mb: 3,
                  borderRadius: "8px",
                  "& .MuiAlert-message": { fontWeight: 500, fontSize: "14px" },
                }}
              >
                {sectionError}
              </Alert>
            )}
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <CircularProgress sx={{ color: "#403C6F" }} />
                <Typography
                  variant="body2"
                  sx={{ color: "#7F7C9F", fontWeight: 500 }}
                >
                  Initializing assessment session...
                </Typography>
              </Box>
            ) : (
              renderClinicalNoteStepContent({
                stageKey: getStageKeyForStep(activeStep, hasProgramSpecific),
                memberData,
                assigneeRnName,
                selectedOutcome,
                setSelectedOutcome,
                clinicalNotes,
                setClinicalNotes,
                certification,
                setCertification,
                secondaryColor,
                borderColor,
                primaryColor,
                filteredProgramMap,
                allDisplayActivePrograms,
                initialModalAssessmentTypes,
                referenceData,
                readOnly,
                noteStartTime,
                noteCompletionTime,
                declinedReason,
                setDeclinedReason,
                selectedSubReason,
                setSelectedSubReason,
                declinedNotes,
                setDeclinedNotes,
                isStep0DirectSubmit,
                availableProgramGroups,
                showAssessmentTypeSelect,
                generalAssessmentId,
                clinicalAssessmentId,
                currentAssessmentType,
                handleAssessmentTypeChange,
                handleProgramAssessmentTypeChange,
                handleStep0AddProgram,
                handleRemoveProgram,
                selectedProgram,
                generalAssessmentSchema,
                generalFormPayload,
                setGeneralAssessmentId,
                setGeneralAssessmentVersion,
                setGeneralAssessmentSchema,
                setGeneralFormPayload,
                bgColor,
                columnsCount,
                warnings,
                programSpecificRef,
                displayActivePrograms,
                handleSetActivePrograms,
                availablePrograms,
                schemaLoading,
                activeProgramTab,
                handleProgramSubTabChange,
                handleProgramSpecificAddProgram,
                getOutcomeForProgram,
                programAssessmentSchema,
                programFormPayload,
                setProgramFormPayload,
                lifestyleAssessmentId,
                lifestyleAssessmentSchema,
                lifestyleFormPayload,
                setLifestyleAssessmentId,
                setLifestyleAssessmentSchema,
                setLifestyleFormPayload,
                carePlanAssessmentId,
                carePlanAssessmentSchema,
                carePlanFormPayload,
                setCarePlanAssessmentId,
                setCarePlanAssessmentSchema,
                setCarePlanFormPayload,
              })
            )}
          </Box>
        </Box>

        {/* Fixed Footer Actions */}
        {renderClinicalNoteFooter({
          handlePrevStep,
          handleRequestClose,
          handleSaveDraft,
          loading,
          readOnly,
          isNextLoading,
          activeStep,
          isStep0DirectSubmit,
          generalAssessmentSchema,
          hasProgramSpecific,
          isProgramFormEmpty,
          handleNextStep,
          isSubmitting,
          stepsLength: steps.length,
          buttonLabel,
          isStepValid,
          nextLoadingLabel,
          borderColor,
        })}
      </Paper>

      {renderClinicalNoteAddProgramModal({
        pendingAddProgram,
        onClose: () => {
          setPendingAddProgram(null);
          setPendingAssessmentType("");
        },
        pendingAssessmentType,
        setPendingAssessmentType,
        primaryColor,
        handleConfirmAddProgram,
      })}

      {renderClinicalNoteDiscardModal({
        showDiscardWarning,
        onClose: () => {
          setShowDiscardWarning(false);
          pendingNavigationActionRef.current = null;
        },
        handleSaveDraft,
        readOnly,
        bypassWarningRef,
        pendingNavigationActionRef,
        onBack,
      })}

      {renderClinicalNoteTypeConflictModal({
        typeConflictModal,
        onClose: () => {
          setTypeConflictModal(null);
          newlyAddedProgramIdRef.current = null;
        },
        typeConflictModalQuestion,
        typeConflictModalWarning,
        isConflictProceedLoading,
        handleConfirmTypeConflictProceed,
      })}
    </Box>
  );
});

ClinicalNotePage.propTypes = {
  // NOSONAR
  memberData: PropTypes.object.isRequired,
  selectedProgram: PropTypes.object,
  assessmentType: PropTypes.string,
  clinicalAssessmentId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  batchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isBatch: PropTypes.bool,
  initialStartTime: PropTypes.string,
  initialAssessmentData: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  programMap: PropTypes.object,
  initialClinicalNotes: PropTypes.string,
  clinicalNotePrograms: PropTypes.array,
  memberClinicalPrograms: PropTypes.array,
  onDraftStatusChange: PropTypes.func,
  isDraftSession: PropTypes.bool,
  isShowPhiEnabled: PropTypes.bool,
};

export default ClinicalNotePage;
